import type { Pick, Renderer } from "../render/renderer";
import { SQRT3, hexAt, hexX, hexY } from "../render/hex";
import { BLOCKED, STRUCTURES, STRUCTURE_TIPS, costOf, freeLeft, lockedWhy, setStormWorld, stormWorld, type Placing, type Site, setCancelSite, setPlacing, setSites as setBuildSites, siteDone, siteName } from "../console/BuildTab";
import { industry } from "../state/industry";
import { problems } from "../state/problems";
import { linesOf, recipeByName } from "../state/recipes";
import { setIdleGroups, type IdleGroup } from "../state/idle";
import { setAlerts, type Alert } from "../state/alerts";
import { CAMP_REACH, command, ownCamps, ownPosts, upkeep } from "../state/features";
import { UNLINKED_SVG, goodShape, recipeIconGood } from "./goodIcons";
import { REGION } from "../replay/replay";
import { hostile } from "../state/diplomacy";
import { isUnpowered, offerable, powerOf, retired } from "../state/designs";
import { plan, setPlan, setPlanHandlers, type MinePlan } from "../console/MinePlan";
import { closePolicyEditor, openPolicyEditor, policyEditorOpen, setPolicyHandlers, setPolicyTarget } from "../console/PolicyEditor";
import { barracksPresets, setPolicyGoods, policyFromYaml, ruleText, type Attached, type Baked } from "../state/policy";
import { sideName } from "../state/players";
import { ORE_HUES, oreKey, oreLabel } from "../render/colour";

/** A prospected cell's findings (the commander's state, `intel.findings`). */
/** A report bought: someone's claim of so much of an ore in a cell (true
 *  or not), as of a tick. */
export interface Report {
  cell: number;
  from: number;
  tick: number;
  ore: string;
  amount: number;
}

/** "Coal" for "coal" (reports say it either way; hues are by "Coal"). */
export const oreName = (o: string) => (o.includes("_") || o === o.toLowerCase() ? oreKey(o) : o);

/** How long ago a tick was, in words ("4 min ago"). */
export function agoText(now: number, tick: number): string {
  const s = Math.max(0, (now - tick) / 10);
  return s < 60 ? "just now" : s < 3600 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 360) / 10} h ago`;
}

export interface Finding {
  cell: number;
  by: string[];
  total: [number, number] | null;
  ores: { ore: string; amount: number; error_pct: number }[];
  /** A deep core's per-hex grades, soil's per 4x4 sub-cell (by ore). */
  grid?: Record<string, number[]>;
  grid4?: Record<string, number[]>;
}

/** Goods on their way from one of the commander's stations to another
 *  (the state's `flows`: what loaded freight trains carry now). */
export interface Flow {
  from: number;
  to: number;
  good: number;
  in_transit: number;
}

/** A structure being taken down (the commander's state). */
export interface Decommission {
  at: [number, number];
  ticks_left: number;
  refund: number;
}

/** How far from its hex a mine works deposits (`industry::MINE_RADIUS`). */
const MINE_REACH = 2;

/** How far a storm breaker breaks the wall open (hexes). */
const BREACH = 24;

/** A quantity in a few characters (12k, 1.2M). */
const short = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e4 ? `${Math.round(n / 1e3)}k` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : `${n}`);
import { toggleScreen } from "../screens/Screens";
import type { LivePlayback } from "../state/live";
import { DIRS, DepotKind, NO_ID, Terrain, UnitFlags } from "../replay/format";
import { setCard } from "../console/CommandCard";
import { setGroupCancel, setGroupDecommission, setJobClick, setSummary, type Summary } from "../console/SelectionSummary";
import { gameMenuOpen } from "../overlays/Overlays";
import { setBar } from "../hud/TopBar";
import { engineerAt, engineerBreakdown, engineersReported, siteRef, type Engineer } from "../state/engineers";
import { minutes, sampling, samplingGrid, seenCells } from "../state/sampling";
import { newGround, structureName } from "../state/structures";
import { stationInfo } from "../state/stations";
import { showGuide } from "../screens/Guides";
import * as keys from "./bindings";
import { $, count } from "./format";

/*
 * Live-world controls, after Beyond All Reason's Grid profile: left-drag box
 * select (Shift adds, Ctrl removes), click selects, double-click selects that
 * type in view; right-click moves (fights on an enemy, or after F),
 * right-drag draws a line the selection holds; F then a drag (either button)
 * draws a line it fights its way to; middle-drag pans. Holding Shift
 * queues the order after the current ones (waypoints; F stays armed). Keys:
 * F fight, H patrol (fights back and forth between here and the click), T
 * repeat (the queue loops), G stop, L fire state, ; move state, Ctrl+N / N /
 * N N / Shift+N control groups, Ctrl+E all, Q same types in view, Ctrl+W same
 * types everywhere, Z held while box-selecting keeps to the selection's
 * types, Space held keeps to idle units (still for 3 s, not firing), Esc
 * clear. Selected units show their orders' routes, as in BAR.
 *
 * Camera: arrow keys pan; Ctrl+F1-F4 set a camera anchor, F1-F4 fly to it.
 *
 * Producers (barracks) are selected by clicking them (Shift adds); a bar
 * then sets what they make, as BAR's factory menu (keys Z X C V B, the
 * first row of BAR's build grid).
 *
 * As in BAR, orders go to the selection: each order forms the selected units
 * into a new formation (they leave their old one), so units keep their own
 * orders. A queued order goes to the whole formations the selected units
 * are in (the sim queues per formation; each unit goes on when it arrives).
 * Control groups are remembered selections, as in BAR.
 */

/** One order of a formation's queue, as this client sent it: its hexes (the
 *  point, or the line) and how close counts as there (hexes). */
type Leg = { fight: boolean; pts: [number, number][]; reach: number; ride?: boolean; area?: [[number, number], [number, number]] };

/** The route-drawing leg of an order message (none for stop). */
function legOf(order: Record<string, unknown>): Leg | null {
  switch (order.order) {
    case "move":
    case "fight":
      return { fight: order.order === "fight", pts: [order.to as [number, number]], reach: order.radius as number };
    case "ride":
      // Walk to the boarding station, then by rail to the other.
      return { fight: false, ride: true, pts: [order.from as [number, number], order.to as [number, number]], reach: 2 };
    case "hold_line":
    case "fight_line":
      return { fight: order.order === "fight_line", pts: order.line as [number, number][], reach: order.depth as number };
    case "prospect": {
      // An area: its middle for the route, its corners for the box.
      const [a, b] = [order.from as [number, number], order.to as [number, number]];
      const mid: [number, number] = [Math.round((a[0] + b[0]) / 2), Math.round((a[1] + b[1]) / 2)];
      return { fight: false, pts: [mid], reach: Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1])), area: [a, b] };
    }
    default:
      return null;
  }
}

type Fire = "at_will" | "hold" | "return";
type Moves = "maneuver" | "hold";

/** Formation ids this client hands out (the server scopes them to our side). */
let nextId = 1000;

/** The radius that fits `n` units at about two per hex (the sim's `fit_radius`). */
function fitRadius(n: number): number {
  let r = 0;
  while ((1 + 3 * r * (r + 1)) * 2 < n) r++;
  return r;
}

/** The line depth that fits `n` units along `len` hexes (the sim's `fit_depth`). */
/** A line order is split into stretches of about this many hexes (at most
 *  `MAX_LINE_PARTS`), each held by an equal share of the selection. */
const LINE_PART = 8;
const MAX_LINE_PARTS = 8;

function fitDepth(n: number, len: number): number {
  return Math.min(64, Math.max(0, Math.ceil(n / (2 * Math.max(1, len))) - 1));
}

export class CommandControls {
  readonly selection = new Set<number>();
  private groups = new Map<number, number[]>();
  private lastGroup = { n: -1, t: 0 };
  /** The formation each unit was last ordered in (for postures). */
  private formationOf = new Map<number, number>();
  private posture = new Map<number, { fire: Fire; moves: Moves }>();
  /** The policies on the player's formations (the state's word). */
  private policies = new Map<number, Attached>();
  /** Attached or detached here, not yet in a state (a state a moment old
   *  would undo it on screen): each with when. */
  private policySent = new Map<number, { p: Attached | null; at: number }>();
  /** Each formation's orders, standing and queued (for drawing routes). */
  private legs = new Map<number, Leg[]>();
  private routes = document.createElementNS("http://www.w3.org/2000/svg", "g");
  /** F (or H) was pressed: the next click fights (patrols) there. */
  private armedState: "fight" | "patrol" | "ride" | "prospect" | "plan" | null = null;
  /** A structure waiting for its place (the Build tab); track waits for
   *  two clicks, `from` holds the first. */
  private placing: (Placing & { from?: [number, number] }) | null = null;
  /** The commander's construction sites, drawn where they'll stand. */
  private sites: Site[] = [];
  /** What the commander's prospecting found, by need-map cell. */
  private findings: Finding[] = [];
  private intelGrid = { cols: 0, cell: 16 };
  /** A command waiting for its click (BAR's armed command), shown by the
   *  cursor; any change of selection drops it. */
  private get armed(): "fight" | "patrol" | "ride" | "prospect" | "plan" | null {
    return this.armedState;
  }
  private set armed(v: "fight" | "patrol" | "ride" | "prospect" | "plan" | null) {
    this.armedState = v;
    document.body.classList.toggle("arming-area", v === "prospect" || v === "plan");
    document.body.classList.toggle("armed-fight", v === "fight");
    document.body.classList.toggle("armed-patrol", v === "patrol");
    document.body.classList.toggle("armed-ride", v === "ride");
  }
  /** Formations whose queue repeats (patrols, T). */
  private repeating = new Set<number>();
  /** Z is held: box selection keeps to the selection's types. */
  private zHeld = false;
  /** Space is held: box selection keeps to idle units. */
  private spaceHeld = false;
  /** When each unit last changed hex (ms), for idleness. */
  private movedAt = new Float64Array(0);
  /** Selected producers (indices into the statics' producers). */
  private factories = new Set<number>();
  /** Rally points this client set, per producer. */
  private rallies = new Map<number, [number, number]>();
  /** What each producer makes, as far as this client knows. */
  private making: number[];
  /** The selection's summary (BAR's selection panel), refreshed a few times a second. */
  private infoAt = 0;
  /** Arrow keys held (panning), and camera anchors (F1-F4). */
  private arrows = new Set<string>();
  private anchors = new Map<string, [number, number, number]>();
  private lastFrame = performance.now();
  /** The build bar's slots (BAR's grid, first row), as bindable actions. */
  private static BUILD_SLOTS = ["build1", "build2", "build3", "build4", "build5"];
  private box = $("select-box");
  private overlay = $("order-overlay") as unknown as SVGSVGElement;
  private abort = new AbortController();

  constructor(
    private canvas: HTMLCanvasElement,
    private renderer: Renderer,
    private playback: LivePlayback,
    readonly faction: number,
    private toast: (text: string) => void,
    /** A click on the map: the unit or hex to show in the Selection tab (null: none). */
    private inspect: (pick: Pick | null) => void = () => {},
  ) {
    this.overlay.append(this.routes);
    // The storm breaker belongs to worlds with a storm wall.
    setStormWorld(!!playback.state.statics.region);
    this.making = [...(playback.header.producer_designs ?? [])];
    this.movedAt = new Float64Array(playback.state.tile.length);
    setPolicyHandlers({
      bake: (yaml) => this.playback.bakePolicy(yaml) as unknown as Promise<Baked>,
      attach: (yaml) => this.attachPolicy(yaml),
      changed: () => this.syncCard(),
    });
    playback.onFrame(() => {
      const s = playback.state;
      const now = performance.now();
      for (const id of s.live) if (s.tile[id] !== s.prevTile[id]) this.movedAt[id] = now;
    });
    this.bind();
    this.syncCard();
    this.stopKeys = keys.onBindingsChange(() => this.syncCard());
    // Construction cancelled (the Build tab's ×, the Selection tab, Del).
    setCancelSite((at) => this.cancelSites(at));
    setGroupCancel((at) => this.cancelSites(at));
    // Several structures: decommission them all (asked once, in place).
    setGroupDecommission((ids) => {
      const s = this.playback.state;
      for (const k of ids) {
        const d = s.depots[k];
        if (d && d.kind !== DepotKind.Home) this.playback.send({ type: "decommission", at: [s.q(d.tile), s.r(d.tile)] });
      }
      this.toast(`Decommissioning ${ids.length}: steel back in 30 s`);
      this.structs.clear();
      this.renderInfo();
    });
    // An engineer's row in the Selection tab: select it and centre on it.
    setJobClick((id) => {
      this.setSelection([id], "replace");
      this.focus([id]);
    });
  }

  /** F, H: the next click fights (patrols) there. */
  /** The Build tab chose a structure: the next click places it. */
  place(p: Placing): void {
    const locked = lockedWhy(p);
    if (locked) return this.toast(locked);
    const same = this.placing && this.placing.what === p.what && this.placing.resource === p.resource && this.placing.recipe === p.recipe;
    this.placing = same ? null : { ...p };
    setPlacing(this.placing);
    document.body.classList.toggle("placing", !!this.placing);
    this.showOre(this.placing?.what === "mine" ? (this.placing.resource ?? "All") : null);
    this.syncCard();
    if (this.placing) this.toast(p.what === "track" ? "Track: click where it starts" : "Click where to build: drag for a row, Shift+drag for a grid");
  }

  private stopPlacing(): void {
    this.placing = null;
    setPlacing(null);
    document.body.classList.remove("placing");
    this.showOre(null);
    // Placed (or not): the card goes back to the orders.
    this.buildPage = false;
    this.syncCard();
  }

  /** What a right-click at `pick` would do, for the cursor: attack an
   *  enemy, build (engineers selected, a site of yours there), or nothing
   *  special. */
  intent(pick: Pick | null): "attack" | "build" | null {
    if (!pick || !this.selection.size) return null;
    const s = this.playback.state;
    if (pick.unit !== null && hostile(s.faction[pick.unit], this.faction)) return "attack";
    if (pick.unit === null && this.engineers().length && this.sites.some((x) => x.at[0] === pick.q && x.at[1] === pick.r)) return "build";
    return null;
  }

  /** Why the server would turn down placing `p` at (q, r), or null if it
   *  would take it (Sim::order_build's rules, as far as this side sees:
   *  passable ground; nothing built or being built there, a plant also on
   *  one of your stations; stations on track; sixteen sites at most). */
  private placeProblem(p: Placing, q: number, r: number): string | null {
    const s = this.playback.state;
    if (q < 0 || r < 0 || q >= s.width || r >= s.height) return "Off the map";
    const t = r * s.width + q;
    // A storm breaker in the storm or past it (wall, outside, the void not
    // yet made): it waits in the queue until a breach reaches it.
    const reg = s.statics.region;
    if (p.what === "breaker" && reg && reg[t] !== REGION.INSIDE) {
      return this.sites.some((x) => x.at[0] === q && x.at[1] === r) ? "Already being built here" : null;
    }
    const ground = s.statics.terrain[t];
    if (ground === Terrain.WATER) return "Water";
    if (ground === Terrain.BLOCKED) return "Mountains";
    if (p.what === "track") return null;
    if (this.sites.some((x) => x.what !== "track" && x.at[0] === q && x.at[1] === r)) return "Already being built here";
    const depot = s.depots.find((d) => d.tile === t && d.faction !== 255);
    // Nothing stands in the storm or the void; a storm breaker stands
    // against the wall (on either side of it).
    const region = s.statics.region;
    if (region && region[t] === REGION.WALL) return "In the storm";
    if (region && region[t] === REGION.VOID) return "Nothing there";
    if (p.what === "breaker" && region && !this.hexesWithin(q, r, 2).some((i) => region[i] === REGION.WALL)) return "Must stand against the storm wall";
    if (p.what === "plant") return depot && depot.faction !== this.faction ? "Theirs is here" : null;
    if (depot) return depot.faction === this.faction ? "Something's built here" : "Theirs is here";
    if ((p.what === "station" || p.what === "trade_post") && !s.statics.track[t]) return p.what === "station" ? "Stations go on track" : "Trade posts go on track";
    return null;
  }

  /** Builds sent and not yet seen in a report: what, where, the game tick
   *  it was sent at, and how many structures were built then. */
  private pending: { what: string; at: [number, number]; tick: number; built: number }[] = [];
  private builtCount = 0;

  /** A new report (its tick, and how many structures are built): a build
   *  sent well before it that's neither among the sites, nor built there,
   *  nor finished since (built went up), was turned down; say so. Only
   *  reports made after the order reached the server count. */
  private checkPending(tick: number, built: number): void {
    const s = this.playback.state;
    const settled = this.pending.filter((b) => tick > b.tick + 30);
    this.pending = this.pending.filter((b) => tick <= b.tick + 30);
    const grew = built > this.builtCount;
    this.builtCount = built;
    const missing = settled.filter((b) => {
      const t = b.at[1] * s.width + b.at[0];
      const sited = [...this.serverSites, ...this.serverQueued].some((x) => (x.at[0] === b.at[0] && x.at[1] === b.at[1]) || (x.to && x.to[0] === b.at[0] && x.to[1] === b.at[1]));
      const standing = s.depots.some((d) => d.tile === t && d.faction === this.faction);
      return !sited && !standing && !grew && b.built === built;
    });
    if (missing.length) this.toast(`Your ${missing[0].what} at ${missing[0].at[0]}, ${missing[0].at[1]} didn't start: the server turned it down`);
    // Settled builds' provisional ghosts go (placed: the server's replace them).
    this.provisional = this.provisional.filter((p) => !settled.some((b) => b.at[0] === p.at[0] && b.at[1] === p.at[1]));
  }

  /** What the player has set up here (selection, control groups, camera
   *  spots, orders and their routes, placing, builds sent): carried over
   *  when the server re-sends the world (a new header), so nothing resets. */
  carry(): Record<string, unknown> {
    return {
      selection: [...this.selection],
      groups: this.groups,
      formationOf: this.formationOf,
      posture: this.posture,
      prefer: this.prefer,
      legs: this.legs,
      repeating: this.repeating,
      factories: [...this.factories],
      rallies: this.rallies,
      anchors: this.anchors,
      placing: this.placing,
      buildPage: this.buildPage,
      buildQueue: this.buildQueue,
      pending: this.pending,
      provisional: this.provisional,
      rodeBefore: this.rodeBefore,
      copied: this.copied,
    };
  }

  /** Takes over what `carry` kept (the same unit ids: the world went on). */
  resume(c: Record<string, unknown>): void {
    this.groups = c.groups as typeof this.groups;
    this.formationOf = c.formationOf as typeof this.formationOf;
    this.posture = c.posture as typeof this.posture;
    if (c.prefer) this.prefer = c.prefer as typeof this.prefer;
    this.legs = c.legs as typeof this.legs;
    this.repeating = c.repeating as typeof this.repeating;
    this.rallies = c.rallies as typeof this.rallies;
    this.anchors = c.anchors as typeof this.anchors;
    this.buildQueue = c.buildQueue as typeof this.buildQueue;
    this.pending = c.pending as typeof this.pending;
    this.provisional = c.provisional as typeof this.provisional;
    this.rodeBefore = c.rodeBefore as boolean;
    this.copied = c.copied as typeof this.copied;
    for (const k of c.factories as number[]) this.factories.add(k);
    this.setSelection(c.selection as number[], "replace");
    this.buildPage = c.buildPage as boolean;
    if (c.placing) this.place(c.placing as Placing);
    this.syncCard();
  }

  /** The selected engineers (designs that build). */
  private engineers(): number[] {
    const s = this.playback.state;
    const designs = this.playback.header.designs;
    return [...this.selection].filter((u) => s.alive[u] && designs[s.design[u]]?.modules?.some((m) => m.kind === "construction"));
  }

  /** Engineers sent to build, and the sites they build in turn (Shift
   *  queues): each goes to the next once the one before is done. `listed`:
   *  the current site has shown in the state (so its going means done). */
  private buildQueue: { ids: number[]; sites: [number, number][]; listed: boolean } | null = null;

  /** Sends the queue's engineers to its first site (they build any site of
   *  yours within a hex once there). */
  private sendBuilders(): void {
    const q = this.buildQueue;
    if (!q || !q.sites.length) return;
    const s = this.playback.state;
    const ids = q.ids.filter((u) => s.alive[u]);
    if (!ids.length) {
      this.buildQueue = null;
      return;
    }
    q.listed = false;
    this.orderGroups([{ ids, order: { order: "move", to: q.sites[0], radius: 1 } }], "Build");
  }

  /** Each engineer's hex and since when it's stood there (ms), for idle. */
  private still = new Map<number, { tile: number; since: number }>();
  /** Idle engineers now, and which was selected last (to cycle). */
  private idle: number[] = [];
  private idleScanned = 0;
  private idleAt = -1;
  private idleTip = "";

  /** Engineers standing idle: still for three seconds, at no site of
   *  yours. Counted on the top bar (BAR's idle-builder button). */
  private findIdle(): void {
    const s = this.playback.state;
    const designs = this.playback.header.designs;
    const now = performance.now();
    // Four times a second is plenty for a count (it walks every unit).
    if (now - this.idleScanned < 250) return;
    this.idleScanned = now;
    const idle: number[] = [];
    for (const id of s.live) {
      if (s.faction[id] !== this.faction || !designs[s.design[id]]?.modules?.some((m) => m.kind === "construction")) continue;
      const was = this.still.get(id);
      if (!was || was.tile !== s.tile[id]) {
        this.still.set(id, { tile: s.tile[id], since: now });
        continue;
      }
      const [q, r] = [s.q(s.tile[id]), s.r(s.tile[id])];
      // The state's word, when it says; else still for three seconds at no
      // site of yours.
      if (engineersReported()) {
        if (engineerAt(q, r)?.doing === "idle") idle.push(id);
        continue;
      }
      if (now - was.since < 3000) continue;
      const atSite = this.sites.some((x) => Math.max(Math.abs(x.at[0] - q), Math.abs(x.at[1] - r), Math.abs(x.at[0] - q + x.at[1] - r)) <= 1);
      if (!atSite) idle.push(id);
    }
    for (const id of this.still.keys()) if (!s.alive[id]) this.still.delete(id);
    const tip = engineersReported() ? `Engineers: ${engineerBreakdown()}. Select the next idle one.` : "";
    if (tip !== this.idleTip || idle.length !== this.idle.length || idle.some((u, i) => u !== this.idle[i])) {
      this.idle = idle;
      this.idleTip = tip;
      setBar("idle", { count: idle.length, key: keys.hint("idleEngineer"), tip, run: () => this.nextIdle() });
    }
  }

  /** What an engineer's doing, in words ("Building Depot 45%"), and
   *  whether it wants you (waiting, idle). */
  private jobText(e: Engineer | undefined, q: number, r: number): { text: string; warn: boolean } {
    const ref = siteRef(e);
    const site = ref ? this.sites.find((x) => x.id === ref.id) : undefined;
    const name = site ? siteName(site) : "a site";
    switch (e?.doing) {
      case "building":
        return { text: `Building ${name}${site ? ` ${Math.floor((100 * siteDone(site)) / Math.max(1, site.need))}%` : ""}`, warn: false };
      case "going": {
        const d = site ? Math.max(Math.abs(site.at[0] - q), Math.abs(site.at[1] - r), Math.abs(site.at[0] - q + site.at[1] - r)) : null;
        return { text: `Going to ${name}${d === null ? "" : `, ${d} hex${d === 1 ? "" : "es"}`}`, warn: false };
      }
      case "orders":
        return { text: "Under your orders", warn: false };
      case "no_steel":
        return { text: "Waiting for steel", warn: true };
      case "idle":
        return { text: "Idle: nothing to build", warn: true };
      default:
        return { text: "Engineer", warn: false };
    }
  }

  /** Each engineer's state over it on the map (BAR marks a constructor's):
   *  a hammer building, an arrow going (a dashed line to its site), a flag
   *  under orders, an amber bar waiting for steel, "zz" idle. Zoomed in
   *  enough to see units. */
  private drawEngineers(parts: string[], screen: (x: number, y: number) => string): void {
    if (!engineersReported() || this.renderer.camera.zoom < 6) return;
    const s = this.playback.state;
    const designs = this.playback.header.designs;
    const [bx0, by0, bx1, by1] = this.renderer.camera.bounds();
    for (const id of s.live) {
      if (s.faction[id] !== this.faction || !designs[s.design[id]]?.modules?.some((m) => m.kind === "construction")) continue;
      const [wx, wy] = this.renderer.unitPos(id, this.playback.alpha);
      if (wx < bx0 || wx > bx1 || wy < by0 || wy > by1) continue;
      const e = engineerAt(s.q(s.tile[id]), s.r(s.tile[id]));
      if (!e) continue;
      const [x, y] = screen(wx, wy).split(",").map(Number);
      const [px, py] = [x, y - 14];
      if (e.doing === "going") {
        const ref = siteRef(e);
        const site = ref ? this.sites.find((t) => t.id === ref.id) : undefined;
        if (site) {
          // A track's far end, for one going there.
          const end = ref!.far && site.to ? site.to : site.at;
          const [sx, sy] = screen(hexX(end[0], end[1]), hexY(end[1])).split(",").map(Number);
          parts.push(`<line class="eng-going" x1="${x.toFixed(1)}" y1="${y.toFixed(1)}" x2="${sx.toFixed(1)}" y2="${sy.toFixed(1)}"/>`);
        }
      }
      // Drawn about the origin, then placed and scaled up to read.
      const g =
        e.doing === "building"
          ? `<path class="pip" d="M-4,-3h7v3h-7z M0,0v5"/>`
          : e.doing === "going"
            ? `<path class="pip fill" d="M-4,-3l7,3l-7,3z"/>`
            : e.doing === "orders"
              ? `<path class="pip" d="M-3,4v-8"/><path class="pip fill" d="M-3,-4l6,2l-6,2z"/>`
              : e.doing === "no_steel"
                ? `<rect class="pip-steel" x="-4" y="-2" width="8" height="4"/>`
                : `<text class="pip-idle" x="0" y="3">zz</text>`;
      parts.push(`<g transform="translate(${px.toFixed(1)} ${(py - 4).toFixed(1)}) scale(1.6)">${g}</g>`);
    }
  }

  /** Selects the next idle engineer and centres on it. */
  private nextIdle(): void {
    if (!this.idle.length) return;
    this.idleAt = (this.idleAt + 1) % this.idle.length;
    const id = this.idle[this.idleAt];
    this.setSelection([id], "replace");
    this.focus([id]);
  }

  /** The queue's site is done (it was listed and isn't now): the next. */
  private followBuildQueue(): void {
    const q = this.buildQueue;
    if (!q || !q.sites.length) return;
    const [sq, sr] = q.sites[0];
    const there = this.sites.some((x) => x.at[0] === sq && x.at[1] === sr);
    if (there) q.listed = true;
    else if (q.listed) {
      q.sites.shift();
      if (q.sites.length) this.sendBuilders();
      else this.buildQueue = null;
    }
  }

  /** The ore layer as it was before placing a mine switched it. */
  private oreBefore: { intel: boolean; ore: string } | null = null;

  /** Placing a mine: the ground shows its ore (`null` puts the layer back). */
  private showOre(ore: string | null): void {
    const o = this.renderer.options;
    if (ore) {
      this.oreBefore ??= { intel: o.intel, ore: o.ore };
      o.intel = true;
      o.ore = ore;
    } else if (this.oreBefore) {
      o.intel = this.oreBefore.intel;
      o.ore = this.oreBefore.ore;
      this.oreBefore = null;
    }
  }

  /** A click while placing: the build order (or track's first end). */
  private placeAt(e: MouseEvent, keep: boolean): void {
    const p = this.placing;
    if (!p) return;
    const [sx, sy] = this.local(e);
    const pick = this.renderer.pick(sx, sy, this.playback.alpha);
    const at: [number, number] = [pick.q, pick.r];
    if (p.what === "track" && !p.from) {
      p.from = at;
      this.toast("Track: click where it ends");
      return;
    }
    this.placeHex(at, keep);
  }

  /** A hex wanted by a tool (the admin's): the next plain left-click on the
   *  map goes to it instead of selecting. */
  private hexPicker: ((at: [number, number]) => void) | null = null;
  pickHex(cb: ((at: [number, number]) => void) | null): void {
    this.hexPicker = cb;
    document.body.classList.toggle("picking-hex", !!cb);
  }

  /** The overlay as last drawn: the view it was drawn for, when, and
   *  whether the player has done something since. */
  private drawnView = "";
  private drawnAt = 0;
  private overlayDirty = true;

  /** A drag's row while placing, as ghost rings on the overlay. */
  private rowPreview = document.createElementNS("http://www.w3.org/2000/svg", "g");

  /** Your construction sites within a screen box (device pixels). */
  private sitesIn(x0: number, y0: number, x1: number, y1: number): [number, number][] {
    const cam = this.renderer.camera;
    return this.sites
      .filter((x) => x.id >= 0)
      .filter((x) => {
        const [sx, sy] = cam.toScreen(hexX(x.at[0], x.at[1]), hexY(x.at[1]));
        return sx >= x0 && sx <= x1 && sy >= y0 && sy <= y1;
      })
      .map((x) => x.at);
  }

  /** Hexes in a straight line from a to b, every `step` hexes (both ends). */
  private lineHexes(a: [number, number], b: [number, number], step: number): [number, number][] {
    const n = Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[0] - b[0] + a[1] - b[1]));
    const out: [number, number][] = [];
    for (let i = 0; i <= n; i += step) {
      const t = n ? i / n : 0;
      const [q, r] = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      // Cube rounding.
      let [rq, rr, rs] = [Math.round(q), Math.round(r), Math.round(-q - r)];
      const [dq, dr, ds] = [Math.abs(rq - q), Math.abs(rr - r), Math.abs(rs + q + r)];
      if (dq > dr && dq > ds) rq = -rr - rs;
      else if (dr > ds) rr = -rq - rs;
      out.push([rq, rr]);
    }
    return out;
  }

  /** How far apart a row of a structure goes: mines don't share ground. */
  private rowStep(what: string): number {
    // Breakers: a breach apart (each breach brings the wall to the next).
    return what === "mine" ? 2 * MINE_REACH + 1 : what === "breaker" ? BREACH : 2;
  }

  /** A row of a structure dragged out while placing (BAR's line build,
   *  Factorio's drag): every spot that fits gets one; track is one piece
   *  from where the drag began to where it ended. */
  /** Hexes over the box from a to b, every `step` hexes each way. */
  private gridHexes(a: [number, number], b: [number, number], step: number): [number, number][] {
    const [qa, qb, ra, rb] = [Math.min(a[0], b[0]), Math.max(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[1], b[1])];
    const out: [number, number][] = [];
    for (let r = ra; r <= rb; r += step) for (let q = qa; q <= qb; q += step) out.push([q, r]);
    return out.slice(0, 400);
  }

  private placeRow(from: [number, number], to: [number, number], keep: boolean, grid = false): void {
    const p = this.placing;
    if (!p) return;
    if (p.what === "track") {
      p.from = from;
      this.placeHex(to, keep);
      return;
    }
    let placed = 0;
    let skipped = 0;
    for (const at of grid ? this.gridHexes(from, to, this.rowStep(p.what)) : this.lineHexes(from, to, this.rowStep(p.what))) {
      if (this.placeProblem(p, at[0], at[1])) skipped++;
      else {
        this.placeHex(at, true, true);
        placed++;
      }
    }
    const name = STRUCTURES.find(([, x]) => x.what === p.what && x.recipe === p.recipe)?.[0] ?? p.what;
    this.toast(placed ? `${placed} × ${name.toLowerCase()}${skipped ? ` (${skipped} spot${skipped === 1 ? "" : "s"} didn't fit)` : ""}` : "None of those spots fit");
    if (!keep) this.stopPlacing();
  }

  /** Places one structure at a hex (keep: carry on placing; quiet: part of
   *  a row, so no toast of its own). */
  private placeHex(at: [number, number], keep: boolean, quiet = false): void {
    const p = this.placing;
    if (!p) return;
    // What the server would turn down, said now, not sent.
    const problem = this.placeProblem(p, at[0], at[1]) ?? (p.from ? this.placeProblem(p, p.from[0], p.from[1]) : null);
    if (problem) {
      if (!quiet) this.toast(`Can't build there: ${problem}`);
      return;
    }
    const command: Record<string, unknown> = { type: "build", what: p.what, at: p.from ?? at };
    if (p.resource) command.resource = p.resource;
    if (p.recipe) command.recipe = p.recipe;
    if (p.what === "track") command.to = at;
    this.playback.send(command);
    this.pending.push({ what: p.what === "plant" ? (p.recipe ?? "plant").toLowerCase() : p.what, at: (p.from ?? at) as [number, number], tick: this.playback.state.tick, built: this.builtCount });
    // Drawn at once, as the server will draw it, until its own arrives.
    this.provisional.push({
      id: -1 - this.provisional.length - this.pending.length,
      what: p.what,
      at: (p.from ?? at) as [number, number],
      ...(p.what === "track" ? { to: at } : {}),
      ...(p.recipe ? { recipe: p.recipe } : {}),
      need: 1,
      done: 0,
    });
    this.publishSites();
    // Engineers selected: they go and build it (Shift: after the ones
    // already queued); else whoever's free does, unbidden.
    const engineers = this.engineers();
    const site: [number, number] = (p.from ?? at) as [number, number];
    if (engineers.length && p.what === "track" && p.from) {
      // Each end of track is built on its own: half to each is twice as fast.
      const half = Math.ceil(engineers.length / 2);
      const groups = [
        { ids: engineers.slice(0, half), order: { order: "move", to: p.from, radius: 1 } },
        { ids: engineers.slice(half), order: { order: "move", to: at, radius: 1 } },
      ].filter((g) => g.ids.length);
      this.orderGroups(groups, quiet ? "" : "Track: half to each end");
    } else if (engineers.length) {
      const q = this.buildQueue;
      const same = !!q && q.ids.length === engineers.length && q.ids.every((u) => engineers.includes(u));
      if (keep && same && q) q.sites.push(site);
      else {
        this.buildQueue = { ids: engineers, sites: [site], listed: false };
        this.sendBuilders();
      }
    } else if (!quiet) this.toast(p.what === "track" ? "Track: click on to carry it on; right-click or Esc to stop" : "Ordered: your engineers will build it");
    // Track runs on from where it ended (a line is click, click, click).
    if (p.what === "track") p.from = at;
    else if (!keep) this.stopPlacing();
  }

  /** Structures being taken down: each with when its report came, its
   *  longest time seen (the ring's full), and whether it's grown (an enemy
   *  near slows it). */
  private decommissions: (Decommission & { seen: number; full: number; slowed: boolean })[] = [];

  setDecommissions(list: Decommission[]): void {
    const now = performance.now();
    this.decommissions = list.map((d) => {
      const was = this.decommissions.find((x) => x.at[0] === d.at[0] && x.at[1] === d.at[1]);
      // Unchanged since the last report (reports every 5 s, the work in
      // steps): counting down from when it last moved; slowed when it went
      // down less than the time since (an enemy near), or grew.
      if (was && was.ticks_left === d.ticks_left) return { ...was, slowed: was.slowed || now - was.seen > 45_000 };
      const elapsed = was ? (now - was.seen) / 100 : 0;
      return { ...d, seen: now, full: Math.max(d.ticks_left, was?.full ?? 300), slowed: !!was && was.ticks_left - d.ticks_left < elapsed * 0.7 };
    });
  }

  /** Goods on their way, and the goods' names (by id). */
  private flows: Flow[] = [];
  private goodNames: string[] = [];

  setFlows(flows: Flow[], goods: string[]): void {
    this.flows = flows;
    this.goodNames = goods;
  }

  /** Each route's way along the rail (tiles, from station to station),
   *  found once: null where no track joins them. */
  private railRoutes = new Map<string, number[] | null>();

  /** The rail between two tiles (breadth-first over track hexes). */
  private railPath(from: number, to: number): number[] | null {
    const key = `${from}-${to}`;
    if (this.railRoutes.has(key)) return this.railRoutes.get(key)!;
    const s = this.playback.state;
    const track = s.statics.track;
    const w = s.width;
    const prev = new Map<number, number>([[from, -1]]);
    const queue = [from];
    let found = false;
    for (let i = 0; i < queue.length && queue.length < 200_000; i++) {
      const t = queue[i];
      if (t === to) {
        found = true;
        break;
      }
      const [q, r] = [t % w, Math.floor(t / w)];
      for (const [dq, dr] of DIRS) {
        const [nq, nr] = [q + dq, r + dr];
        if (nq < 0 || nr < 0 || nq >= w || nr >= s.height) continue;
        const n = nr * w + nq;
        if (prev.has(n) || (!track[n] && n !== to)) continue;
        prev.set(n, t);
        queue.push(n);
      }
    }
    let path: number[] | null = null;
    if (found) {
      path = [];
      for (let t = to; t !== -1; t = prev.get(t)!) path.push(t);
      path.reverse();
    }
    this.railRoutes.set(key, path);
    return path;
  }

  /** The goods-flow network as runs of rail (a good, its amount, its
   *  tiles, and their world bounds), from the flows. */
  private flowRuns: { flows: Flow[]; goods: number[]; partial: boolean; runs: { good: number; n: number; tiles: number[]; box: [number, number, number, number] }[] } | null = null;
  private flowNetwork(): { goods: number[]; partial: boolean; runs: { good: number; n: number; tiles: number[]; box: [number, number, number, number] }[] } {
    const s = this.playback.state;
    // One network, not a line per route: each good's amount on each step
    // of rail, summed over every flow that takes it that way, so flows of a
    // good going the same way along the same track draw as one (thicker
    // after they join).
    const steps = new Map<string, { good: number; a: number; b: number; n: number }>();
    // New routes' paths found a few milliseconds a redraw (a hundred new
    // routes at once was a tenth of a second's stall); the rest next time.
    const deadline = performance.now() + 6;
    let partial = false;
    for (const f of this.flows) {
      const [from, to] = [s.depots[f.from]?.tile, s.depots[f.to]?.tile];
      if (from === undefined || to === undefined || f.in_transit <= 0 || from === to) continue;
      const routeKey = f.from < f.to ? `${from}-${to}` : `${to}-${from}`;
      if (!this.railRoutes.has(routeKey) && performance.now() > deadline) {
        partial = true;
        continue;
      }
      // The rail the trains take (found one way, so both ways share it).
      const rail = f.from < f.to ? this.railPath(from, to) : this.railPath(to, from)?.slice().reverse();
      const path = rail ?? [from, to];
      for (let i = 1; i < path.length; i++) {
        const key = `${f.good}:${path[i - 1]}>${path[i]}`;
        const was = steps.get(key);
        if (was) was.n += f.in_transit;
        else steps.set(key, { good: f.good, a: path[i - 1], b: path[i], n: f.in_transit });
      }
    }
    // Steps chained into runs: a run goes on while the next step is the only
    // one on, carries the same amount, and nothing else joins there.
    const out = new Map<string, (typeof steps extends Map<string, infer V> ? V : never)[]>();
    const into = new Map<string, number>();
    for (const st of steps.values()) {
      const k = `${st.good}:${st.a}`;
      out.set(k, [...(out.get(k) ?? []), st]);
      into.set(`${st.good}:${st.b}`, (into.get(`${st.good}:${st.b}`) ?? 0) + 1);
    }
    const next = (st: { good: number; b: number; n: number }) => {
      const on = out.get(`${st.good}:${st.b}`) ?? [];
      return on.length === 1 && on[0].n === st.n && into.get(`${st.good}:${st.b}`) === 1 ? on[0] : null;
    };
    const continues = new Set([...steps.values()].map(next).filter((x) => x));
    const used = new Set<object>();
    const runs: { good: number; n: number; tiles: number[] }[] = [];
    const walk = (first: { good: number; a: number; b: number; n: number }) => {
      const tiles = [first.a];
      for (let st: typeof first | null = first; st && !used.has(st); st = next(st)) {
        used.add(st);
        tiles.push(st.b);
      }
      runs.push({ good: first.good, n: first.n, tiles });
    };
    for (const st of steps.values()) if (!continues.has(st)) walk(st);
    for (const st of steps.values()) if (!used.has(st)) walk(st);
    // A lane a good, on the right of the way it goes (so the two ways sit
    // either side of the track).
    const goods = [...new Set(runs.map((r) => r.good))].sort((a, b) => a - b);
    return {
      goods,
      partial,
      runs: runs.map((r) => {
        const box: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];
        for (const t of r.tiles) {
          const [x, y] = [hexX(s.q(t), s.r(t)), hexY(s.r(t))];
          box[0] = Math.min(box[0], x);
          box[1] = Math.min(box[1], y);
          box[2] = Math.max(box[2], x);
          box[3] = Math.max(box[3], y);
        }
        return { ...r, box };
      }),
    };
  }

  /** Goods flow: an arrow a good from station to station, in the good's
   *  colour, thicker the more is on its way; hovering names it. Several
   *  goods on one route sit side by side. */
  private drawFlows(parts: string[], screen: (x: number, y: number) => string): void {
    if (!this.renderer.options.flows || !this.flows.length) return;
    const s = this.playback.state;
    const hue = (g: string) => {
      const ore = g.replace(/ ore$/, "");
      if (ORE_HUES.some(([o]) => o === ore)) return ORE_HUES.find(([o]) => o === ore)![1];
      return g === "Steel" ? "#9aa3ad" : g === "Munitions" ? "#ece2c4" : "#c9c4b8";
    };
    // The network's runs change only with the flows (a state every few
    // seconds), so they're worked out then, not every frame.
    if (this.flowRuns?.flows !== this.flows || this.flowRuns.partial) this.flowRuns = { flows: this.flows, ...this.flowNetwork() };
    // Some routes still to find: again at the next redraw.
    if (this.flowRuns.partial) this.overlayDirty = true;
    const { runs, goods } = this.flowRuns;
    const xy = (t: number) => screen(hexX(s.q(t), s.r(t)), hexY(s.r(t))).split(",").map(Number);
    // Only runs that reach the view.
    const [vx0, vy0, vx1, vy1] = this.renderer.camera.bounds();
    for (const run of runs) {
      if (run.box[2] < vx0 - 2 || run.box[0] > vx1 + 2 || run.box[3] < vy0 - 2 || run.box[1] > vy1 + 2) continue;
      // Every third hex (hex centres zigzag), and both ends.
      const pts = run.tiles.filter((_, i) => i % 3 === 0 || i === run.tiles.length - 1).map(xy);
      const total = pts.slice(1).reduce((acc, p, i) => acc + Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]), 0);
      if (total < 4) continue;
      const off = (goods.indexOf(run.good) + 0.6) * 6;
      const shifted = pts.map((p, i) => {
        const [p0, p1] = [pts[Math.max(0, i - 1)], pts[Math.min(pts.length - 1, i + 1)]];
        const len = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]) || 1;
        return [p[0] - ((p1[1] - p0[1]) / len) * off, p[1] + ((p1[0] - p0[0]) / len) * off];
      });
      const name = (this.goodNames[run.good] ?? "goods").replace("Munitions", "rounds");
      const width = Math.min(6, 1.5 + Math.log10(1 + run.n));
      const c = hue(this.goodNames[run.good] ?? "");
      const tip = `${run.n.toLocaleString("en-US")} ${name.toLowerCase()} on the way`;
      // The arrowhead halfway along, pointing the way they go (runs long
      // enough to hold one).
      let walked = 0;
      let head = "";
      for (let i = 1; i < shifted.length && !head && total >= 30; i++) {
        const [p0, p1] = [shifted[i - 1], shifted[i]];
        const seg = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
        if (walked + seg >= total / 2 && seg > 0) {
          const t = (total / 2 - walked) / seg;
          const [mx, my] = [p0[0] + (p1[0] - p0[0]) * t, p0[1] + (p1[1] - p0[1]) * t];
          const [ux, uy] = [(p1[0] - p0[0]) / seg, (p1[1] - p0[1]) / seg];
          const [nx, ny] = [-uy, ux];
          head = `${(mx + ux * 8).toFixed(1)},${(my + uy * 8).toFixed(1)} ${(mx - ux * 5 + nx * 6).toFixed(1)},${(my - uy * 5 + ny * 6).toFixed(1)} ${(mx - ux * 5 - nx * 6).toFixed(1)},${(my - uy * 5 - ny * 6).toFixed(1)}`;
        }
        walked += seg;
      }
      parts.push(
        `<g class="flow why" data-tip="${tip}"><polyline points="${shifted.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ")}" stroke="${c}" stroke-width="${width.toFixed(1)}" fill="none" stroke-linejoin="round"/>` +
          (head ? `<polygon points="${head}" fill="${c}"/>` : "") +
          `</g>`,
      );
    }
  }

  /** The commander's construction sites (from their state), and orders
   *  queued past the open sites' limit. */
  setSites(sites: Site[], tick = 0, built = 0, queued: Site[] = []): void {
    this.serverSites = sites;
    this.serverQueued = queued.map((q) => ({ ...q, need: q.need ?? 1, done: 0, queued: true }));
    if (tick) this.checkPending(tick, built);
    this.publishSites();
  }

  /** The sites as the server last reported them; and builds just sent, as
   *  provisional sites (drawn at once, 0%) until the server's arrive. */
  private serverSites: Site[] = [];
  private serverQueued: Site[] = [];
  private provisional: Site[] = [];

  /** The sites shown: the server's, and the provisional ones it hasn't
   *  reported yet (by hex), to the map and the Build tab. */
  private publishSites(): void {
    const same = (a: Site, b: Site) => a.at[0] === b.at[0] && a.at[1] === b.at[1];
    this.provisional = this.provisional.filter((p) => !this.serverSites.some((x) => same(x, p)) && !this.serverQueued.some((x) => same(x, p)));
    this.sites = [...this.serverSites, ...this.serverQueued, ...this.provisional];
    this.renderer.setSites(this.sites, this.faction);
    setBuildSites(this.sites);
  }

  /** The commander's prospecting findings (from their state). */
  setFindings(findings: Finding[], cols: number, cell: number): void {
    this.findings = findings;
    this.intelGrid = { cols, cell };
  }

  /** Reports bought (claims, true or not), drawn apart from findings. */
  private reports: Report[] = [];
  setReports(reports: Report[]): void {
    this.reports = reports;
  }

  /** Claimed cells: hatched in the ore's hue with a dashed outline (never
   *  the findings' solid heatmap); a cell surveyed by the commander keeps
   *  only the outline, its own findings winning. Up close, the claim in
   *  words. */
  private drawReports(parts: string[], screen: (x: number, y: number) => string): void {
    const { cols, cell } = this.intelGrid;
    if (!cols || !this.reports.length || !this.renderer.options.intel) return;
    const own = new Set(this.findings.map((f) => f.cell));
    const hue = (o: string) => ORE_HUES.find(([x]) => x === oreName(o))?.[1] ?? "#cccccc";
    const view = this.canvasRect();
    const used = new Set<string>();
    const cells: string[] = [];
    // Several claims on one cell: one shape, outlined in the first's hue.
    const byCell = new Map<number, Report[]>();
    for (const r of this.reports) {
      const l = byCell.get(r.cell);
      if (l) l.push(r);
      else byCell.set(r.cell, [r]);
    }
    let drawn = 0;
    for (const [c, rs] of byCell) {
      if (drawn > 2000) break;
      const q0 = (c % cols) * cell;
      const r0 = Math.floor(c / cols) * cell;
      const corner = (q: number, r: number) => screen(hexX(q, r), hexY(r));
      const pts = [corner(q0, r0), corner(q0 + cell, r0), corner(q0 + cell, r0 + cell), corner(q0, r0 + cell)];
      const xy = pts.map((p) => p.split(",").map(Number));
      const xs = xy.map((p) => p[0]);
      const ys = xy.map((p) => p[1]);
      if (Math.max(...xs) < 0 || Math.min(...xs) > view.width || Math.max(...ys) < 0 || Math.min(...ys) > view.height) continue;
      drawn++;
      const first = rs[0];
      const ore = oreName(first.ore);
      const mine = own.has(c);
      if (!mine) used.add(ore);
      cells.push(`<polygon class="report-cell${mine ? " surveyed" : ""}" points="${pts.join(" ")}" stroke="${hue(first.ore)}"${mine ? "" : ` fill="url(#report-hatch-${ore})"`}/>`);
      // Up close: the claims in words.
      if (Math.hypot(xy[2][0] - xy[0][0], xy[2][1] - xy[0][1]) >= 420) {
        const [cx, cy] = screen(hexX(q0 + cell / 2, r0 + cell / 2), hexY(r0 + cell / 2)).split(",").map(Number);
        rs.forEach((r, k) => {
          cells.push(`<text class="report-label" x="${cx.toFixed(1)}" y="${(cy + 30 + k * 14).toFixed(1)}" fill="${hue(r.ore)}">Claim: ${oreLabel(oreName(r.ore)).toLowerCase()} ${short(r.amount)} · ${r.from === this.faction ? "you" : sideName(r.from)}</text>`);
        });
      }
    }
    if (!cells.length) return;
    const defs = [...used].map(
      (o) =>
        `<pattern id="report-hatch-${o}" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)"><rect width="7" height="7" fill="none"/><line x1="0" y1="0" x2="0" y2="7" stroke="${hue(o)}" stroke-width="2" opacity="0.45"/></pattern>`,
    );
    parts.push(`<defs>${defs.join("")}</defs>`, ...cells);
  }

  /** Prospected cells' figures, once a cell is big enough on screen to
   *  read (the ground's heatmap shows the rest). */
  private drawFindings(parts: string[], screen: (x: number, y: number) => string): void {
    const { cols, cell } = this.intelGrid;
    if (!cols || !this.renderer.options.intel) return;
    const ore = (o: { ore: string; amount: number; error_pct: number }) =>
      `${oreLabel(o.ore)} ${short(o.amount)}${o.error_pct ? ` ±${o.error_pct}%` : ""}`;
    for (const f of this.findings) {
      const q0 = (f.cell % cols) * cell;
      const r0 = Math.floor(f.cell / cols) * cell;
      const corner = (q: number, r: number) => screen(hexX(q, r), hexY(r));
      const pts = [corner(q0, r0), corner(q0 + cell, r0), corner(q0 + cell, r0 + cell), corner(q0, r0 + cell)];
      const [ax, ay] = pts[0].split(",").map(Number);
      const [bx, by] = pts[2].split(",").map(Number);
      if (Math.hypot(bx - ax, by - ay) < 420) continue;
      // Empty ground says nothing (the cursor's readout does).
      const lines = f.ores.length ? f.ores.map(ore) : f.total?.[0] ? [`Ore ${short(f.total[0])} ±${f.total[1]}%`] : [];
      const [cx, cy] = screen(hexX(q0 + cell / 2, r0 + cell / 2), hexY(r0 + cell / 2)).split(",").map(Number);
      lines.forEach((l, k) => {
        parts.push(`<text class="finding-label" x="${cx.toFixed(1)}" y="${(cy + (k - (lines.length - 1) / 2) * 14).toFixed(1)}">${l}</text>`);
      });
    }
  }

  /** The ores a mine at (q, r) would reach, from the findings: each in its
   *  atlas hue with one to three marks for how rich (per hex where samples
   *  tell, else the cell's find), as SVG text. */
  private reachLabel(q: number, r: number): string {
    const { cols, cell } = this.intelGrid;
    if (!cols) return `<tspan class="muted">Not prospected</tspan>`;
    const byCell = new Map(this.findings.map((f) => [f.cell, f]));
    const score = new Map<string, number>();
    let surveyed = false;
    let unknown = false;
    for (let dq = -MINE_REACH; dq <= MINE_REACH; dq++) {
      for (let dr = Math.max(-MINE_REACH, -dq - MINE_REACH); dr <= Math.min(MINE_REACH, -dq + MINE_REACH); dr++) {
        const [hq, hr] = [q + dq, r + dr];
        const f = byCell.get(Math.floor(hr / cell) * cols + Math.floor(hq / cell));
        if (!f) continue;
        surveyed = true;
        const [lq, lr] = [hq - Math.floor(hq / cell) * cell, hr - Math.floor(hr / cell) * cell];
        // A grade 0-255 per ore: the hex's (core), its sixteenth's (soil),
        // else the cell's find against a rich cell's.
        const grades: [string, number][] = f.grid
          ? Object.entries(f.grid).map(([o, g]) => [o, g[lr * cell + lq] ?? 0])
          : f.grid4
            ? Object.entries(f.grid4).map(([o, g]) => [o, g[Math.floor((lr * 4) / cell) * 4 + Math.floor((lq * 4) / cell)] ?? 0])
            : f.ores.map((o) => [o.ore, Math.min(255, (o.amount / 40_000) * 255)]);
        if (!f.ores.length && !f.grid && !f.grid4 && f.total?.[0]) unknown = true;
        for (const [o, g] of grades) score.set(o, Math.max(score.get(o) ?? 0, g));
      }
    }
    const found = [...score].filter(([, g]) => g > 8).sort((a, b) => b[1] - a[1]);
    if (!found.length) return `<tspan class="muted">${!surveyed ? "Not prospected" : unknown ? "Ore, kind not known" : "No ore known here"}</tspan>`;
    const hue = (o: string) => ORE_HUES.find(([x]) => x === o)?.[1] ?? "#ccc";
    return found
      .map(([o, g]) => `<tspan fill="${hue(o)}">${oreLabel(o)} ${"●".repeat(g > 150 ? 3 : g > 60 ? 2 : 1)}</tspan>`)
      .join(`<tspan class="muted"> + </tspan>`);
  }

  private arm(kind: "fight" | "patrol" | "ride" | "prospect"): void {
    this.armed = this.selection.size ? kind : null;
    if (this.armed)
      this.toast(kind === "fight" ? "Fight" : kind === "patrol" ? "Patrol" : kind === "ride" ? "Ride: click the station to go to" : "Prospect area: drag a box over the ground to survey");
    document.body.classList.toggle("arming-area", this.armed === "prospect");
    this.syncCard();
  }

  /** Plan mines: arm a box drag (stage one); the panel takes it from there. */
  planMines(): void {
    this.cancelPlan();
    this.stopPlacing();
    this.armedState = null;
    this.armed = "plan";
    this.planWhat = { what: "mine" };
    this.toast("Plan mines: drag a box over prospected ground");
    this.planHandlers();
  }

  /** What a grid in the layout editor is of. */
  private planWhat: Placing = { what: "mine" };

  private planHandlers(): void {
    setPlanHandlers({
      change: (c) => this.replan(c),
      build: () => this.buildPlan(),
      cancel: () => this.cancelPlan(),
    });
  }

  /** A grid dragged out (Shift or Alt) while placing: the layout editor,
   *  with the grid's options, before anything's sent. */
  private openGrid(p: Placing, box: [[number, number], [number, number]]): void {
    this.planWhat = { what: p.what, ...(p.recipe ? { recipe: p.recipe } : {}), ...(p.resource ? { resource: p.resource } : {}) };
    this.stopPlacing();
    this.planHandlers();
    setPlan(null);
    this.replan({ mode: "grid", box, spacing: this.rowStep(p.what), stagger: true, nudge: true, rail: false });
  }

  /** The box or an option changed: the layout again (a grid's here, a
   *  mine plan's from the server). */
  private replan(change: Partial<MinePlan>): void {
    const was = plan();
    // How many to build: the same proposal, a different share of it.
    if (change.count !== undefined && was?.mode === "mines" && Object.keys(change).length === 1) {
      setPlan(this.planSubset(was, change.count));
      return;
    }
    const raw = change.box ?? was?.box;
    if (!raw) return;
    // Kept on the map (a box dragged past its edge still plans what's on it).
    const { width: w, height: h } = this.playback.state;
    const clamp = ([q, r]: [number, number]): [number, number] => [Math.min(w - 1, Math.max(0, q)), Math.min(h - 1, Math.max(0, r))];
    const box: [[number, number], [number, number]] = [clamp(raw[0]), clamp(raw[1])];
    const mode = change.mode ?? was?.mode ?? "mines";
    const name = STRUCTURES.find(([, x]) => x.what === this.planWhat.what && x.recipe === this.planWhat.recipe)?.[0] ?? "Mine";
    const next: MinePlan = {
      mode,
      name,
      box,
      rail: change.rail ?? was?.rail ?? mode === "mines",
      ore: change.ore ?? was?.ore ?? "",
      spacing: change.spacing ?? was?.spacing ?? this.rowStep(this.planWhat.what),
      stagger: change.stagger ?? was?.stagger ?? true,
      nudge: change.nudge ?? was?.nudge ?? true,
      mines: was?.mines ?? [],
      track: was?.track ?? [],
      unfit: [],
      rows: 0,
      cols: 0,
      labels: was?.labels ?? [],
      steel: was?.steel ?? 0,
      trackHexes: was?.trackHexes ?? 0,
      waiting: mode === "mines",
      error: "",
      // A grid of mines switched to known ore can switch back.
      fromGrid: mode === "mines" ? (change.mode === "mines" || (was?.fromGrid ?? false)) : false,
      count: was?.count,
    };
    if (mode === "mines") {
      setPlan(next);
      this.playback.send({ type: "plan_mines", from: box[0], to: box[1], rail: next.rail, ...(next.ore ? { ore: next.ore } : {}) });
      return;
    }
    setPlan(this.layGrid(next));
  }

  /** A grid's spots over its box: every `spacing` hexes, rows offset by
   *  half on alternate rows (hex packing) or straight; a spot that doesn't
   *  fit moves to the nearest hex within half the spacing that does (or is
   *  left out); track along each row and between rows' first spots. */
  private layGrid(p: MinePlan): MinePlan {
    const [[q0, r0], [q1, r1]] = p.box;
    const [qa, qb, ra, rb] = [Math.min(q0, q1), Math.max(q0, q1), Math.min(r0, r1), Math.max(r0, r1)];
    const step = Math.max(1, p.spacing);
    const what = this.planWhat;
    const fits = (q: number, r: number) => !this.placeProblem(what, q, r);
    const spots: [number, number][] = [];
    const unfit: [number, number][] = [];
    const rowsOf: [number, number][][] = [];
    let cols = 0;
    for (let r = ra; r <= rb; r += step) {
      // Offset rows: the hex grid as it lies (every spot the spacing from
      // its six nearest, closest packing). Straight rows: each row pulled
      // back by half its slant, so columns stand upright on screen.
      const slant = p.stagger ? 0 : -Math.floor((r - ra) / 2);
      const line: [number, number][] = [];
      for (let q = qa + slant; q <= qb + slant; q += step) {
        if (q < 0) continue;
        let at: [number, number] | null = fits(q, r) ? [q, r] : null;
        if (!at && p.nudge) {
          const reach = Math.max(1, Math.floor(step / 2));
          search: for (let d = 1; d <= reach; d++) {
            for (let dq = -d; dq <= d; dq++) {
              for (let dr = Math.max(-d, -dq - d); dr <= Math.min(d, -dq + d); dr++) {
                if (Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr)) !== d) continue;
                if (fits(q + dq, r + dr) && !spots.some(([x, y]) => x === q + dq && y === r + dr)) {
                  at = [q + dq, r + dr];
                  break search;
                }
              }
            }
          }
        }
        if (at) {
          spots.push(at);
          line.push(at);
        } else unfit.push([q, r]);
      }
      cols = Math.max(cols, line.length + unfit.filter(([, y]) => y === r).length);
      rowsOf.push(line);
      if (spots.length >= 400) break;
    }
    // Track: along each row, and each row's first spot to the next's.
    const track: [[number, number], [number, number]][] = [];
    if (p.rail) {
      for (const line of rowsOf) for (let i = 1; i < line.length; i++) track.push([line[i - 1], line[i]]);
      const firsts = rowsOf.filter((l) => l.length).map((l) => l[0]);
      for (let i = 1; i < firsts.length; i++) track.push([firsts[i - 1], firsts[i]]);
    }
    const dist = (x: [number, number], y: [number, number]) => Math.max(Math.abs(x[0] - y[0]), Math.abs(x[1] - y[1]), Math.abs(x[0] - y[0] + x[1] - y[1]));
    const trackHexes = track.reduce((a, [x, y]) => a + dist(x, y), 0);
    // Steel: each beyond the free ones, and track unless it's free.
    const each = ({ depot: 100, station: 150, barracks: 150, mine: 200, plant: 300, breaker: 2000, guard_post: 300 } as Record<string, number>)[what.what] ?? 0;
    const free = freeLeft(what.what === "plant" ? (what.recipe ?? "") : what.what);
    const steel = Math.max(0, spots.length - free) * each + (freeLeft("track") > 0 ? 0 : trackHexes * 5);
    return { ...p, mines: spots, unfit, track, rows: rowsOf.length, cols, steel, trackHexes, labels: [], waiting: false };
  }

  /** The server's plan: mines best first, and the track joining them. */
  planReply(m: Record<string, unknown>): void {
    const p = plan();
    if (!p || p.mode !== "mines") return;
    // Each mine: where, and how much ore its footprint holds (best first).
    const raw = (m.mines as number[][] | undefined) ?? [];
    const mines = raw.map((x) => [x[0], x[1]] as [number, number]);
    const track = (m.track as [[number, number], [number, number]][] | undefined) ?? [];
    const labels = raw.map((x) => {
      const ores = this.reachLabel(x[0], x[1]).replace(/<[^>]+>/g, "");
      return x[2] ? `${ores} · ${Math.round(x[2]).toLocaleString("en-US")} in reach` : ores;
    });
    this.planFull = { mines, track, labels };
    // How many: as the player last chose, else the free mines left (at
    // least two: a steelworks wants iron and coal).
    const count = Math.min(mines.length, p.count ?? Math.max(2, freeLeft("mine")));
    setPlan(this.planSubset({ ...p, waiting: false, error: (m.error as string | undefined) ?? "" }, count));
  }

  /** The server's whole proposal, before choosing how many to build. */
  private planFull: { mines: [number, number][]; track: [[number, number], [number, number]][]; labels: string[] } = { mines: [], track: [], labels: [] };

  /** The best `count` of the proposed mines (iron and coal alternating
   *  when both are on offer and no one ore is asked for), with only the
   *  track joining them (the proposal's track is a tree with the mines at
   *  its ends: the union of the paths between the chosen ones). */
  private planSubset(p: MinePlan, count: number): MinePlan {
    const full = this.planFull;
    const n = Math.max(1, Math.min(count, full.mines.length));
    const idx = full.mines.map((_, i) => i);
    let pick: number[];
    // A spot is an iron (or coal) mine when that ore leads its label: the
    // mine takes its leading ore's name, so "Nickel + Iron" is no iron mine.
    const has = (i: number, ore: string) => full.labels[i]?.startsWith(oreLabel(ore));
    if (!p.ore && idx.some((i) => has(i, "Iron")) && idx.some((i) => has(i, "Coal"))) {
      const iron = idx.filter((i) => has(i, "Iron"));
      const coal = idx.filter((i) => has(i, "Coal"));
      const rest = idx.filter((i) => !iron.includes(i) && !coal.includes(i));
      pick = [];
      while (pick.length < n && (iron.length || coal.length || rest.length)) {
        if (iron.length && pick.length < n) pick.push(iron.shift()!);
        if (coal.length && pick.length < n) pick.push(coal.shift()!);
        if (!iron.length && !coal.length && rest.length) pick.push(rest.shift()!);
      }
      pick.sort((a, b) => a - b);
    } else pick = idx.slice(0, n);
    const key = (h: [number, number]) => `${h[0]},${h[1]}`;
    let track = full.track;
    if (n < full.mines.length && track.length) {
      const adj = new Map<string, { to: string; e: number }[]>();
      track.forEach(([a, b], e) => {
        for (const [x, y] of [[a, b], [b, a]] as const) {
          const l = adj.get(key(x)) ?? [];
          l.push({ to: key(y), e });
          adj.set(key(x), l);
        }
      });
      const keep = new Set<number>();
      const done = new Set<string>();
      // Where the tree joins the player's network (home, or their track):
      // its ends that aren't mines. Always kept, so the chosen mines are
      // joined to home, not only to each other.
      const mineKeys = new Set(full.mines.map(key));
      const roots = [...adj].filter(([k, l]) => l.length === 1 && !mineKeys.has(k)).map(([k]) => k);
      const targets = [...pick.map((i) => key(full.mines[i])), ...roots];
      for (const start of targets) {
        if (done.has(start) || !adj.has(start)) continue;
        // Walk the tree from this mine; the paths to the other chosen ones.
        const parent = new Map<string, { from: string; e: number } | null>([[start, null]]);
        const queue = [start];
        while (queue.length) {
          const at = queue.shift()!;
          for (const { to, e } of adj.get(at) ?? []) {
            if (parent.has(to)) continue;
            parent.set(to, { from: at, e });
            queue.push(to);
          }
        }
        for (const target of targets) {
          let at = target;
          if (!parent.has(at)) continue;
          done.add(at);
          while (parent.get(at)) {
            const step = parent.get(at)!;
            keep.add(step.e);
            at = step.from;
          }
        }
        done.add(start);
      }
      track = track.filter((_, e) => keep.has(e));
    }
    const mines = pick.map((i) => full.mines[i]);
    const labels = pick.map((i) => full.labels[i]);
    const hexes = track.reduce((a, [x, y]) => a + Math.max(Math.abs(x[0] - y[0]), Math.abs(x[1] - y[1]), Math.abs(x[0] - y[0] + x[1] - y[1])), 0);
    const steel = Math.max(0, mines.length - freeLeft("mine")) * 200 + (freeLeft("track") > 0 ? 0 : hexes * 5);
    return { ...p, mines, track, labels, steel, trackHexes: hexes, count: n, total: full.mines.length };
  }

  /** Build all: every spot and stretch of track in the layout, ordered at
   *  once (past the open sites' limit they queue). */
  private buildPlan(): void {
    const p = plan();
    if (!p) return;
    let placed = 0;
    let skipped = 0;
    const what: Placing = p.mode === "mines" ? { what: "mine" } : this.planWhat;
    this.placing = { ...what };
    for (const at of p.mines) {
      if (this.placeProblem(this.placing, at[0], at[1])) {
        skipped++;
        continue;
      }
      this.placeHex(at, true, true);
      placed++;
    }
    if (p.rail) {
      for (const [a, b] of p.track) {
        this.placing = { what: "track", from: a };
        this.placeHex(b, true, true);
      }
    }
    this.stopPlacing();
    const name = p.mode === "mines" ? "mine" : p.name.toLowerCase();
    this.toast(
      `Building ${placed} × ${name}${p.rail && p.track.length ? ` and ${p.track.length} stretch${p.track.length === 1 ? "" : "es"} of track` : ""}${skipped ? ` (${skipped} spot${skipped === 1 ? "" : "s"} no longer fit)` : ""}`,
    );
    setPlan(null);
  }

  private cancelPlan(): void {
    setPlan(null);
    if (this.armed === "plan") this.armed = null;
  }

  /** The plan's box corners on screen (device px), for its handles. */
  private planCorners(): [number, number][] {
    const p = plan();
    if (!p) return [];
    const [[q0, r0], [q1, r1]] = p.box;
    const [qa, qb, ra, rb] = [Math.min(q0, q1), Math.max(q0, q1), Math.min(r0, r1), Math.max(r0, r1)];
    return (
      [
        [qa, ra],
        [qb, ra],
        [qb, rb],
        [qa, rb],
      ] as [number, number][]
    ).map(([q, r]) => this.renderer.camera.toScreen(hexX(q, r), hexY(r)) as [number, number]);
  }

  /** Whether any of the selection prospects (its card offers an area). */
  private prospecting(): boolean {
    const s = this.playback.state;
    const d = this.playback.header.designs;
    return [...this.selection].some((u) => s.alive[u] && d[s.design[u]]?.modules?.some((m) => m.kind === "prospector"));
  }

  /** Prospect area, then a box: the selection shares out its cells and
   *  samples every one (Shift queues it after what they're doing). */
  private prospectOrder(a: [number, number], b: [number, number], queue: boolean): void {
    const order = { order: "prospect", from: a, to: b };
    // Only the prospectors take it; the rest of the selection carries on
    // with what it was doing.
    const s = this.playback.state;
    const designs = this.playback.header.designs;
    const can = (u: number) => !!designs[s.design[u]]?.modules?.some((m) => m.kind === "prospector");
    const ids = [...this.selection].filter((u) => s.alive[u] && can(u));
    if (!ids.length) return this.toast("None of those can prospect");
    if (!queue) return this.orderGroups([{ ids, order }], "Prospect area");
    // Queued: onto a formation only when all of it prospects (else it would
    // pull the others along); otherwise the prospectors form their own.
    const pure = (f: number) => [...this.formationOf].every(([u, g]) => g !== f || !s.alive[u] || can(u));
    const groups = this.queueGroups()
      .map((g) => ({ ...g, ids: g.ids.filter(can) }))
      .filter((g) => g.ids.length)
      .map((g) => (g.formation !== null && pure(g.formation) ? g : { formation: null, ids: g.ids, size: g.ids.length }));
    this.queueOrders(groups, () => order, "Prospect area");
  }

  /** Ride then a click: the selection rides the rail from your station
   *  nearest them (with track) to your station nearest the click. */
  private rideOrder(e: MouseEvent, queue: boolean): void {
    const [sx, sy] = this.local(e);
    const pick = this.renderer.pick(sx, sy, this.playback.alpha);
    const s = this.playback.state;
    const w = s.width;
    const hexDist = (a: [number, number], b: [number, number]) => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[0] - b[0] + a[1] - b[1]));
    const onTrack = (t: number) => {
      const [q, r] = [t % w, Math.floor(t / w)];
      return !!s.statics.track[t] || DIRS.some(([dq, dr]) => q + dq >= 0 && r + dr >= 0 && q + dq < w && r + dr < s.height && !!s.statics.track[(r + dr) * w + q + dq]);
    };
    const stations = s.depots.filter((d) => d.faction === this.faction).map((d) => [s.q(d.tile), s.r(d.tile), d.tile] as [number, number, number]);
    const nearest = (to: [number, number], list: [number, number, number][]) =>
      list.reduce<[number, number, number] | null>((best, x) => (!best || hexDist([x[0], x[1]], to) < hexDist([best[0], best[1]], to) ? x : best), null);
    const dest = nearest([pick.q, pick.r], stations);
    if (!dest || hexDist([dest[0], dest[1]], [pick.q, pick.r]) > 4) {
      this.toast("Ride: click one of your stations");
      return;
    }
    const ids = [...this.selection].filter((u) => s.alive[u]);
    if (!ids.length) return;
    const centre: [number, number] = [Math.round(ids.reduce((a, u) => a + s.q(s.tile[u]), 0) / ids.length), Math.round(ids.reduce((a, u) => a + s.r(s.tile[u]), 0) / ids.length)];
    const board = nearest(centre, stations.filter((x) => x[2] !== dest[2] && onTrack(x[2])));
    if (!board) {
      this.toast("Ride: none of your stations near them is on the rail");
      return;
    }
    const order = { order: "ride", from: [board[0], board[1]], to: [dest[0], dest[1]] };
    if (queue) this.queueOrders(this.queueGroups(), () => order, "Ride");
    else this.order(order, "Ride");
  }

  private cycleFire(): void {
    const next: Record<Fire, Fire> = { at_will: "hold", hold: "return", return: "at_will" };
    const name: Record<Fire, string> = { at_will: "fire at will", hold: "hold fire", return: "return fire" };
    this.setPosture((p) => (p.fire = next[p.fire]), (p) => `Fire state: ${name[p.fire]}`);
  }

  /** What each formation shoots at first (v31.4; the server keeps it, the
   *  client remembers what it set): BAR-style cycling button. */
  private prefer = new Map<number, "nearest" | "armour" | "soft" | "structures">();
  private static TARGETS: ["nearest" | "armour" | "soft" | "structures", string][] = [
    ["nearest", "Nearest"],
    ["armour", "Armour"],
    ["soft", "Soft"],
    ["structures", "Structures"],
  ];
  /** The selection's target priority, as the button shows it. */
  private targetNow(): string {
    const s = this.playback.state;
    const fs = new Set([...this.selection].filter((u) => s.alive[u]).map((u) => this.formationOf.get(u)));
    const ts = new Set([...fs].map((f) => (f === undefined ? "nearest" : (this.prefer.get(f) ?? "nearest"))));
    if (ts.size !== 1) return "Mixed";
    return CommandControls.TARGETS.find(([k]) => k === [...ts][0])?.[1] ?? "Nearest";
  }
  private cycleTarget(): void {
    const s = this.playback.state;
    const ids = [...this.selection].filter((id) => s.alive[id] && !this.renderer.emplacementOf(s.design[id]));
    if (!ids.length) return;
    const loose = ids.filter((u) => !this.formationOf.has(u));
    if (loose.length) {
      const id = nextId++;
      this.playback.send({ type: "form", id, units: loose.map((u) => s.key[u].toString()) });
      for (const u of loose) this.formationOf.set(u, id);
    }
    const formations = [...new Set(ids.map((u) => this.formationOf.get(u)!))];
    const order = CommandControls.TARGETS.map(([k]) => k);
    const now = this.prefer.get(formations[0]) ?? "nearest";
    const next = order[(order.indexOf(now) + 1) % order.length];
    for (const f of formations) {
      this.prefer.set(f, next);
      this.playback.send({ type: "prefer", id: f, target: next });
    }
    const words: Record<string, string> = { nearest: "the nearest first", armour: "armour first", soft: "soft targets first", structures: "structures first" };
    this.toast(`Target: ${words[next]}`);
    this.syncCard();
  }

  private cycleMoves(): void {
    this.setPosture((p) => (p.moves = p.moves === "hold" ? "maneuver" : "hold"), (p) => `Move state: ${p.moves === "hold" ? "hold position" : "maneuver"}`);
  }

  private selectAll(): void {
    const s = this.playback.state;
    this.setSelection([...s.live].filter((id) => s.faction[id] === this.faction), "replace");
  }

  /** The command card (a Solid component, `console/CommandCard`): the
   *  selected barracks' build card, or else the orders. Written to its
   *  store whenever what it shows changes. */
  private syncCard(): void {
    if (policyEditorOpen()) this.policyTarget();
    if (this.factories.size && !this.selection.size) {
      const designs = this.playback.header.designs;
      const current = new Set([...this.factories].map((k) => this.making[k]));
      const b = [...this.factories].map((k) => this.playback.state.barracks.get(k)).filter((x) => x !== undefined);
      const waiting = b.filter((x) => x.waiting).length;
      const steel = b.length === 1 ? ` · ${b[0].steel} steel${b[0].recruits === null ? "" : ` · ${b[0].recruits} recruits`}` : "";
      const old = [...current].some((d) => retired().has(d));
      setCard({
        mode: "build",
        heading: `${this.factories.size} ${(() => {
          const f = [...this.factories].filter((k) => this.isFoundry(k)).length;
          return f === this.factories.size ? (f === 1 ? "foundry" : "foundries") : f ? "barracks and foundries" : "barracks";
        })()}${steel}${waiting ? ` · ${waiting} waiting` : ""}${old ? " · making a retired design until you pick another" : ""}`,
        warn: waiting > 0,
        buttons: this.buildable().map((d, slot) => {
          const pct = designs[d].build_pct ?? 100;
          return {
            label: retired().has(d) ? `${designs[d].name} (retired)` : designs[d].name,
            key: slot < CommandControls.BUILD_SLOTS.length ? keys.hint(CommandControls.BUILD_SLOTS[slot]) : "",
            small: `${pct / 100}× time`,
            current: current.has(d),
            run: () => this.produce(d),
          };
        }),
        limits: this.factoryLimits(),
        // Copy and paste, and which parts a paste applies.
        tools: [
          ...(this.factoryLimits()
            ? [
                {
                  label: `Policy${(() => {
                    const n = [...this.factories].map((k) => this.producerPolicy(k)?.name).find(Boolean);
                    return n ? `: ${n}` : "…";
                  })()}`,
                  key: "",
                  tip: "Standing rules for these barracks: what to make, and how much steel to keep, by what's near (Anti-tank when armour comes…)",
                  pressed: policyEditorOpen(),
                  run: () => this.togglePolicy(),
                },
              ]
            : []),
          { label: "Copy", key: keys.hint("copy"), tip: "Copy this barracks' settings", run: () => this.copyFactory() },
          { label: "Paste", key: keys.hint("paste"), tip: "Paste the copied settings: the parts switched on", run: () => this.pasteFactory(), disabled: !this.copied },
          ...CommandControls.PARTS.map(([key, text, title]) => ({
            label: text,
            key: "",
            small: "paste",
            tip: title,
            pressed: this.pasteParts[key],
            run: () => {
              this.pasteParts[key] = !this.pasteParts[key];
              try {
                localStorage.setItem("ferro-paste-parts", JSON.stringify(this.pasteParts));
              } catch {
                /* remembered for this session only */
              }
              this.syncCard();
            },
          })),
        ],
      });
      return;
    }
    // A trade post selected: what it sells, a toggle a good, each with its
    // bid now (and "falling" when sales have driven it well down).
    const post = this.selectedPost();
    if (post) {
      const goods = this.aiMarket.bids.map((bid, i) => ({ g: i + 1, bid })).filter((x) => x.bid > 0 && this.goodNames[x.g] && this.goodNames[x.g] !== "Recruits");
      const selling = new Set(post.sells);
      // Credits a minute at the bids now, from what it's sold lately.
      const st = this.playback.state;
      const k = st.depots.findIndex((d) => d.tile === post.at[1] * st.width + post.at[0]);
      // The server's count where it gives one (`sold`), else watched.
      const sold = new Map(post.sells.map((g) => [g, post.sold ? (post.sold.find((x) => x.good === g)?.per_min ?? 0) : this.soldPerMin(k, g)]));
      const known = [...sold.values()].some((v) => v !== null);
      const perMin = post.sells.reduce((a, g) => a + (sold.get(g) ?? 0) * (this.aiMarket.bids[g - 1] ?? 0), 0);
      const earning = !selling.size ? "" : known ? ` · about ${Math.round(perMin).toLocaleString("en-US")} cr/min` : " · earnings after a few minutes";
      setCard({
        mode: "build",
        heading: `Trade post · ${selling.size ? `selling ${[...selling].map((g) => this.goodNames[g]?.toLowerCase()).join(", ")}` : "selling nothing"}${earning}`,
        warn: false,
        tools: [],
        buttons: goods.map(({ g, bid }) => {
          const ref = this.refPrices[g] ?? 0;
          const falling = ref > 0 && bid < ref * 0.8;
          // The way it's going: against its bid a few minutes back.
          const h = this.bidHistory.get(g - 1) ?? [];
          const then = h[Math.max(0, h.length - 36)] ?? bid;
          const trend = bid < then * 0.97 ? " ▼" : bid > then * 1.03 ? " ▲" : "";
          return {
            icon: this.goodNames[g],
            label: this.goodNames[g],
            key: "",
            small: `${bid < 10 ? bid.toFixed(1) : Math.round(bid).toLocaleString("en-US")} cr${trend}${falling ? " · low" : ""}${selling.has(g) && (sold.get(g) ?? 0) >= 1 ? ` · ${Math.round(sold.get(g)!).toLocaleString("en-US")}/min` : ""}`,
            tip: `Sell ${this.goodNames[g].toLowerCase()}: every minute, all the post holds, at the bid then. Sells what trains bring it: the spare of these goods from your stations.${falling ? " Its bid is well under its worth from selling a lot; it recovers in an hour or so." : ""}${trend ? (trend.includes("▼") ? " Falling these last minutes." : " Rising these last minutes.") : ""}`,
            pressed: selling.has(g),
            run: () => {
              const next = selling.has(g) ? post.sells.filter((x) => x !== g) : [...post.sells, g];
              this.playback.send({ type: "market_sell", at: post.at, goods: next });
              post.sells = next;
              this.toast(next.length ? `Selling ${next.map((x) => this.goodNames[x]?.toLowerCase()).join(", ")}` : "Selling nothing");
              this.syncCard();
            },
          };
        }),
      });
      return;
    }
    // Plants selected (no units): their recipe, picked as a barracks' design.
    const plants = !this.selection.size ? this.selectedPlants() : [];
    if (plants.length) {
      const making = new Set(plants.map((p) => p.recipe));
      // What they made last minute (the industry report), or why not.
      const st = this.playback.state;
      let made = 0;
      let makes = "";
      let why = "";
      let whyGood: string | undefined;
      let unlinked = false;
      for (const p of plants) {
        const now = industry.status(p.i, st);
        if (now?.why && !why) {
          why = now.long ?? now.why;
          whyGood = now.link ? now.good : undefined;
          unlinked = !!now.link;
        }
        else if (now?.rate) {
          made += now.rate;
          makes ||= now.makes ?? "";
        }
      }
      const lines = plants.reduce((n, p) => n + linesOf(recipeByName(p.recipe) ?? { name: p.recipe, inputs: [], outputs: [] }), 0);
      // Their stations' stock of what the recipe takes and makes (one
      // recipe in use): Factorio's input and output slots. An input short
      // of a cycle for every line, or an output at the station's cap, is
      // why it's idle, so it's marked.
      const slots: { good: string; n: number; cap: number; role: "in" | "out"; stuck: boolean }[] = [];
      const rec = making.size === 1 ? recipeByName([...making][0]) : undefined;
      if (rec) {
        const sites = this.playback.header.sites ?? [];
        const stations = [...new Set(plants.map((p) => sites[p.i]?.station).filter((x): x is number => x !== undefined))];
        const infos = stations.map((k) => stationInfo(k)).filter((x) => !!x);
        if (infos.length) {
          const held = (g: string) => infos.reduce((n, x) => n + (x!.freight.get(g) ?? 0), 0);
          const cap = infos.reduce((n, x) => n + (x!.capacity ?? 0), 0);
          const perPlant = linesOf(rec);
          for (const [, g, k] of rec.inputs) {
            const n = held(g);
            slots.push({ good: g, n, cap, role: "in", stuck: n < k * perPlant });
          }
          for (const [, g] of rec.outputs) {
            const n = held(g);
            slots.push({ good: g, n, cap, role: "out", stuck: cap > 0 && n >= cap });
          }
        }
      }
      setCard({
        mode: "build",
        heading: plants.length > 1 ? count(plants.length, "plants") : "Plant",
        warn: !made && !!why,
        tools: [],
        buttons: [],
        recipe: {
          current: [...making],
          lines,
          slots,
          made: made >= 0.5 ? `Made last minute: ${Math.round(made).toLocaleString("en-US")} ${makes}` : why ? (unlinked ? why : `Idle: ${why.toLowerCase()}`) : "",
          madeGood: made >= 0.5 ? undefined : whyGood,
          unlinked: made < 0.5 && unlinked,
          pick: (r: string) => this.setRecipe(r),
        },
      });
      return;
    }
    // Builders selected, on the build page: what they can build (BAR's
    // constructor menu, a page beside the orders, never instead of them).
    const engineers = this.engineers();
    if (!engineers.length) this.buildPage = false;
    if (engineers.length && this.buildPage) {
      const others = this.selection.size - engineers.length;
      setCard({
        mode: "build",
        heading: `${count(engineers.length, "engineers")}${others ? ` + ${others} other` : ""} · build`,
        warn: false,
        buttons: STRUCTURES.filter(([, p]) => p.what !== "breaker" || stormWorld()).map(([label, p], slot) => ({
          label,
          key: slot < CommandControls.BUILD_SLOTS.length ? keys.hint(CommandControls.BUILD_SLOTS[slot]) : "",
          small: costOf(p),
          current: !!this.placing && this.placing.what === p.what && this.placing.recipe === p.recipe,
          disabled: !!lockedWhy(p),
          tip: lockedWhy(p) ?? STRUCTURE_TIPS[p.recipe ?? p.what] ?? undefined,
          run: () => this.place(p),
        })),
        tools: [{ label: "Back to orders", key: "Esc", run: () => this.showBuildPage(false) }],
      });
      return;
    }
    const any = this.selection.size > 0;
    const order = (label: string, action: string, run: () => void, extra: { tip?: string; needs?: boolean; pressed?: boolean } = {}) => ({
      label,
      key: keys.hint(action),
      tip: extra.tip,
      run,
      disabled: (extra.needs ?? true) && !any,
      pressed: extra.pressed,
    });
    // Only fixed emplacements selected (guard posts): nothing that moves.
    if (this.fixedOnly()) {
      setCard({
        mode: "orders",
        heading: "",
        warn: false,
        tools: [],
        buttons: [
          order("Stop", "stop", () => this.order({ order: "stop" }, "Stop")),
          order("Fire state", "fireState", () => this.cycleFire(), { tip: "Fire at will, hold fire, return fire" }),
          order("Same type", "sameType", () => this.setSelection(this.sameTypes(true), "replace"), { tip: "The selected types in view" }),
          order("All units", "allUnits", () => this.selectAll(), { tip: "Every unit you have", needs: false }),
        ],
      });
      return;
    }
    setCard({
      mode: "orders",
      heading: "",
      warn: false,
      tools: [],
      buttons: [
        order("Fight", "fight", () => this.arm("fight"), {
          pressed: this.armed === "fight",
          tip: "Attack-move: they fire on anyone on the way. Without it, units keep the peace: they only return fire, or fire on anyone who comes onto your ground.",
        }),
        order("Patrol", "patrol", () => this.arm("patrol"), { pressed: this.armed === "patrol" }),
        order("Ride", "ride", () => this.arm("ride"), { pressed: this.armed === "ride", tip: "Ride a train: click the station to go to" }),
        // Prospectors among the selection: an area to survey between them.
        ...(this.prospecting()
          ? [
              order("Prospect area", "prospectArea", () => this.arm("prospect"), {
                pressed: this.armed === "prospect",
                tip: "Prospect area: drag a box; the selected prospectors split it up and sample every cell in it.",
              }),
            ]
          : []),
        order("Stop", "stop", () => this.order({ order: "stop" }, "Stop")),
        order("Repeat", "repeat", () => this.toggleRepeat(), { tip: "Repeat the queue" }),
        order("Fire state", "fireState", () => this.cycleFire(), { tip: "Fire at will, hold fire, return fire" }),
        order("Move state", "moveState", () => this.cycleMoves(), { tip: "Maneuver or hold position" }),
        {
          label: `Target: ${this.targetNow()}`,
          key: "",
          tip: "What they shoot at first when they can: the nearest, armour, soft targets (unarmoured), or structures (posts, camps, guard posts). A click cycles.",
          run: () => this.cycleTarget(),
          disabled: !any,
        },
        order("Policy", "policy", () => this.togglePolicy(), { pressed: policyEditorOpen(), tip: "Standing rules they follow on their own: fall back when it goes badly, engage what comes close…", needs: false }),
        order("Same type", "sameType", () => this.setSelection(this.sameTypes(true), "replace"), { tip: "The selected types in view" }),
        order("All units", "allUnits", () => this.selectAll(), { tip: "Every unit you have", needs: false }),
        // Builders among the selection: their build page.
        ...(engineers.length ? [order("Build", "buildMenu", () => this.showBuildPage(true), { tip: "What the selected builders can build" })] : []),
      ],
    });
  }

  /** The command card's build page (builders selected), or the orders. */
  private buildPage = false;

  private showBuildPage(on: boolean): void {
    this.buildPage = on;
    this.syncCard();
  }

  /** A barracks' settings, copied part by part (Ctrl+C, Ctrl+V). Each part
   *  is one setting; pasting applies only the parts switched on, so more
   *  (policies, queues) can join the list without changing how it works. */
  private copied: { product?: number; rally?: [number, number] | null } | null = null;
  private pasteParts: Record<string, boolean> = (() => {
    try {
      return { product: true, rally: true, ...JSON.parse(localStorage.getItem("ferro-paste-parts") ?? "{}") };
    } catch {
      return { product: true, rally: true };
    }
  })();
  private static PARTS: [key: "product" | "rally", label: string, title: string][] = [
    ["product", "What", "Paste what it makes (on or off)"],
    ["rally", "Rally", "Paste where its new units go (on or off)"],
  ];

  private copyFactory(): void {
    const k = [...this.factories][0];
    if (k === undefined) return;
    this.copied = { product: this.making[k], rally: this.rallies.get(k) ?? null };
    const design = this.playback.header.designs[this.making[k]]?.name ?? "?";
    const rally = this.copied.rally ? `rally ${this.copied.rally[0]} ${this.copied.rally[1]}` : "no rally point";
    this.toast(`Copied barracks: ${design}, ${rally}`);
    this.syncCard();
  }

  private pasteFactory(): void {
    const c = this.copied;
    if (!c || !this.factories.size) return;
    const applied: string[] = [];
    for (const k of this.factories) {
      if (this.pasteParts.product && c.product !== undefined) {
        this.playback.send({ type: "produce", producer: k, design: c.product });
        this.making[k] = c.product;
      }
      if (this.pasteParts.rally && c.rally !== undefined) {
        this.playback.send({ type: "rally", producer: k, to: c.rally });
        if (c.rally) this.rallies.set(k, c.rally);
        else this.rallies.delete(k);
      }
    }
    if (this.pasteParts.product && c.product !== undefined) applied.push(this.playback.header.designs[c.product]?.name ?? "?");
    if (this.pasteParts.rally && c.rally !== undefined) applied.push(c.rally ? "rally point" : "no rally point");
    this.toast(applied.length ? `Pasted to ${this.factories.size} barracks: ${applied.join(", ")}` : "Nothing to paste");
    this.syncCard();
  }

  /** What Shift+right-click copied: a plant's recipe, a trade post's sell
   *  list, or a barracks' design and rally point. */
  private clip: { kind: "plant"; recipe: string } | { kind: "post"; sells: number[] } | { kind: "barracks"; product: number; rally: [number, number] | null } | null = null;
  /** Tiles pasted to in the drag under way (null: not pasting). */
  private pasted: Set<number> | null = null;

  /** The cursor on the map (device pixels), for the pipette. */
  private hoverPx: [number, number] | null = null;

  /** Factorio's pipette (Q): the structure under the cursor, a plant with
   *  its recipe, taken in hand to build another. False when there's none
   *  of yours there. */
  private pipette(): boolean {
    if (!this.hoverPx || this.placing || this.armed) return false;
    const s = this.playback.state;
    const pick = this.renderer.pick(...this.hoverPx, this.playback.alpha);
    const t = pick.tile;
    if (t === null) return false;
    const here = this.settingsAt(t);
    const sites = this.playback.header.sites ?? [];
    let p: Placing | null = null;
    if (here?.kind === "post") p = { what: "trade_post" };
    else if (here?.kind === "plant") p = { what: "plant", recipe: this.switched.get(here.sites[0]) ?? sites[here.sites[0]].name };
    else if (here?.kind === "barracks") p = { what: this.isFoundry(here.k) ? "foundry" : "barracks" };
    else {
      const d = s.depots.find((x) => x.tile === t && x.faction === this.faction);
      if (d?.kind === DepotKind.Station) p = { what: "station" };
      else if (d?.kind === DepotKind.Depot || d?.kind === DepotKind.Field) p = { what: "depot" };
      else if (d?.kind === DepotKind.Mine) p = { what: "mine" };
    }
    if (!p) return false;
    const locked = lockedWhy(p);
    if (locked) {
      this.toast(locked);
      return true;
    }
    this.place(p);
    return true;
  }

  /** A construction site of yours at a hex, in words for the hover
   *  readout: how far along, why it's stuck, or why a queued one waits. */
  siteTip(q: number, r: number): string {
    const site = this.sites.find((x) => x.at[0] === q && x.at[1] === r);
    if (!site) return "";
    const name = siteName(site);
    if (site.queued) {
      const s = this.playback.state;
      const reg = s.statics.region?.[r * s.width + q];
      // A breaker beyond the wall keeps its place until a breach reaches it.
      if (site.what === "breaker" && reg !== undefined && reg !== REGION.WALL && reg !== REGION.INSIDE) return `${name} (planned): waiting for the wall to reach it`;
      return `${name} (planned): opens when one of your open sites is done`;
    }
    const pct = Math.floor((100 * Math.min(1, siteDone(site) / Math.max(1, site.need))));
    const why = site.blocked ? BLOCKED[site.blocked] : undefined;
    return why ? `${name}: ${pct}% built, ${why[0].toLowerCase()}. ${why[1]}` : `${name}: ${pct}% built`;
  }

  /** Your plant, trade post or barracks at a tile, with what it is set to. */
  private settingsAt(t: number): { kind: "plant"; sites: number[] } | { kind: "post"; at: [number, number] } | { kind: "barracks"; k: number } | null {
    const s = this.playback.state;
    const [q, r] = [s.q(t), s.r(t)];
    const post = this.aiMarket.posts.find((p) => p.at[0] === q && p.at[1] === r);
    if (post) return { kind: "post", at: post.at };
    const sites = this.playback.header.sites ?? [];
    const plants = sites.flatMap((x, i) => (x.kind === "plant" && x.faction === this.faction && (x.tile === t || s.depots[x.station]?.tile === t) ? [i] : []));
    if (plants.length) return { kind: "plant", sites: plants };
    const k = s.statics.producers.findIndex((p) => p.tile === t && p.faction === this.faction);
    if (k >= 0) return { kind: "barracks", k };
    return null;
  }

  private clipWords(): string {
    const c = this.clip;
    if (!c) return "";
    if (c.kind === "plant") return c.recipe;
    if (c.kind === "post") return c.sells.length ? `selling ${c.sells.map((g) => this.goodNames[g]?.toLowerCase()).join(", ")}` : "selling nothing";
    return this.playback.header.designs[c.product]?.name ?? "a design";
  }

  private copySettings(here: NonNullable<ReturnType<CommandControls["settingsAt"]>>): void {
    const sites = this.playback.header.sites ?? [];
    if (here.kind === "plant") this.clip = { kind: "plant", recipe: this.switched.get(here.sites[0]) ?? sites[here.sites[0]].name };
    else if (here.kind === "post") this.clip = { kind: "post", sells: [...(this.aiMarket.posts.find((p) => p.at === here.at)?.sells ?? [])] };
    else this.clip = { kind: "barracks", product: this.making[here.k], rally: this.rallies.get(here.k) ?? null };
    this.toast(`Copied ${this.clipWords()}: Shift+click another to paste`);
  }

  private pasteAt(here: NonNullable<ReturnType<CommandControls["settingsAt"]>>, t: number): void {
    const c = this.clip;
    if (!c || !this.pasted || this.pasted.has(t)) return;
    this.pasted.add(t);
    const s = this.playback.state;
    const sites = this.playback.header.sites ?? [];
    if (c.kind === "plant" && here.kind === "plant") {
      for (const i of here.sites) {
        const from = this.switched.get(i) ?? sites[i].name;
        if (from === c.recipe) continue;
        this.playback.send({ type: "set_recipe", at: [s.q(sites[i].tile), s.r(sites[i].tile)], from, to: c.recipe });
        this.switched.set(i, c.recipe);
      }
    } else if (c.kind === "post" && here.kind === "post") {
      this.playback.send({ type: "market_sell", at: here.at, goods: c.sells });
      const p = this.aiMarket.posts.find((x) => x.at === here.at);
      if (p) p.sells = [...c.sells];
    } else if (c.kind === "barracks" && here.kind === "barracks") {
      if (this.pasteParts.product) {
        this.playback.send({ type: "produce", producer: here.k, design: c.product });
        this.making[here.k] = c.product;
      }
      if (this.pasteParts.rally) {
        this.playback.send({ type: "rally", producer: here.k, to: c.rally });
        if (c.rally) this.rallies.set(here.k, c.rally);
        else this.rallies.delete(here.k);
      }
    }
    this.overlayDirty = true;
  }

  /** The selected barracks' limits for their card (v31.3; null on older
   *  servers): each shown when they agree, blank when they differ; a change
   *  goes to every selected one, keeping each one's other limit. */
  private factoryLimits(): { keep: number | null; max: number | null; set: (field: "keep" | "max", v: number) => void } | null {
    const s = this.playback.state;
    const ks = [...this.factories];
    const at = (k: number) => {
      const p = s.statics.producers[k];
      return p ? industry.barracksLimits(s.q(p.tile), s.r(p.tile)) : null;
    };
    const ls = ks.map((k) => this.limitsSent.get(k) ?? at(k));
    if (!ls.length || ls.some((l) => !l)) return null;
    const same = (f: "keep" | "max") => (ls.every((l) => l![f] === ls[0]![f]) ? ls[0]![f] : null);
    return {
      keep: same("keep"),
      max: same("max"),
      set: (field, v) => {
        const n = Math.max(0, Math.floor(v || 0));
        ks.forEach((k, i) => {
          const was = ls[i]!;
          const next = { keep: field === "keep" ? n : was.keep, max: field === "max" ? n : was.max };
          this.playback.send({ type: "limit", producer: k, keep_steel: next.keep, max_units: next.max });
          this.limitsSent.set(k, next);
        });
        this.toast(field === "keep" ? (n ? `Keeping ${n.toLocaleString("en-US")} steel` : "No steel kept back") : n ? `Up to ${n} of the design` : "No cap on the design");
        this.syncCard();
      },
    };
  }
  /** Limits just sent, until the state says (by producer). */
  private limitsSent = new Map<number, { keep: number; max: number }>();

  /** The policy on producer `k` (the state's), if any. */
  private producerPolicy(k: number): { name: string; yaml: string; cost: number; last_rule: number | null } | null {
    const s = this.playback.state;
    const p = s.statics.producers[k];
    return p ? industry.barracksPolicy(s.q(p.tile), s.r(p.tile)) : null;
  }

  /** Designs a producer can make, in id order (the build bar's slots). */
  /** Whether producer `k` is a foundry (the state says so). */
  private isFoundry(k: number): boolean {
    const p = this.playback.state.statics.producers[k];
    const s = this.playback.state;
    return !!p && industry.isFoundry(s.q(p.tile), s.r(p.tile));
  }

  /** A titan design (made only at foundries). */
  private isTitan(d: number): boolean {
    return !!this.playback.header.designs[d]?.footprint;
  }

  private buildable(): number[] {
    // Quickest first (infantry, anti-tank, then the heavier designs).
    // Retired designs are out, unless a selected barracks still makes one.
    // Titans at foundries, the rest at barracks (a mix: both).
    const making = new Set([...this.factories].map((k) => this.making[k]));
    const kinds = new Set([...this.factories].map((k) => this.isFoundry(k)));
    return this.playback.header.designs
      .map((d, i) => [d.build_pct ?? 0, i] as const)
      .filter(([, i]) => kinds.has(this.isTitan(i)))
      .filter(([pct, i]) => pct > 0 && (offerable(i, this.playback.header.designs[i].name) || making.has(i)))
      .sort((a, b) => a[0] - b[0] || a[1] - b[1])
      .map(([, i]) => i);
  }

  /** The own producer on a tile, if any. */
  private producerAt(tile: number | null): number | null {
    if (tile === null) return null;
    const list = this.playback.state.statics.producers;
    const k = list.findIndex((p) => p.tile === tile && p.faction === this.faction);
    return k < 0 ? null : k;
  }

  private setFactories(ids: number[], mode: "replace" | "add"): void {
    if (mode === "replace") this.factories.clear();
    for (const k of ids) this.factories.add(k);
    this.syncCard();
  }

  /** Right-click with producers selected: where their new units go (BAR's rally point). */
  private rallyOrder(e: MouseEvent): void {
    const [sx, sy] = this.local(e);
    const pick = this.renderer.pick(sx, sy, this.playback.alpha);
    if (pick.tile === null) return;
    for (const k of this.factories) {
      this.playback.send({ type: "rally", producer: k, to: [pick.q, pick.r] });
      this.rallies.set(k, [pick.q, pick.r]);
    }
    this.toast("Rally point set");
    this.ping(e.offsetX, e.offsetY, "#f2d27a");
  }

  private produce(design: number): void {
    // Only where it can be made: a titan at foundries, the rest elsewhere.
    for (const k of [...this.factories].filter((k) => this.isFoundry(k) === this.isTitan(design))) {
      this.playback.send({ type: "produce", producer: k, design });
      this.making[k] = design;
      this.asked.set(k, { design, tick: this.playback.state.tick });
    }
    this.toast(`${this.factories.size} producer${this.factories.size > 1 ? "s" : ""}: ${this.playback.header.designs[design].name}`);
    this.syncCard();
  }

  /** Designs just asked of barracks (by producer), and when: a state well
   *  after that shows another, and the player hears it wasn't taken. */
  private asked = new Map<number, { design: number; tick: number }>();

  /** The commander's state: what each barracks really makes (the card
   *  follows it), and any order it didn't take, said. */
  checkProduce(state: Record<string, unknown>): void {
    const s = this.playback.state;
    const tick = (state.tick as number | undefined) ?? s.tick;
    const list = (state.barracks as { at: [number, number]; design?: number }[] | undefined) ?? [];
    const byHex = new Map(list.map((b) => [`${b.at[0]},${b.at[1]}`, b.design]));
    const producers = s.statics.producers;
    producers.forEach((p, k) => {
      if (p.faction !== this.faction) return;
      const real = byHex.get(`${s.q(p.tile)},${s.r(p.tile)}`);
      if (real === undefined) return;
      const want = this.asked.get(k);
      if (want && tick > want.tick + 30) {
        this.asked.delete(k);
        if (real !== want.design) this.toast(`Barracks didn't take that design: still making ${this.playback.header.designs[real]?.name ?? "what it was"}`);
      }
      if (!this.asked.has(k)) this.making[k] = real;
    });
    if (this.factories.size) this.syncCard();
  }

  private stopKeys: () => void = () => {};

  dispose(): void {
    this.abort.abort();
    this.stopKeys();
    setCard({ mode: "none", buttons: [], tools: [] });
    setSummary(null);
    setBar("idle", { count: 0, key: "", run: () => {} });
    this.box.classList.add("hidden");
    this.overlay.replaceChildren();
  }

  /** The canvas's place on the page, read once a resize (a read after
   *  the overlay's writes would force a layout each time). */
  private rectCache: DOMRect | null = null;
  private rectWatch: ResizeObserver | null = null;
  private canvasRect(): DOMRect {
    if (!this.rectWatch) {
      this.rectWatch = new ResizeObserver(() => (this.rectCache = null));
      this.rectWatch.observe(this.canvas);
      window.addEventListener("resize", () => (this.rectCache = null));
    }
    this.rectCache ??= this.canvas.getBoundingClientRect();
    return this.rectCache;
  }

  private local(e: MouseEvent): [number, number] {
    const rect = this.canvasRect();
    return [(e.clientX - rect.left) * this.renderer.dpr, (e.clientY - rect.top) * this.renderer.dpr];
  }

  /** Own units under a screen rectangle (device pixels). */
  private unitsIn(x0: number, y0: number, x1: number, y1: number): number[] {
    const s = this.playback.state;
    const cam = this.renderer.camera;
    const out: number[] = [];
    for (const id of s.live) {
      if (s.faction[id] !== this.faction) continue;
      // Comms posts and training camps stand still: never in a box.
      if (this.renderer.emplacementOf(s.design[id])) continue;
      const [wx, wy] = this.renderer.unitPos(id, this.playback.alpha);
      const [sx, sy] = cam.toScreen(wx, wy);
      if (sx >= x0 && sx <= x1 && sy >= y0 && sy <= y1) out.push(id);
    }
    return out;
  }

  /** The tiles within `n` hexes of (q, r), on the map. */
  private hexesWithin(q: number, r: number, n: number): number[] {
    const s = this.playback.state;
    const out: number[] = [];
    for (let dq = -n; dq <= n; dq++) {
      for (let dr = Math.max(-n, -dq - n); dr <= Math.min(n, -dq + n); dr++) {
        const [hq, hr] = [q + dq, r + dr];
        if (hq >= 0 && hr >= 0 && hq < s.width && hr < s.height) out.push(hr * s.width + hq);
      }
    }
    return out;
  }

  /** Selected construction sites, by their hex ("q,r"). */
  readonly siteSel = new Set<string>();

  /** Your construction site at a hex (a track's by either end). */
  private siteAt(q: number, r: number): Site | undefined {
    return this.sites.find((x) => (x.at[0] === q && x.at[1] === r) || (x.to && x.to[0] === q && x.to[1] === r));
  }

  private setSiteSel(at: [number, number][], mode: "replace" | "add"): void {
    if (mode === "replace") this.siteSel.clear();
    for (const a of at) this.siteSel.add(`${a[0]},${a[1]}`);
    this.renderInfo();
  }

  /** Sites like `site` (what it builds) on screen. */
  private sitesLike(site: Site): [number, number][] {
    const [bx0, by0, bx1, by1] = this.renderer.camera.bounds();
    return this.sites
      .filter((x) => x.what === site.what && x.recipe === site.recipe)
      .filter((x) => {
        const [px, py] = [hexX(x.at[0], x.at[1]), hexY(x.at[1])];
        return px >= bx0 && px <= bx1 && py >= by0 && py <= by1;
      })
      .map((x) => x.at);
  }

  /** Cancels construction: the server refunds all the steel paid in; a
   *  ghost it hasn't reported yet just goes (and is cancelled too, in case
   *  its build arrives first). */
  cancelSites(at: [number, number][]): void {
    if (!at.length) return;
    const hit = (x: { at: [number, number] }) => at.some((a) => a[0] === x.at[0] && a[1] === x.at[1]);
    for (const a of at) this.playback.send({ type: "cancel", at: a });
    this.serverSites = this.serverSites.filter((x) => !hit(x));
    this.serverQueued = this.serverQueued.filter((x) => !hit(x));
    this.provisional = this.provisional.filter((x) => !hit(x));
    this.pending = this.pending.filter((x) => !hit(x));
    for (const a of at) this.siteSel.delete(`${a[0]},${a[1]}`);
    this.publishSites();
    this.renderInfo();
    this.toast(at.length === 1 ? "Cancelled: its steel goes back home" : `Cancelled ${at.length}: their steel goes back home`);
  }

  /** Selected structures of yours (depot indices; barracks are producers,
   *  in `factories`). */
  readonly structs = new Set<number>();

  /** A structure's kind for selecting alike: a mine is a mine, a plant
   *  its recipe(s), the rest by kind. */
  private kindOf(k: number): string {
    const s = this.playback.state;
    const d = s.depots[k];
    if (!d) return "";
    return d.kind === DepotKind.Mine ? "mine" : d.kind === DepotKind.Plant ? structureName(this.playback.header, d, k) : `k${d.kind}`;
  }

  /** Your structures (not closed, not barracks) at a tile, by index. */
  private structAt(tile: number | null): number | null {
    if (tile === null) return null;
    const k = this.playback.state.depots.findIndex((d) => d.tile === tile && d.faction === this.faction && d.kind !== DepotKind.Barracks);
    return k < 0 ? null : k;
  }

  private setStructs(ids: number[], mode: "replace" | "add"): void {
    if (mode === "replace") this.structs.clear();
    for (const k of ids) this.structs.add(k);
    this.renderInfo();
    this.syncCard();
  }

  /** The AI market as the state last said: bids (credits a unit, goods 1
   *  up), this hour's offers, and the player's posts with what they sell. */
  private aiMarket: { bids: number[]; posts: { at: [number, number]; sells: number[]; sold?: { good: number; per_min: number }[]; held?: [number, number][] }[] } = { bids: [], posts: [] };

  /** A trade post's stock at a hex (the state's `held`), by good name;
   *  null where there's no post of yours. */
  postHeld(q: number, r: number): [string, number][] | null {
    const p = this.aiMarket.posts.find((x) => x.at[0] === q && x.at[1] === r);
    return p ? (p.held ?? []).map(([g, n]) => [this.goodNames[g] ?? `Good ${g}`, n] as [string, number]) : null;
  }
  private refPrices: number[] = [];
  private bidHistory = new Map<number, number[]>();
  /** The storm's stirrings to show (each a hex, when it starts, how long,
   *  its swirl's turn), and the list they came from. */
  private stirrings: { q: number; r: number; start: number; dur: number; turn: number }[] = [];
  private stirKey = "";
  /** Storm raids under way at your structures (v31.13: nest, target). */
  private raids: { at: [number, number]; target: [number, number] }[] = [];
  setAiMarket(state: Record<string, unknown>): void {
    this.raids = (state.storm_raids as { at: [number, number]; target: [number, number] }[] | undefined) ?? [];
    // The storm stirring (v31.14.1): each new list (a game minute's), a
    // brief faint disturbance at each hex at its own moment in the minute.
    const stir = state.storm_stirrings as [number, number][] | undefined;
    if (stir) {
      const key = JSON.stringify(stir);
      if (key !== this.stirKey) {
        this.stirKey = key;
        const now = performance.now();
        for (const [q, r] of stir) this.stirrings.push({ q, r, start: now + Math.random() * 50_000, dur: 3000 + Math.random() * 2500, turn: Math.random() * 360 });
      }
    }
    // What each formation shoots at first, as the server keeps it (when it
    // says; absent: nearest), over what this client remembered.
    const prefs = state.target_prefs as Record<string, "armour" | "soft" | "structures"> | undefined;
    if (prefs) {
      const next = new Map<number, "nearest" | "armour" | "soft" | "structures">();
      for (const [f, t] of Object.entries(prefs)) next.set(Number(f) & 0xffffff, t);
      for (const f of this.prefer.keys()) if (!next.has(f)) next.set(f, "nearest");
      this.prefer = next;
    }
    const m = state.ai_market as { bids?: number[]; posts?: { at: [number, number]; sells: number[]; sold?: { good: number; per_min: number }[]; held?: [number, number][] }[] } | undefined;
    if (m) {
      this.aiMarket = { bids: m.bids ?? [], posts: m.posts ?? [] };
      // Each good's bid as it went (a sample a state, the last half hour or
      // so): its trend on the post's card.
      (m.bids ?? []).forEach((b, i) => {
        const h = this.bidHistory.get(i) ?? [];
        h.push(b);
        if (h.length > 360) h.shift();
        this.bidHistory.set(i, h);
      });
    }
    if (Array.isArray(state.goods)) {
      this.goodNames = state.goods as string[];
      setPolicyGoods(this.goodNames);
    }
    if (Array.isArray(state.prices)) this.refPrices = state.prices as number[];
    this.limitsSent.clear();
    this.publishIdle();
    this.watchSales();
    this.publishAlerts(state);
    // A post's or plants' card shows stock: fresh each state.
    if (this.structs.size && !this.selection.size) this.syncCard();
  }

  /** What each trade post has sold of each good lately: its station's
   *  used a minute where the state says, else the drops in its stock (it
   *  sells all it holds each minute). Keyed "depot:good". */
  private sales = new Map<string, { last: number; first: number; drops: [number, number][] }>();
  private watchSales(): void {
    const s = this.playback.state;
    const now = performance.now();
    for (const p of this.aiMarket.posts) {
      const k = s.depots.findIndex((d) => d.tile === p.at[1] * s.width + p.at[0]);
      const info = k >= 0 ? stationInfo(k) : undefined;
      if (!info) continue;
      for (const g of p.sells) {
        const name = this.goodNames[g];
        const n = info.freight.get(name) ?? 0;
        const key = `${k}:${g}`;
        const w = this.sales.get(key) ?? { last: n, first: now, drops: [] };
        if (n < w.last) w.drops.push([now, w.last - n]);
        w.last = n;
        w.drops = w.drops.filter(([t]) => now - t < 10 * 60_000);
        this.sales.set(key, w);
      }
    }
  }
  /** A post's sales of good `g` a minute (null: not known yet). */
  private soldPerMin(k: number, g: number): number | null {
    const used = stationInfo(k)?.flow.get(this.goodNames[g])?.used;
    if (used) return used;
    const w = this.sales.get(`${k}:${g}`);
    if (!w) return null;
    const mins = Math.min(10, (performance.now() - w.first) / 60_000);
    if (mins < 1.5 && !w.drops.length) return null;
    return w.drops.reduce((a, [, n]) => a + n, 0) / Math.max(1, mins);
  }

  /** Your structures as last seen (tile to kind), to notice one lost; and
   *  those lost lately (hex, when). */
  private ownSeen: Map<number, number> | null = null;
  private lost: { at: [number, number]; t: number }[] = [];

  /** The alert strip: structures lost (the last five minutes), titans
   *  unpowered, sites starved, plants idle for want of a good, and trade
   *  posts selling into a collapsed bid. */
  private problems = problems;
  private publishAlerts(state: Record<string, unknown>): void {
    const s = this.playback.state;
    const now = performance.now();
    const hex = (t: number): [number, number] => [s.q(t), s.r(t)];
    // Lost: yours last time, gone or someone else's now (not one you took
    // down: those are marked 255 while it happens).
    const mine = new Map<number, number>();
    for (const d of s.depots) if (d.faction === this.faction) mine.set(d.tile, d.kind);
    const down = new Set(s.depots.filter((d) => d.faction === 255).map((d) => d.tile));
    const taking = new Set(this.decommissions.map((d) => d.at[1] * s.width + d.at[0]));
    if (this.ownSeen) {
      for (const t of this.ownSeen.keys()) if (!mine.has(t) && !down.has(t) && !taking.has(t)) this.lost.push({ at: hex(t), t: now });
    }
    this.ownSeen = mine;
    this.lost = this.lost.filter((x) => now - x.t < 5 * 60_000);
    const list: Alert[] = [];
    if (this.lost.length)
      list.push({ id: "lost", icon: "lost", label: count(this.lost.length, "structures") + " lost", detail: "Destroyed or taken in the last five minutes.", at: this.lost.map((x) => x.at) });
    // A storm raid heading for your structures: where it's going.
    if (this.raids.length) {
      const one = this.raids.length === 1;
      list.push({
        id: "raid",
        icon: "raid",
        label: one ? "Storm raid heading for your station" : `${this.raids.length} storm raids heading for your stations`,
        detail: "Creatures from a storm nest are coming for it: put units in their way. The camera goes to the target.",
        at: this.raids.map((x) => x.target),
      });
    }
    // The army's steel upkeep unpaid (v31): every barracks waits.
    if (upkeep()?.unpaid) {
      const at = s.statics.producers.filter((p) => p.faction === this.faction).map((p) => hex(p.tile));
      list.push({ id: "upkeep", icon: "Steel", label: "Upkeep unpaid: barracks stopped", detail: "Your army's steel upkeep went unpaid, so no barracks makes anything until there's steel for it. Make more steel, or have fewer units.", at });
    }
    // Formations over command capacity (v31): they hold, taking no orders.
    const cmd = command();
    if (cmd?.uncommanded.size) {
      // Where they are: the server's, else what this client formed.
      const at: [number, number][] = [...cmd.at];
      if (!at.length) {
        for (const f of cmd.uncommanded) {
          const u = [...this.formationOf].find(([, g]) => g === f)?.[0];
          if (u !== undefined && s.alive[u]) at.push(hex(s.tile[u]));
        }
      }
      list.push({ id: "command", icon: "command", label: `${count(cmd.uncommanded.size, "formations")} over command capacity`, detail: `They hold and take no orders. Command: ${cmd.used} of ${cmd.capacity}; build a comms post for 20 more.`, at });
    }
    const unpowered = [...s.live].filter((u) => s.faction[u] === this.faction && isUnpowered(s.design[u]));
    if (unpowered.length)
      list.push({ id: "unpowered", icon: "Batteries", label: count(unpowered.length, "titans") + " unpowered", detail: "No fuel or batteries this minute: they stand still until their station has some.", at: unpowered.map((u) => hex(s.tile[u])) });
    const starved = this.serverSites.filter((x) => x.blocked === "no_steel" || x.blocked === "no_engineers" || x.blocked === "reach");
    if (starved.length)
      list.push({ id: "starved", icon: "Steel", label: count(starved.length, "sites") + " stuck", detail: "Construction waiting for steel or engineers.", at: starved.map((x) => x.at) });
    // What isn't working that you can fix (v31.15's reasons), in words:
    // after the steady alerts above, before the market's.
    list.push(...this.problems.compute(state, performance.now(), { build: keys.hint("buildMenu"), stop: keys.hint("stop") }));
    const collapsed = this.aiMarket.posts.filter((p) => p.sells.some((g) => (this.refPrices[g] ?? 0) > 0 && (this.aiMarket.bids[g - 1] ?? 0) < 0.5 * this.refPrices[g]));
    if (collapsed.length)
      list.push({ id: "bids", icon: "Credits", label: count(collapsed.length, "trade posts") + " selling at half price or less", detail: "Selling a lot has driven the bid down; it recovers in an hour or so. Sell something else meanwhile.", at: collapsed.map((p) => p.at) });
    setAlerts(list);
  }

  /** Over command capacity (v31): an order to a formation that holds, or
   *  new formations past the capacity, says so (they'd take no orders). */
  private commandCheck(fresh: number, formations: number[]): void {
    const c = command();
    if (!c) return;
    const held = formations.some((f) => c.uncommanded.has(f));
    // After the order's own toast (which would cover it).
    if (held || (fresh && c.used + fresh > c.capacity)) setTimeout(() => this.toast("Over command capacity: build a comms post"), 0);
  }

  /** Your idle plants by recipe and reason, for the Production screen. */
  private publishIdle(): void {
    const s = this.playback.state;
    const sites = this.playback.header.sites ?? [];
    const groups = new Map<string, IdleGroup>();
    const totals = new Map<string, number>();
    const lower = new Map(this.goodNames.map((g) => [g.toLowerCase(), g]));
    sites.forEach((site, i) => {
      if (site.kind !== "plant" || site.faction !== this.faction || s.depots[site.station]?.faction === 255) return;
      const name = this.switched.get(i) ?? site.name;
      totals.set(name, (totals.get(name) ?? 0) + 1);
      const st = industry.status(i, s);
      if (!st?.why) return;
      const key = `${name}|${st.why}`;
      let g = groups.get(key);
      if (!g) {
        const r = recipeByName(name);
        const missing = st.good ?? (st.why.startsWith("No ") ? lower.get(st.why.slice(3).split(",")[0].trim().toLowerCase()) ?? null : null);
        const good = missing ?? (/full/i.test(st.why) && r?.outputs[0] ? r.outputs[0][1] : null);
        g = { recipe: name, why: st.why, long: st.long, link: st.link, good, idle: 0, total: 0, stations: [] };
        groups.set(key, g);
      }
      g.idle++;
      if (!g.stations.includes(site.station)) g.stations.push(site.station);
    });
    for (const g of groups.values()) g.total = totals.get(g.recipe) ?? g.idle;
    // Barracks waiting too (a cap reached, no steel, no recruits...): a
    // row per kind and reason.
    const ownProducers = s.statics.producers.map((p, k) => ({ p, k })).filter(({ p }) => p.faction === this.faction);
    const bgroups = new Map<string, IdleGroup>();
    for (const { p, k } of ownProducers) {
      const why = industry.barracksWhy(s.q(p.tile), s.r(p.tile));
      if (!why) continue;
      const kind = this.isFoundry(k) ? "Foundry" : "Barracks";
      const key = `${kind}|${why[0]}`;
      let g = bgroups.get(key);
      if (!g) {
        const good = /steel/i.test(why[0]) ? "Steel" : /recruits/i.test(why[0]) ? "Recruits" : null;
        g = { recipe: kind, why: why[0], long: why[0] === "At its cap" ? why[1].split(".")[0] : undefined, good, idle: 0, total: ownProducers.filter((x) => this.isFoundry(x.k) === (kind === "Foundry")).length, stations: [], producers: [] };
        bgroups.set(key, g);
      }
      g.idle++;
      g.producers!.push(k);
    }
    setIdleGroups([...groups.values(), ...bgroups.values()].sort((a, b) => b.idle - a.idle));
  }

  /** Selects these barracks and brings them into view (the Production
   *  screen's idle list). */
  selectProducers(ks: number[]): void {
    const s = this.playback.state;
    this.setSelection([], "replace");
    this.setFactories(ks, "replace");
    const pts = ks.map((k) => s.statics.producers[k]?.tile).filter((t): t is number => t !== undefined).map((t) => [hexX(s.q(t), s.r(t)), hexY(s.r(t))]);
    if (!pts.length) return;
    const pad = 4;
    this.renderer.camera.fit(Math.min(...pts.map((p) => p[0])) - pad, Math.min(...pts.map((p) => p[1])) - pad, Math.max(...pts.map((p) => p[0])) + pad, Math.max(...pts.map((p) => p[1])) + pad);
  }

  /** Selects these structures and brings them into view (the Production
   *  screen's idle list). */
  selectStations(stations: number[]): void {
    const s = this.playback.state;
    this.setSelection([], "replace");
    this.setStructs(stations, "replace");
    const xs = stations.map((k) => s.depots[k]?.tile).filter((t): t is number => t !== undefined);
    if (!xs.length) return;
    const pts = xs.map((t) => [hexX(s.q(t), s.r(t)), hexY(s.r(t))]);
    const pad = 4;
    this.renderer.camera.fit(Math.min(...pts.map((p) => p[0])) - pad, Math.min(...pts.map((p) => p[1])) - pad, Math.max(...pts.map((p) => p[0])) + pad, Math.max(...pts.map((p) => p[1])) + pad);
  }

  /** The selected structure, if it's one of the player's trade posts. */
  private selectedPost(): { at: [number, number]; sells: number[]; sold?: { good: number; per_min: number }[] } | null {
    if (this.structs.size !== 1 || this.selection.size) return null;
    const s = this.playback.state;
    const d = s.depots[[...this.structs][0]];
    if (!d) return null;
    const [q, r] = [s.q(d.tile), s.r(d.tile)];
    return this.aiMarket.posts.find((p) => p.at[0] === q && p.at[1] === r) ?? null;
  }

  /** Recipes switched here, not yet in a new header (site index to name). */
  private switched = new Map<number, string>();

  /** The selected structures' plants (site indexes and what each makes). */
  private selectedPlants(): { i: number; at: [number, number]; recipe: string }[] {
    const s = this.playback.state;
    const sites = this.playback.header.sites ?? [];
    const out: { i: number; at: [number, number]; recipe: string }[] = [];
    sites.forEach((site, i) => {
      if (site.kind !== "plant" || site.faction !== this.faction || !this.structs.has(site.station)) return;
      out.push({ i, at: [s.q(site.tile), s.r(site.tile)], recipe: this.switched.get(i) ?? site.name });
    });
    return out;
  }

  /** Switches every selected plant to `to` (one order each, named by what
   *  it makes now: plants sharing a station are separate). */
  private setRecipe(to: string): void {
    const plants = this.selectedPlants().filter((p) => p.recipe !== to);
    for (const p of plants) {
      this.playback.send({ type: "set_recipe", at: p.at, from: p.recipe, to });
      this.switched.set(p.i, to);
    }
    this.toast(plants.length ? `${count(plants.length, "plants")}: ${to}` : `Already ${to}`);
    this.syncCard();
    this.renderInfo();
  }

  /** Your structures like `k` (and producers like a producer): in view, or
   *  everywhere. */
  private structsLike(k: number, inView: boolean): number[] {
    const s = this.playback.state;
    const kind = this.kindOf(k);
    const [bx0, by0, bx1, by1] = this.renderer.camera.bounds();
    return s.depots.flatMap((d, i) => {
      if (d.faction !== this.faction || this.kindOf(i) !== kind) return [];
      if (inView) {
        const [x, y] = this.renderer.tileXY(d.tile);
        if (x < bx0 || x > bx1 || y < by0 || y > by1) return [];
      }
      return [i];
    });
  }

  private factoriesAll(inView: boolean): number[] {
    const s = this.playback.state;
    const [bx0, by0, bx1, by1] = this.renderer.camera.bounds();
    return s.statics.producers.flatMap((p, k) => {
      if (p.faction !== this.faction) return [];
      if (inView) {
        const [x, y] = this.renderer.tileXY(p.tile);
        if (x < bx0 || x > bx1 || y < by0 || y > by1) return [];
      }
      return [k];
    });
  }

  /** Shift-clicks on structures, by kind (time): three of a kind quickly
   *  bring the tip that they can be selected all at once. */
  private shiftClicks: { kind: string; t: number }[] = [];
  private noteShiftClick(kind: string): void {
    const now = performance.now();
    this.shiftClicks = [...this.shiftClicks.filter((c) => now - c.t < 4000), { kind, t: now }];
    if (this.shiftClicks.filter((c) => c.kind === kind).length >= 3) {
      showGuide(`${this.playback.header.title}.${this.faction}`, {
        id: "tip-select-kind",
        title: "Tip: select them all at once",
        lines: [`Double-click a structure to select all of that kind on screen; ${keys.hint("sameTypeAll")} selects them across the whole map.`],
      });
    }
  }

  private setSelection(ids: number[], mode: "replace" | "add" | "remove"): void {
    this.armed = null;
    if (ids.length || mode === "replace") this.setFactories([], "replace");
    if (ids.length || mode === "replace") this.structs.clear();
    if (ids.length || mode === "replace") this.siteSel.clear();
    if (mode === "replace") this.selection.clear();
    for (const id of ids) {
      if (mode === "remove") this.selection.delete(id);
      else this.selection.add(id);
    }
    this.renderer.selection = this.selection;
    // Only builders selected: their build menu, as BAR shows a
    // constructor's (Esc goes back to the orders); anything else, orders.
    const builders = this.engineers().length;
    this.buildPage = builders > 0 && builders === [...this.selection].filter((u) => this.playback.state.alive[u]).length;
    this.syncCard();
    this.renderInfo();
  }

  /** Forms the selection into a new formation and gives it `order`. */
  private order(order: Record<string, unknown>, label: string): void {
    const s = this.playback.state;
    const ids = [...this.selection].filter((id) => s.alive[id]);
    this.orderGroups([{ ids, order }], label);
  }

  /** Gives each group its own formation and order (a line order is split
   *  into stretches, one group each, as BAR spreads a formation). */
  private orderGroups(groups: { ids: number[]; order: Record<string, unknown> }[], label: string): void {
    const s = this.playback.state;
    // Emplacements (comms posts, training camps) take no orders.
    groups = groups.map((g) => ({ ...g, ids: g.ids.filter((u) => !this.renderer.emplacementOf(s.design[u])) }));
    const all = groups.flatMap((g) => g.ids);
    if (!all.length) return;
    this.commandCheck(groups.filter((g) => g.ids.length).map((g) => Math.max(1, Math.ceil(g.ids.length / (command()?.perPoint ?? 50)))).reduce((a, b) => a + b, 0), []);
    // As in BAR, fire and move states belong to the units: a new order keeps
    // them (the first selected unit's, when they differ).
    const was = this.formationOf.get(all[0]);
    const kept = was === undefined ? undefined : this.posture.get(was);
    // A policy goes with them too (resuming then means the new orders).
    const policy = was === undefined ? undefined : this.policies.get(was);
    for (const g of groups) {
      if (!g.ids.length) continue;
      const id = nextId++;
      this.playback.send({ type: "form", id, units: g.ids.map((u) => s.key[u].toString()) });
      this.playback.send({ type: "order", id, ...g.order });
      const leg = legOf(g.order);
      if (leg) this.legs.set(id, [leg]);
      if (kept) {
        this.posture.set(id, { ...kept });
        this.playback.send({ type: "posture", id, fire: kept.fire, moves: kept.moves });
      }
      // And what they shoot at first.
      const pref = was === undefined ? undefined : this.prefer.get(was);
      if (pref && pref !== "nearest") {
        this.prefer.set(id, pref);
        this.playback.send({ type: "prefer", id, target: pref });
      }
      if (policy) {
        this.playback.send({ type: "attach", id, policy: policy.yaml });
        this.notePolicy(id, { ...policy, formation: id, last_rule: null });
      }
      for (const u of g.ids) this.formationOf.set(u, id);
    }
    // The formation they left, if nobody's left in it: its policy comes off.
    if (policy && was !== undefined && ![...this.formationOf].some(([u, f]) => f === was && this.playback.state.alive[u])) {
      this.playback.send({ type: "attach", id: was, policy: null });
      this.notePolicy(was, null);
    }
    this.toast(`${label}: ${count(all.length, "units")}`);
  }

  /** The selection split for a queued order: by the formation each unit is
   *  in (whole formations take it), and the loose ones (no formation, or one
   *  whose orders weren't ours) together. */
  private queueGroups(): { formation: number | null; ids: number[]; size: number }[] {
    const s = this.playback.state;
    const by = new Map<number | null, number[]>();
    for (const u of this.selection) {
      if (!s.alive[u]) continue;
      const f = this.formationOf.get(u);
      const key = f !== undefined && this.legs.has(f) ? f : null;
      by.set(key, [...(by.get(key) ?? []), u]);
    }
    const size = new Map<number, number>();
    for (const [u, f] of this.formationOf) if (s.alive[u] && by.has(f)) size.set(f, (size.get(f) ?? 0) + 1);
    return [...by].map(([formation, ids]) => ({ formation, ids, size: formation === null ? ids.length : (size.get(formation) ?? ids.length) }));
  }

  /** Queues an order per group (`make` gives each its order): appended to
   *  existing formations, a new formation for the loose units. */
  private queueOrders(groups: { formation: number | null; ids: number[] }[], make: (k: number) => Record<string, unknown>, label: string): void {
    let n = 0;
    groups.forEach((g, k) => {
      const order = make(k);
      if (g.formation === null) {
        this.orderGroups([{ ids: g.ids, order }], label);
      } else {
        this.playback.send({ type: "order", id: g.formation, queue: true, ...order });
        this.commandCheck(0, [g.formation]);
        const leg = legOf(order);
        if (leg) this.legs.get(g.formation)?.push(leg);
      }
      n += g.ids.length;
    });
    this.toast(`${label} (queued): ${count(n, "units")}`);
  }

  /** H then a click: the selection patrols between where it stands and there
   *  (a repeating queue of two fights). */
  private patrolOrder(e: MouseEvent): void {
    const [sx, sy] = this.local(e);
    const pick = this.renderer.pick(sx, sy, this.playback.alpha);
    const s = this.playback.state;
    const ids = [...this.selection].filter((id) => s.alive[id]);
    if (!ids.length) return;
    let x = 0;
    let y = 0;
    for (const u of ids) {
      const [wx, wy] = this.renderer.unitPos(u, this.playback.alpha);
      x += wx;
      y += wy;
    }
    const [q, r] = hexAt(x / ids.length, y / ids.length);
    const radius = fitRadius(ids.length);
    this.order({ order: "fight", to: [q, r], radius }, "Patrol");
    const id = this.formationOf.get(ids[0])!;
    const leg = { order: "fight", to: [pick.q, pick.r], radius };
    this.playback.send({ type: "order", id, queue: true, ...leg });
    this.legs.get(id)?.push(legOf(leg)!);
    this.playback.send({ type: "repeat", id, on: true });
    this.repeating.add(id);
    this.ping(e.offsetX, e.offsetY, "#6fb7ff");
  }

  /** T: the selected formations' queues repeat (or stop repeating). */
  private toggleRepeat(): void {
    const s = this.playback.state;
    const formations = new Set([...this.selection].filter((u) => s.alive[u]).map((u) => this.formationOf.get(u)).filter((f) => f !== undefined && this.legs.has(f)) as number[]);
    if (!formations.size) return;
    const on = ![...formations].every((f) => this.repeating.has(f));
    for (const f of formations) {
      this.playback.send({ type: "repeat", id: f, on });
      if (on) this.repeating.add(f);
      else this.repeating.delete(f);
    }
    this.toast(on ? "Repeat on" : "Repeat off");
  }

  /** Own units of the selection's types: in view, or everywhere (BAR's Q and Ctrl+W). */
  private sameTypes(inView: boolean): number[] {
    const s = this.playback.state;
    const types = new Set([...this.selection].filter((u) => s.alive[u]).map((u) => s.design[u]));
    const cam = this.renderer.camera;
    const pool = inView ? this.unitsIn(0, 0, cam.width, cam.height) : [...s.live].filter((id) => s.faction[id] === this.faction);
    return pool.filter((id) => types.has(s.design[id]));
  }

  /** A move (fight, if armed) to a world point: the minimap's right-click. */
  orderAtWorld(wx: number, wy: number, queue: boolean): void {
    const [q, r] = hexAt(wx, wy);
    const s = this.playback.state;
    if (q < 0 || r < 0 || q >= s.width || r >= s.height || !this.selection.size) return;
    const kind = this.armed === "fight" ? "fight" : "move";
    const label = kind === "fight" ? "Fight" : "Move";
    if (queue) {
      const groups = this.queueGroups();
      this.queueOrders(groups, (k) => ({ order: kind, to: [q, r], radius: fitRadius(groups[k].size) }), label);
    } else {
      this.order({ order: kind, to: [q, r], radius: fitRadius(this.selection.size) }, label);
    }
    if (!queue) this.armed = null;
  }

  /** Whether everything selected is fixed in place (speed 0: guard posts). */
  private fixedOnly(): boolean {
    const s = this.playback.state;
    const d = this.playback.header.designs;
    return this.selection.size > 0 && [...this.selection].every((u) => (d[s.design[u]]?.speed ?? 1) === 0);
  }

  private moveOrder(e: MouseEvent, fight: boolean, queue = false): void {
    if (this.fixedOnly()) {
      this.toast("Guard posts never move");
      return;
    }
    const [sx, sy] = this.local(e);
    const pick = this.renderer.pick(sx, sy, this.playback.alpha);
    const s = this.playback.state;
    const onEnemy = pick.unit !== null && hostile(s.faction[pick.unit], this.faction);
    const kind = fight || onEnemy ? "fight" : "move";
    const label = kind === "fight" ? "Fight" : "Move";
    // A long move or fight goes by rail where that's quicker, per formation
    // (Alt: walk); a fight fights its way from the station it alights at.
    const riders = !queue && !e.altKey ? this.rideWhereQuicker([pick.q, pick.r], (ids) => ({ order: kind, to: [pick.q, pick.r], radius: fitRadius(ids.length) })) : new Set<number>();
    if (riders.size) {
      const rest = [...this.selection].filter((u) => s.alive[u] && !riders.has(u));
      if (rest.length) this.orderGroups([{ ids: rest, order: { order: kind, to: [pick.q, pick.r], radius: fitRadius(rest.length) } }], label);
    } else if (queue) {
      const groups = this.queueGroups();
      this.queueOrders(groups, (k) => ({ order: kind, to: [pick.q, pick.r], radius: fitRadius(groups[k].size) }), label);
    } else {
      this.order({ order: kind, to: [pick.q, pick.r], radius: fitRadius(this.selection.size) }, label);
    }
    this.ping(e.offsetX, e.offsetY, kind === "fight" ? "#ef5b5b" : "#7cd67c");
  }

  /** Whether the auto-ride toast has said it in full yet. */
  private rodeBefore = false;

  /** A train for a long trip, if one's quicker, for the units `ids` (a
   *  formation, or loose units together): your railed station near them and
   *  another near the target, joined by track, when walking to the first,
   *  waiting for a train (if it shows none, about a minute: one is borrowed),
   *  riding (about four times infantry's pace) and walking from the second
   *  beats walking all the way at the slowest one's pace. */
  private planRide(ids: number[], target: [number, number]): { from: [number, number]; to: [number, number] } | null {
    const s = this.playback.state;
    const designs = this.playback.header.designs;
    ids = ids.filter((u) => s.alive[u]);
    if (!ids.length) return null;
    const dist = (a: [number, number], b: [number, number]) => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[0] - b[0] + a[1] - b[1]));
    const centre: [number, number] = [Math.round(ids.reduce((a, u) => a + s.q(s.tile[u]), 0) / ids.length), Math.round(ids.reduce((a, u) => a + s.r(s.tile[u]), 0) / ids.length)];
    const walkHexes = dist(centre, target);
    if (walkHexes < 40) return null;
    // Ticks to cover hexes: about a thousand progress a hex, `speed` a tick.
    const slowest = Math.max(1, Math.min(...ids.map((u) => designs[s.design[u]]?.speed ?? 100)));
    const foot = Math.max(1, ...designs.filter((d) => (d.chassis === "Foot" || d.chassis === "Robot") && d.speed > 0).map((d) => d.speed));
    const walkTicks = (hexes: number) => (hexes * 1000) / slowest;
    const trainTicks = (hexes: number) => (hexes * 1000) / (4 * foot);
    const w = s.width;
    const railed = (t: number) => {
      const [q, r] = [t % w, Math.floor(t / w)];
      return !!s.statics.track[t] || DIRS.some(([dq, dr]) => q + dq >= 0 && r + dr >= 0 && q + dq < w && r + dr < s.height && !!s.statics.track[(r + dr) * w + q + dq]);
    };
    const stations = s.depots
      .map((d, k) => ({ d, k }))
      .filter(({ d }) => d.faction === this.faction && railed(d.tile))
      .map(({ d, k }) => ({ tile: d.tile, k, at: [s.q(d.tile), s.r(d.tile)] as [number, number] }));
    const near = (p: [number, number], reach: number) =>
      stations.filter((x) => dist(x.at, p) <= reach).sort((a, b) => dist(a.at, p) - dist(b.at, p));
    let best: { from: [number, number]; to: [number, number]; cost: number } | null = null;
    const walkAll = walkTicks(walkHexes);
    // Where they'd board: a station within reach of any of them (a strung
    // out group's middle can be far from all), the walk to it their mean.
    const at = ids.map((u) => [s.q(s.tile[u]), s.r(s.tile[u])] as [number, number]);
    const meanWalk = (p: [number, number]) => at.reduce((a, x) => a + dist(x, p), 0) / at.length;
    const boarding = stations
      .filter((x) => at.some((u) => dist(u, x.at) <= 30))
      .sort((a, b) => meanWalk(a.at) - meanWalk(b.at))
      .slice(0, 4);
    for (const a of boarding) {
      // No trains there now: one's borrowed for riders waiting (a minute or so).
      const wait = (stationInfo(a.k)?.trains ?? 1) > 0 ? 100 : 600;
      for (const b of near(target, 40).slice(0, 4)) {
        if (a.tile === b.tile) continue;
        const rail = this.railPath(a.tile, b.tile);
        if (!rail) continue;
        const cost = walkTicks(meanWalk(a.at)) + wait + trainTicks(rail.length) + walkTicks(dist(b.at, target));
        if (cost < walkAll * 0.85 && (!best || cost < best.cost)) best = { from: a.at, to: b.at, cost };
      }
    }
    return best && { from: best.from, to: best.to };
  }

  /** Long trips by rail where that's quicker, per formation (a spread-out
   *  selection plans from where each part is): those that ride take the
   *  train and then `then` (queued), the rest are left for the caller.
   *  Returns the units that ride. */
  private rideWhereQuicker(target: [number, number], then: (ids: number[]) => Record<string, unknown>): Set<number> {
    const riders = new Set<number>();
    const rides: string[] = [];
    for (const g of this.queueGroups()) {
      const ride = this.planRide(g.ids, target);
      if (!ride) continue;
      this.orderGroups([{ ids: g.ids, order: { order: "ride", from: ride.from, to: ride.to } }], "Ride");
      const f = this.formationOf.get(g.ids[0]);
      if (f !== undefined) {
        const next = then(g.ids);
        this.playback.send({ type: "order", id: f, queue: true, ...next });
        const leg = legOf(next);
        if (leg) this.legs.get(f)?.push(leg);
      }
      for (const u of g.ids) riders.add(u);
      rides.push(`${ride.from[0]}, ${ride.from[1]}`);
    }
    if (rides.length) {
      this.toast(this.rodeBefore ? `By rail from ${rides[0]}${rides.length > 1 ? ` (+${rides.length - 1} more)` : ""}` : `Riding the train from ${rides[0]}: quicker than walking (Alt walks)`);
      this.rodeBefore = true;
    }
    return riders;
  }

  private lineOrder(points: [number, number][], fight = false, queue = false, walkOnly = false): void {
    // Dense along the drawn path, so the line has no gaps.
    const hexes: [number, number][] = [];
    const seen = new Set<string>();
    for (let k = 0; k + 1 < points.length; k++) {
      const [ax, ay] = points[k];
      const [bx, by] = points[k + 1];
      const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / 2));
      for (let i = 0; i <= steps; i++) {
        const p = this.renderer.pick(ax + ((bx - ax) * i) / steps, ay + ((by - ay) * i) / steps, this.playback.alpha);
        const key = `${p.q},${p.r}`;
        if (p.tile !== null && !seen.has(key)) {
          seen.add(key);
          hexes.push([p.q, p.r]);
        }
      }
    }
    if (hexes.length < 2) return;
    const s = this.playback.state;
    const ids = [...this.selection].filter((id) => s.alive[id]);
    if (!ids.length) return;
    // Far from the line: by rail to near its middle where that's quicker,
    // then the line (each formation its share, as if queued).
    if (!queue && !walkOnly) {
      const mid = hexes[Math.floor(hexes.length / 2)];
      const kindNow = fight ? "fight_line" : "hold_line";
      const riders = this.rideWhereQuicker(mid, (g) => ({ order: kindNow, line: hexes, depth: fitDepth(g.length, hexes.length) }));
      if (riders.size === ids.length) return;
      if (riders.size) {
        const rest = ids.filter((u) => !riders.has(u));
        this.orderGroups([{ ids: rest, order: { order: kindNow, line: hexes, depth: fitDepth(rest.length, hexes.length) } }], fight ? "Fight to line" : "Hold line");
        return;
      }
    }
    // Spread evenly along the line, as BAR does: cut the drawn path into equal
    // stretches and give each the same share of the units, taken in order
    // along the line's direction so no one crosses another's path.
    const parts = Math.max(1, Math.min(MAX_LINE_PARTS, Math.ceil(hexes.length / LINE_PART), ids.length));
    const [q0, r0] = hexes[0];
    const [q1, r1] = hexes[hexes.length - 1];
    const dx = hexX(q1, r1) - hexX(q0, r0);
    const dy = hexY(r1) - hexY(r0);
    const along = (u: number) => {
      const [x, y] = this.renderer.unitPos(u, this.playback.alpha);
      return x * dx + y * dy;
    };
    const kind = fight ? "fight_line" : "hold_line";
    if (queue) {
      // Each formation takes a stretch in proportion to its size, in order
      // along the line by where it stands.
      const mean = (g: { ids: number[] }) => g.ids.reduce((a, u) => a + along(u), 0) / g.ids.length;
      const groups = this.queueGroups().sort((a, b) => mean(a) - mean(b));
      const total = groups.reduce((a, g) => a + g.size, 0);
      let done = 0;
      const cuts = groups.map((g) => {
        const from = Math.floor((done * hexes.length) / total);
        done += g.size;
        return [from, Math.max(from + 1, Math.floor((done * hexes.length) / total))];
      });
      this.queueOrders(groups, (k) => {
        const line = hexes.slice(cuts[k][0], cuts[k][1]);
        return { order: kind, line, depth: fitDepth(groups[k].size, line.length) };
      }, fight ? "Fight to line" : "Hold line");
      return;
    }
    const sorted = ids.map((u) => [along(u), u] as const).sort((a, b) => a[0] - b[0] || a[1] - b[1]).map((a) => a[1]);
    const groups = [];
    for (let k = 0; k < parts; k++) {
      const line = hexes.slice(Math.floor((k * hexes.length) / parts), Math.max(Math.floor(((k + 1) * hexes.length) / parts), Math.floor((k * hexes.length) / parts) + 1));
      const members = sorted.slice(Math.floor((k * sorted.length) / parts), Math.floor(((k + 1) * sorted.length) / parts));
      const depth = fitDepth(members.length, line.length);
      groups.push({ ids: members, order: { order: kind, line, depth } });
    }
    this.orderGroups(groups, fight ? "Fight to line" : "Hold line");
  }

  /** The policy editor: opened on the selection's policy, if it has one. */
  private togglePolicy(): void {
    if (policyEditorOpen()) return closePolicyEditor();
    const s = this.playback.state;
    // Barracks selected (no units): their policy (v31.7).
    if (!this.selection.size && this.factories.size) {
      const k = [...this.factories].find((x) => this.producerPolicy(x));
      let bp;
      try {
        bp = k === undefined ? undefined : policyFromYaml(this.producerPolicy(k)!.yaml);
      } catch {
        bp = undefined;
      }
      this.policyTarget();
      // None on them yet: start from the first barracks preset.
      const designs = this.playback.header.designs;
      openPolicyEditor(bp ?? barracksPresets(this.buildable().map((d) => ({ id: d, name: designs[d].name })))[0]?.[1]);
      return;
    }
    const f = [...this.selection].filter((u) => s.alive[u]).map((u) => this.formationOf.get(u)).find((x) => x !== undefined && this.policies.has(x));
    let p;
    try {
      p = f === undefined ? undefined : policyFromYaml(this.policies.get(f)!.yaml);
    } catch {
      p = undefined;
    }
    openPolicyEditor(p);
    this.policyTarget();
  }

  /** Attaches `yaml` (null: detaches) to every formation in the selection,
   *  loose units formed first. */
  private attachPolicy(yaml: string | null): void {
    const s = this.playback.state;
    // Barracks (v31.7): one order each.
    if (!this.selection.size && this.factories.size) {
      let name = "";
      try {
        name = yaml ? policyFromYaml(yaml).name : "";
      } catch {
        /* the state will say */
      }
      for (const k of this.factories) this.playback.send({ type: "attach_producer", producer: k, policy: yaml });
      this.toast(yaml === null ? `Policy detached: ${count(this.factories.size, "barracks")}` : `Policy "${name}": ${count(this.factories.size, "barracks")}`);
      return;
    }
    const ids = [...this.selection].filter((u) => s.alive[u]);
    if (!ids.length) return;
    const loose = ids.filter((u) => !this.formationOf.has(u));
    if (loose.length && yaml !== null) {
      const id = nextId++;
      this.playback.send({ type: "form", id, units: loose.map((u) => s.key[u].toString()) });
      for (const u of loose) this.formationOf.set(u, id);
    }
    const formations = new Set(ids.map((u) => this.formationOf.get(u)).filter((f): f is number => f !== undefined));
    let name = "";
    try {
      name = yaml ? policyFromYaml(yaml).name : "";
    } catch {
      /* the state will say */
    }
    for (const f of formations) {
      if (yaml === null && !this.policies.has(f)) continue;
      this.playback.send({ type: "attach", id: f, policy: yaml });
      this.notePolicy(f, yaml === null ? null : { formation: f, name, yaml, cost: 0, last_rule: null });
    }
    this.toast(yaml === null ? `Policy detached: ${count(ids.length, "units")}` : `Policy "${name}": ${count(ids.length, "units")}`);
    this.policyTarget();
    this.renderInfo();
  }

  /** The policies on the player's formations, from the state. */
  setPolicies(state: Record<string, unknown>): void {
    const list = state.policies as Attached[] | undefined;
    if (!list) return;
    this.policies = new Map(list.map((p) => [p.formation, p]));
    const now = performance.now();
    for (const [f, x] of this.policySent) {
      // The state has caught up (or it's been long enough to trust it).
      const there = this.policies.get(f);
      if (now - x.at > 10000 || (x.p ? there?.name === x.p.name : !there)) this.policySent.delete(f);
      else if (x.p) this.policies.set(f, x.p);
      else this.policies.delete(f);
    }
    // Formation ids from an earlier visit stay taken (a new one with the
    // same id would inherit its policy).
    for (const p of list) if (p.formation >= nextId) nextId = p.formation + 1;
    // Which units each is on, where the state says and this client doesn't
    // know (after a reload).
    const s = this.playback.state;
    const unknown = list.filter((p) => p.units?.length);
    if (unknown.length) {
      const byKey = new Map<string, number>();
      for (const u of s.live) byKey.set(s.key[u].toString(), u);
      for (const p of unknown)
        for (const k of p.units!) {
          const u = byKey.get(String(k));
          if (u !== undefined && !this.formationOf.has(u)) this.formationOf.set(u, p.formation);
        }
    }
    this.policyTarget();
  }

  private notePolicy(f: number, p: Attached | null): void {
    if (p) this.policies.set(f, p);
    else this.policies.delete(f);
    this.policySent.set(f, { p, at: performance.now() });
  }

  /** What the editor's Attach would apply to. */
  private policyTarget(): void {
    const s = this.playback.state;
    if (!this.selection.size && this.factories.size) {
      const designs = this.playback.header.designs;
      const attached = [...this.factories].map((k) => this.producerPolicy(k)?.name).filter((n): n is string => !!n);
      setPolicyTarget({ units: this.factories.size, attached, kind: "barracks", designs: this.buildable().map((d) => ({ id: d, name: designs[d].name })) });
      return;
    }
    const ids = [...this.selection].filter((u) => s.alive[u]);
    const fs = new Set(ids.map((u) => this.formationOf.get(u)));
    const attached = [...fs].map((f) => (f === undefined ? undefined : this.policies.get(f)?.name)).filter((n): n is string => !!n);
    setPolicyTarget({ units: ids.length, attached, kind: "units" });
  }

  /** The selection card's line on its policy: which, and what it did last. */
  private policyLine(ids: number[]): string {
    const fs = [...new Set(ids.map((u) => this.formationOf.get(u)))];
    const on = fs.map((f) => (f === undefined ? undefined : this.policies.get(f))).filter((p): p is Attached => !!p);
    if (!on.length) return "";
    const p = on[0];
    let last = "nothing yet";
    if (p.last_rule !== null && p.last_rule !== undefined) {
      try {
        last = `rule ${p.last_rule + 1}: ${ruleText(policyFromYaml(p.yaml).rules[p.last_rule])}`;
      } catch {
        last = `rule ${p.last_rule + 1}`;
      }
    }
    const of = fs.length > 1 ? ` (on ${on.length} of ${fs.length} groups)` : "";
    return `Policy: ${p.name}${of} · last acted: ${last}`;
  }

  private setPosture(change: (p: { fire: Fire; moves: Moves }) => void, label: (p: { fire: Fire; moves: Moves }) => string): void {
    const s = this.playback.state;
    const ids = [...this.selection].filter((id) => s.alive[id]);
    if (!ids.length) return;
    // Units not yet ordered get a formation of their own (holding still).
    const loose = ids.filter((u) => !this.formationOf.has(u));
    if (loose.length) {
      const id = nextId++;
      this.playback.send({ type: "form", id, units: loose.map((u) => s.key[u].toString()) });
      for (const u of loose) this.formationOf.set(u, id);
    }
    const formations = new Set(ids.map((u) => this.formationOf.get(u)!));
    let shown = "";
    for (const f of formations) {
      const p = this.posture.get(f) ?? { fire: "at_will", moves: "maneuver" };
      change(p);
      this.posture.set(f, p);
      this.playback.send({ type: "posture", id: f, fire: p.fire, moves: p.moves });
      shown = label(p);
    }
    this.toast(shown);
  }

  /** A short-lived marker where an order went. */
  private ping(x: number, y: number, color: string): void {
    const ns = "http://www.w3.org/2000/svg";
    const c = document.createElementNS(ns, "circle");
    c.setAttribute("cx", String(x));
    c.setAttribute("cy", String(y));
    c.setAttribute("r", "10");
    c.setAttribute("stroke", color);
    c.setAttribute("class", "ping");
    this.overlay.append(c);
    setTimeout(() => c.remove(), 700);
  }

  private bind(): void {
    const signal = this.abort.signal;
    const c = this.canvas;
    // The player's hand on mouse or keys: the overlay redraws at once.
    const touched = () => (this.overlayDirty = true);
    for (const ev of ["pointermove", "pointerdown", "pointerup", "wheel"]) c.addEventListener(ev, touched, { signal, passive: true });
    window.addEventListener("keydown", touched, { signal });
    window.addEventListener("keyup", touched, { signal });
    // A drag: where it began as a hex (world, so zooming mid-drag doesn't
    // move it), its drawn line in world positions, and whether a second
    // button cancelled it (BAR: right-click cancels a build drag).
    let drag: {
      button: number;
      x: number;
      y: number;
      moved: boolean;
      start: [number, number];
      wpath: [number, number][];
      mod: MouseEvent;
      cancelled?: boolean;
      /** A plan's corner being dragged (0..3). */
      handle?: number;
    } | null = null;
    const toWorld = (e: MouseEvent): [number, number] => this.renderer.camera.toWorld(...this.local(e)) as [number, number];
    const toScreen = (w: [number, number]): [number, number] => this.renderer.camera.toScreen(w[0], w[1]) as [number, number];
    const cancelDrag = () => {
      if (!drag) return;
      drag.cancelled = true;
      line?.remove();
      line = null;
      this.box.classList.add("hidden");
      this.rowPreview.remove();
    };
    let line: SVGPolylineElement | null = null;
    // Capture phase: these gestures replace the replay viewer's (left-drag pans).
    c.addEventListener(
      "pointerdown",
      (e) => {
        if (e.pointerType !== "mouse") return;
        e.stopImmediatePropagation();
        c.focus();
        c.setPointerCapture(e.pointerId);
        // A second button while dragging: the drag's off (no new drag).
        if (drag) {
          if (drag.button === 0 && e.button === 2) cancelDrag();
          return;
        }
        const at = this.renderer.pick(...this.local(e), this.playback.alpha);
        drag = { button: e.button, x: e.offsetX, y: e.offsetY, moved: false, start: [at.q, at.r], wpath: [toWorld(e)], mod: e };
        // Factorio's settings copy and paste: Shift+right-click a plant,
        // trade post or barracks copies it (with no units selected: Shift+
        // right is a queued order then); Shift+left-click one of the same
        // kind pastes, and a drag with Shift held pastes over each it
        // crosses.
        const free = !this.placing && !this.armed && !this.hexPicker && !plan();
        if (free && e.shiftKey && at.tile !== null) {
          const here = this.settingsAt(at.tile);
          if (e.button === 2 && here && !this.selection.size) {
            this.copySettings(here);
            drag.cancelled = true;
            return;
          }
          if (e.button === 0 && here && this.clip && here.kind === this.clip.kind) {
            this.pasted = new Set();
            this.pasteAt(here, at.tile);
            drag.cancelled = true;
            return;
          }
        }
        // On a corner of the plan's box: that corner is dragged.
        if (e.button === 0 && plan()) {
          const [sx, sy] = this.local(e);
          const k = this.planCorners().findIndex(([x, y]) => Math.hypot(x - sx, y - sy) < 12 * this.renderer.dpr);
          if (k >= 0) drag.handle = k;
        }
      },
      { signal, capture: true },
    );
    c.addEventListener(
      "pointermove",
      (e) => {
        if (e.pointerType !== "mouse" || !drag) return;
        e.stopImmediatePropagation();
        // Pasting along a drag: each structure of the copied kind crossed.
        if (this.pasted) {
          const h = this.renderer.pick(...this.local(e), this.playback.alpha);
          const here = h.tile !== null ? this.settingsAt(h.tile) : null;
          if (here && h.tile !== null && here.kind === this.clip?.kind) this.pasteAt(here, h.tile);
          return;
        }
        // The right button joining a left drag (pointer events report a
        // chorded press as a move): cancel it.
        if (drag.button === 0 && e.buttons & 2) cancelDrag();
        if (drag.cancelled) return;
        if (!drag.moved && Math.hypot(e.offsetX - drag.x, e.offsetY - drag.y) > 5) drag.moved = true;
        if (!drag.moved) return;
        const drawing = drag.button === 2 || (drag.button === 0 && this.armed === "fight");
        if (drag.handle !== undefined) {
          // A corner of the plan's box, dragged: the box follows (the
          // opposite corner stays).
          const p = plan();
          const h = this.renderer.pick(...this.local(e), this.playback.alpha);
          if (p) {
            const [[q0, r0], [q1, r1]] = p.box;
            let [qa, qb, ra, rb] = [Math.min(q0, q1), Math.max(q0, q1), Math.min(r0, r1), Math.max(r0, r1)];
            const k = drag.handle;
            if (k === 0 || k === 3) qa = h.q;
            else qb = h.q;
            if (k === 0 || k === 1) ra = h.r;
            else rb = h.r;
            setPlan({ ...p, box: [[qa, ra], [qb, rb]] });
          }
          return;
        }
        if ((this.armed === "prospect" || this.armed === "plan") && drag.button === 0) {
          // An area: the box as the order will take it (corner to corner
          // in hexes, so slanted like the map), not the screen's rectangle.
          const r = this.renderer;
          const a = { q: drag.start[0], r: drag.start[1] };
          const b = r.pick(...this.local(e), this.playback.alpha);
          const [qa, qb, ra, rb] = [Math.min(a.q, b.q), Math.max(a.q, b.q), Math.min(a.r, b.r), Math.max(a.r, b.r)];
          const dpr = r.dpr;
          const pts = [
            [qa, ra],
            [qb, ra],
            [qb, rb],
            [qa, rb],
          ]
            .map(([q, rr]) => r.camera.toScreen(hexX(q, rr), hexY(rr)))
            .map(([x, y]) => `${(x / dpr).toFixed(1)},${(y / dpr).toFixed(1)}`);
          this.rowPreview.innerHTML = `<polygon class="route area" points="${pts.join(" ")}"/>`;
          if (!this.rowPreview.isConnected) this.overlay.append(this.rowPreview);
          return;
        }
        if (this.placing && drag.button === 0) {
          // Placing: the row (or with Shift or Alt, the grid) a drag lays
          // out, as ghost rings.
          const r = this.renderer;
          const a = { q: drag.start[0], r: drag.start[1] };
          const b = r.pick(...this.local(e), this.playback.alpha);
          const hexes =
            this.placing.what === "track"
              ? [[a.q, a.r], [b.q, b.r]]
              : e.shiftKey || e.altKey
                ? this.gridHexes([a.q, a.r], [b.q, b.r], this.rowStep(this.placing.what))
                : this.lineHexes([a.q, a.r], [b.q, b.r], this.rowStep(this.placing.what));
          const dpr = r.dpr;
          this.rowPreview.innerHTML = hexes
            .map(([q, rr]) => {
              const [x, y] = r.camera.toScreen(hexX(q, rr), hexY(rr));
              const bad = !!this.placeProblem(this.placing!, q, rr);
              return `<circle class="row-ghost${bad ? " bad" : ""}" cx="${(x / dpr).toFixed(1)}" cy="${(y / dpr).toFixed(1)}" r="${Math.max(4, r.camera.zoom * 0.8).toFixed(1)}"/>`;
            })
            .join("");
          if (!this.rowPreview.isConnected) this.overlay.append(this.rowPreview);
          return;
        }
        if (drawing) {
          drag.wpath.push(toWorld(e));
          if (!line) {
            line = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
            line.setAttribute("class", this.armed === "fight" ? "order-line fight" : "order-line");
            this.overlay.append(line);
          }
          // From world positions each time: a zoom mid-drag keeps the line.
          const dpr = this.renderer.dpr;
          line.setAttribute("points", drag.wpath.map((w) => toScreen(w)).map(([x, y]) => `${x / dpr},${y / dpr}`).join(" "));
        } else if (drag.button === 0) {
          const [x0, x1] = [Math.min(drag.x, e.offsetX), Math.max(drag.x, e.offsetX)];
          const [y0, y1] = [Math.min(drag.y, e.offsetY), Math.max(drag.y, e.offsetY)];
          Object.assign(this.box.style, { left: `${x0}px`, top: `${y0}px`, width: `${x1 - x0}px`, height: `${y1 - y0}px` });
          this.box.classList.remove("hidden");
        } else if (drag.button === 1) {
          c.classList.add("dragging");
          const r = this.renderer;
          r.camera.panBy(e.movementX * r.dpr, e.movementY * r.dpr);
        }
      },
      { signal, capture: true },
    );
    c.addEventListener(
      "pointerup",
      (e) => {
        if (e.pointerType !== "mouse" || !drag) return;
        e.stopImmediatePropagation();
        // Released while another button is still down: the drag waits.
        if (e.buttons) return;
        const d = drag;
        drag = null;
        c.classList.remove("dragging");
        line?.remove();
        line = null;
        this.box.classList.add("hidden");
        if (this.pasted) {
          if (this.pasted.size) this.toast(`Pasted ${this.clipWords()} to ${count(this.pasted.size, this.clip?.kind === "barracks" ? "barracks" : this.clip?.kind === "post" ? "trade posts" : "plants")}`);
          this.pasted = null;
          this.syncCard();
        }
        // Cancelled by the right button: nothing more.
        if (d.cancelled) {
          this.rowPreview.remove();
          return;
        }
        // The drawn line, in screen pixels now (it was kept in the world).
        const dpath = d.wpath.map((w) => toScreen(w));
        const mode = d.mod.shiftKey ? "add" : d.mod.ctrlKey ? "remove" : "replace";
        // Shift queues; with Shift held, an armed command stays armed (BAR).
        const queue = e.shiftKey;
        this.rowPreview.remove();
        if (this.hexPicker && !d.moved && d.button === 0) {
          const pick = this.renderer.pick(...this.local(e), this.playback.alpha);
          const cb = this.hexPicker;
          this.pickHex(null);
          cb([pick.q, pick.r]);
        } else if (this.placing && d.moved && d.button === 0) {
          // A drag while placing: a row of them along it (Shift or Alt: a
          // grid over the box; it stays in hand after a Shift one).
          const b = this.renderer.pick(...this.local(e), this.playback.alpha);
          if ((e.shiftKey || e.altKey) && this.placing.what !== "track") this.openGrid(this.placing, [d.start, [b.q, b.r]]);
          else this.placeRow(d.start, [b.q, b.r], queue);
        } else if (this.placing && !d.moved && (d.button === 0 || d.button === 2)) {
          // Placing a structure: left places (Shift keeps placing), right cancels.
          if (d.button === 0) this.placeAt(e, queue);
          else this.stopPlacing();
        } else if (d.button === 2 && this.factories.size && !this.selection.size) {
          this.rallyOrder(e);
        } else if (d.handle !== undefined) {
          // A corner let go: plan the new box.
          const p = plan();
          if (p) this.replan({ box: p.box });
        } else if (this.armed === "plan" && d.button === 0) {
          if (d.moved) {
            const b = this.renderer.pick(...this.local(e), this.playback.alpha);
            this.armed = null;
            this.replan({ box: [d.start, [b.q, b.r]] });
          } else this.toast("Plan mines: drag a box (a click is too small)");
        } else if (plan() && d.button === 2 && !d.moved) {
          // Right-click with a plan up: cancels it.
          this.cancelPlan();
        } else if (this.armed === "prospect" && d.button === 0) {
          if (d.moved) {
            const b = this.renderer.pick(...this.local(e), this.playback.alpha);
            this.prospectOrder(d.start, [b.q, b.r], queue);
          } else this.toast("Prospect area: drag a box (a click is too small)");
          if (!queue) {
            this.armed = null;
            document.body.classList.remove("arming-area");
          }
        } else if (this.armed === "ride" && (d.button === 0 || d.button === 2)) {
          this.rideOrder(e, queue);
          if (!queue) this.armed = null;
        } else if (this.armed === "patrol" && (d.button === 0 || d.button === 2)) {
          this.patrolOrder(e);
          if (!queue) this.armed = null;
        } else if (d.button === 0) {
          if (this.armed === "fight") {
            // F then click fights there; F then drag fights to a drawn line.
            if (d.moved && dpath.length > 2) this.lineOrder(dpath, true, queue, e.altKey);
            else this.moveOrder(e, true, queue);
            if (!queue) this.armed = null;
          } else if (d.moved) {
            const dpr = this.renderer.dpr;
            const [x0, x1] = [Math.min(d.x, e.offsetX) * dpr, Math.max(d.x, e.offsetX) * dpr];
            const [y0, y1] = [Math.min(d.y, e.offsetY) * dpr, Math.max(d.y, e.offsetY) * dpr];
            let ids = this.unitsIn(x0, y0, x1, y1);
            if (this.spaceHeld) {
              const s = this.playback.state;
              const now = performance.now();
              ids = ids.filter((u) => now - this.movedAt[u] > 3000 && s.target[u] === NO_ID);
            }
            if (this.zHeld) {
              const s = this.playback.state;
              const types = new Set([...this.selection].map((u) => s.design[u]));
              ids = ids.filter((u) => types.has(s.design[u]));
            }
            // No units in the box: your construction sites in it instead
            // (to cancel an area at once, Factorio's deconstruction drag).
            const inBox = ids.length ? [] : this.sitesIn(x0, y0, x1, y1);
            if (inBox.length) {
              this.setSelection([], mode === "add" ? "add" : "replace");
              this.setSiteSel(inBox, mode === "add" ? "add" : "replace");
              this.toast(`${inBox.length} construction site${inBox.length === 1 ? "" : "s"}: Delete cancels ${inBox.length === 1 ? "it" : "them"}`);
            } else this.setSelection(ids, mode);
            this.inspect(null);
          } else {
            const [sx, sy] = this.local(e);
            const pick = this.renderer.pick(sx, sy, this.playback.alpha);
            const own = pick.unit !== null && this.playback.state.faction[pick.unit] === this.faction;
            // A producer's hex selects the producer, unless one of your
            // units is right under the cursor (new units stand on it; the
            // unit clicked wins, as in BAR).
            const factory = own ? null : this.producerAt(pick.tile);
            const struct = pick.unit === null ? this.structAt(pick.tile) : null;
            if (factory !== null) {
              if (mode !== "add") this.setSelection([], "replace");
              this.setFactories([factory], mode === "add" ? "add" : "replace");
              if (mode === "add") this.noteShiftClick("barracks");
              this.inspect(pick);
            } else if (struct !== null) {
              // One of your structures: selected (Shift adds), and shown.
              if (mode !== "add") this.setSelection([], "replace");
              this.setStructs([struct], mode === "add" ? "add" : "replace");
              if (mode === "add") this.noteShiftClick(this.kindOf(struct));
              this.inspect(pick);
            } else if (pick.unit === null && this.siteAt(pick.q, pick.r)) {
              // A construction site: selected (Shift adds), and shown.
              const site = this.siteAt(pick.q, pick.r)!;
              if (mode !== "add") this.setSelection([], "replace");
              this.setSiteSel([site.at], mode === "add" ? "add" : "replace");
              this.inspect(pick);
            } else {
              this.setSelection(own ? [pick.unit!] : [], own ? mode : "replace");
              // Any unit (an enemy too) or hex shows in the Selection tab.
              this.inspect(pick);
            }
          }
        } else if (d.button === 2) {
          const fight = this.armed === "fight";
          if (d.moved && dpath.length > 2) this.lineOrder(dpath, fight, queue, e.altKey);
          else this.moveOrder(e, fight, queue);
          if (!queue) this.armed = null;
        }
      },
      { signal, capture: true },
    );
    c.addEventListener(
      "dblclick",
      (e) => {
        e.stopImmediatePropagation();
        // Every unit of the clicked one's type in view.
        const [sx, sy] = this.local(e);
        const pick = this.renderer.pick(sx, sy, this.playback.alpha);
        const s = this.playback.state;
        // A structure: every one of its kind on screen.
        if (pick.unit === null) {
          const factory = this.producerAt(pick.tile);
          const struct = this.structAt(pick.tile);
          if (factory !== null) {
            this.setSelection([], "replace");
            this.setFactories(this.factoriesAll(true), "replace");
          } else if (struct !== null) {
            this.setSelection([], "replace");
            this.setStructs(this.structsLike(struct, true), "replace");
          } else if (this.siteAt(pick.q, pick.r)) {
            this.setSelection([], "replace");
            this.setSiteSel(this.sitesLike(this.siteAt(pick.q, pick.r)!), "replace");
          }
          return;
        }
        if (s.faction[pick.unit] !== this.faction) return;
        const design = s.design[pick.unit];
        const cam = this.renderer.camera;
        const ids = this.unitsIn(0, 0, cam.width, cam.height).filter((id) => s.design[id] === design);
        this.setSelection(ids, e.shiftKey ? "add" : "replace");
      },
      { signal, capture: true },
    );
    c.addEventListener("contextmenu", (e) => e.preventDefault(), { signal });
    // Where the cursor is (the pipette picks what's under it).
    c.addEventListener("pointermove", (e) => (this.hoverPx = this.local(e)), { signal, passive: true });
    // A drag the browser took away (lost capture, a cancelled pointer):
    // forgotten, so the next press starts afresh.
    const forget = () => {
      if (!drag) return;
      drag = null;
      c.classList.remove("dragging");
      line?.remove();
      line = null;
      this.box.classList.add("hidden");
      this.rowPreview.remove();
    };
    c.addEventListener("pointercancel", forget, { signal });
    c.addEventListener("lostpointercapture", () => setTimeout(forget, 0), { signal });
    window.addEventListener(
      "keydown",
      (e) => {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement) return;
        // A panel with the keyboard (the recipe picker) keeps its keys.
        if ((e.target as Element | null)?.closest?.(".recipe-picker")) return;
        // Not while a menu or the atlas is up.
        if (!$("loader").classList.contains("hidden") || gameMenuOpen() || document.body.classList.contains("in-atlas")) return;
        const code = e.code;
        const digit = code.startsWith("Digit") ? Number(code.slice(5)) : -1;
        let handled = true;
        if (digit >= 0 && e.ctrlKey && !e.shiftKey) {
          this.groups.set(digit, [...this.selection]);
          this.toast(`Group ${digit}: ${count(this.selection.size, "units")}`);
        } else if (digit >= 0 && e.ctrlKey && e.shiftKey) {
          this.groups.set(digit, [...new Set([...(this.groups.get(digit) ?? []), ...this.selection])]);
        } else if (digit >= 0) {
          const ids = (this.groups.get(digit) ?? []).filter((id) => this.playback.state.alive[id]);
          this.setSelection(ids, e.shiftKey ? "add" : "replace");
          const now = performance.now();
          if (this.lastGroup.n === digit && now - this.lastGroup.t < 400 && ids.length) this.focus(ids);
          this.lastGroup = { n: digit, t: now };
        } else if (code.startsWith("Arrow") && !e.ctrlKey) {
          this.arrows.add(code);
        } else if (/^F[1-4]$/.test(code)) {
          const cam = this.renderer.camera;
          const at = this.anchors.get(code);
          if (e.ctrlKey) {
            this.anchors.set(code, [cam.x, cam.y, cam.zoom]);
            this.toast(`Camera ${code} set`);
          } else if (at) cam.flyTo(at[0], at[1], at[2]);
        } else if (this.factories.size && keys.is("copy", e)) {
          this.copyFactory();
        } else if (this.factories.size && keys.is("paste", e)) {
          this.pasteFactory();
        } else if (!this.factories.size && this.engineers().length && !this.buildPage && keys.is("buildMenu", e)) {
          this.showBuildPage(true);
        } else if (!this.factories.size && this.engineers().length && this.buildPage && CommandControls.BUILD_SLOTS.some((id) => keys.is(id, e))) {
          const p = STRUCTURES[CommandControls.BUILD_SLOTS.findIndex((id) => keys.is(id, e))];
          if (p) this.place(p[1]);
        } else if (this.factories.size && CommandControls.BUILD_SLOTS.some((id) => keys.is(id, e))) {
          const d = this.buildable()[CommandControls.BUILD_SLOTS.findIndex((id) => keys.is(id, e))];
          if (d !== undefined) this.produce(d);
        } else if (keys.is("idleEngineer", e)) {
          this.nextIdle();
        } else if ((keys.is("cancelBuild", e) || code === "Backspace") && this.siteSel.size) {
          this.cancelSites([...this.siteSel].map((k) => k.split(",").map(Number) as [number, number]));
        } else if (keys.is("ride", e)) {
          this.arm("ride");
        } else if (keys.is("prospectArea", e) && this.prospecting()) {
          this.arm("prospect");
        } else if (keys.is("fight", e)) {
          this.arm("fight");
        } else if (keys.is("patrol", e)) {
          this.arm("patrol");
        } else if (keys.is("repeat", e)) {
          this.toggleRepeat();
        } else if (keys.is("sameType", e) && !this.selection.size && !this.structs.size && !this.factories.size && this.pipette()) {
          // Nothing selected, over a structure of yours: Factorio's pipette.
        } else if (keys.is("sameType", e) && !this.selection.size && (this.structs.size || this.factories.size)) {
          if (this.structs.size) this.setStructs(this.structsLike([...this.structs][0], true), "replace");
          else this.setFactories(this.factoriesAll(true), "replace");
        } else if (keys.is("sameTypeAll", e) && !this.selection.size && (this.structs.size || this.factories.size)) {
          if (this.structs.size) this.setStructs(this.structsLike([...this.structs][0], false), "replace");
          else this.setFactories(this.factoriesAll(false), "replace");
        } else if (keys.is("sameType", e)) {
          this.setSelection(this.sameTypes(true), "replace");
        } else if (keys.is("sameTypeAll", e)) {
          this.setSelection(this.sameTypes(false), "replace");
        } else if (keys.is("holdTypes", e)) {
          this.zHeld = true;
        } else if (keys.is("holdIdle", e)) {
          this.spaceHeld = true;
        } else if (keys.is("stop", e)) {
          this.order({ order: "stop" }, "Stop");
        } else if (keys.is("fireState", e)) {
          this.cycleFire();
        } else if (keys.is("moveState", e)) {
          this.cycleMoves();
        } else if (keys.is("policy", e)) {
          this.togglePolicy();
        } else if (keys.is("allUnits", e)) {
          this.selectAll();
        } else if (keys.is("research", e)) {
          e.preventDefault();
          toggleScreen("research");
        } else if (keys.is("designs", e)) {
          e.preventDefault();
          toggleScreen("designs");
        } else if (keys.is("market", e)) {
          e.preventDefault();
          toggleScreen("market");
        } else if (keys.is("production", e)) {
          e.preventDefault();
          toggleScreen("production");
        } else if (keys.is("diplomacy", e)) {
          e.preventDefault();
          toggleScreen("diplomacy");
        } else if (code === "Escape" && (plan() || this.armed === "plan")) {
          this.cancelPlan();
        } else if (code === "Escape" && this.placing) {
          this.stopPlacing();
        } else if (code === "Escape" && this.buildPage) {
          this.showBuildPage(false);
        } else if (code === "Escape" && (this.armed || this.selection.size || this.factories.size || this.structs.size || this.siteSel.size)) {
          this.structs.clear();
          this.siteSel.clear();
          // Esc clears first; with nothing to clear it opens the game menu.
          this.armed = null;
          this.setSelection([], "replace");
          this.inspect(null);
        } else {
          handled = false;
        }
        if (handled) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      { signal, capture: true },
    );
    window.addEventListener(
      "keyup",
      (e) => {
        if (keys.released("holdTypes", e)) this.zHeld = false;
        if (keys.released("holdIdle", e)) this.spaceHeld = false;
        this.arrows.delete(e.code);
      },
      { signal },
    );
    window.addEventListener(
      "blur",
      () => {
        this.zHeld = false;
        this.spaceHeld = false;
        this.arrows.clear();
      },
      { signal },
    );
  }

  /** Draws the routes of the selected units' formations (each frame): from
   *  where they stand through each order still ahead, green for moves and
   *  red for fights, drawn lines as lines. A leg counts as done once the
   *  selected members' centre is within its reach (plus two hexes). */
  update(): void {
    const s = this.playback.state;
    const r = this.renderer;
    this.followBuildQueue();
    this.findIdle();
    // Arrow keys pan, about a screen a second.
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    if (this.arrows.size) {
      const speed = Math.max(r.camera.width, r.camera.height) * dt;
      const dx = (this.arrows.has("ArrowLeft") ? 1 : 0) - (this.arrows.has("ArrowRight") ? 1 : 0);
      const dy = (this.arrows.has("ArrowUp") ? 1 : 0) - (this.arrows.has("ArrowDown") ? 1 : 0);
      r.camera.panBy(dx * speed, dy * speed);
    }
    // The overlay again only when something it shows can have changed: the
    // view, a new frame, the player's hand on mouse or keys, or a tenth of
    // a second gone (bars filling, the server's news). Rebuilding it every
    // display frame was a fifth of the frame.
    const cam = r.camera;
    const view = `${cam.x}|${cam.y}|${cam.zoom}|${cam.width}|${cam.height}|${s.frame}`;
    if (view === this.drawnView && !this.overlayDirty && now - this.drawnAt < 100) return;
    this.drawnView = view;
    this.overlayDirty = false;
    this.drawnAt = now;
    const sums = new Map<number, [number, number, number]>();
    for (const u of this.selection) {
      if (!s.alive[u]) continue;
      const f = this.formationOf.get(u);
      if (f === undefined || !this.legs.has(f)) continue;
      const [x, y] = r.unitPos(u, this.playback.alpha);
      const a = sums.get(f) ?? [0, 0, 0];
      a[0] += x;
      a[1] += y;
      a[2]++;
      sums.set(f, a);
    }
    const dpr = r.dpr;
    const screen = (x: number, y: number) => {
      const [sx, sy] = r.camera.toScreen(x, y);
      return `${(sx / dpr).toFixed(1)},${(sy / dpr).toFixed(1)}`;
    };
    const world = ([q, rr]: [number, number]): [number, number] => [hexX(q, rr), hexY(rr)];
    const parts: string[] = [];
    for (const [f, [sx, sy, n]] of sums) {
      const legs = this.legs.get(f)!;
      const at: [number, number] = [sx / n, sy / n];
      // There: by a leg's point (a ride's only at its destination).
      // An area stays marked while it stands (they work it, then wait in it).
      const near = (leg: Leg) => !leg.area && (leg.ride ? leg.pts.slice(1) : leg.pts).some((p) => {
        const [x, y] = world(p);
        return Math.hypot(x - at[0], y - at[1]) <= (leg.reach + 2) * SQRT3;
      });
      if (!this.repeating.has(f)) {
        while (legs.length > 1 && near(legs[0])) legs.shift();
        if (legs.length === 1 && near(legs[0])) continue;
      }
      let from = at;
      for (const leg of legs) {
        if (leg.ride) {
          // Walking to the boarding station, then along the rail.
          const [a, b] = leg.pts;
          const rail = this.railPath(a[1] * s.width + a[0], b[1] * s.width + b[0]);
          const way = rail ? rail.filter((_, i) => i % 3 === 0 || i === rail.length - 1).map((t) => screen(hexX(s.q(t), s.r(t)), hexY(s.r(t)))) : [screen(...world(a)), screen(...world(b))];
          parts.push(`<polyline class="route ride" points="${screen(...from)} ${way.join(" ")}"/>`);
          const [px, py] = screen(...world(b)).split(",");
          parts.push(`<circle class="route ride" cx="${px}" cy="${py}" r="3"/>`);
          from = world(b);
          continue;
        }
        if (leg.area) {
          const [[q0, r0], [q1, r1]] = leg.area;
          const [qa, qb, ra, rb] = [Math.min(q0, q1), Math.max(q0, q1), Math.min(r0, r1), Math.max(r0, r1)];
          // Cells already sampled inside it, faintly filled.
          const { cols, cell } = this.intelGrid;
          if (cols) {
            for (const f of this.findings) {
              if (!(f.ores.length || f.grid || f.grid4)) continue;
              const [cq, cr] = [(f.cell % cols) * cell, Math.floor(f.cell / cols) * cell];
              if (cq + cell <= qa || cq > qb || cr + cell <= ra || cr > rb) continue;
              const [x0, x1, y0, y1] = [Math.max(cq, qa), Math.min(cq + cell - 1, qb), Math.max(cr, ra), Math.min(cr + cell - 1, rb)];
              const corners = [world([x0, y0]), world([x1, y0]), world([x1, y1]), world([x0, y1])].map((c) => screen(...c));
              parts.push(`<polygon class="area-covered" points="${corners.join(" ")}"/>`);
            }
          }
          const box = [world([qa, ra]), world([qb, ra]), world([qb, rb]), world([qa, rb])].map((c) => screen(...c));
          parts.push(`<polygon class="route area" points="${box.join(" ")}"/>`);
          const mid = world(leg.pts[0]);
          parts.push(`<polyline class="route" points="${screen(...from)} ${screen(...mid)}"/>`);
          from = mid;
          continue;
        }
        const mid = world(leg.pts[Math.floor(leg.pts.length / 2)]);
        const cls = leg.fight ? "route fight" : "route";
        parts.push(`<polyline class="${cls}" points="${screen(...from)} ${screen(...mid)}"/>`);
        if (leg.pts.length > 1) {
          const ends = [leg.pts[0], leg.pts[leg.pts.length - 1]].map((p) => screen(...world(p)));
          parts.push(`<polyline class="${cls} held" points="${ends.join(" ")}"/>`);
        } else {
          const [px, py] = screen(...mid).split(",");
          parts.push(`<circle class="${cls}" cx="${px}" cy="${py}" r="3"/>`);
        }
        from = mid;
      }
      if (this.repeating.has(f) && legs.length > 1) {
        const first = world(legs[0].pts[Math.floor(legs[0].pts.length / 2)]);
        parts.push(`<polyline class="route repeat" points="${screen(...from)} ${screen(...first)}"/>`);
      }
    }
    if (now - this.infoAt > 250) {
      this.infoAt = now;
      this.renderInfo();
      this.syncCard();
    }
    // The mine plan: its box with corner handles, each mine's reach, and
    // the track joining them.
    const mp = plan();
    if (mp) {
      const corners = this.planCorners().map(([x, y]) => [x / dpr, y / dpr]);
      parts.push(`<polygon class="route area" points="${corners.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ")}"/>`);
      for (const [x, y] of corners) parts.push(`<circle class="plan-handle" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6"/>`);
      if (mp.rail) {
        for (const [a, b] of mp.track) {
          const [ax, ay] = screen(hexX(a[0], a[1]), hexY(a[1])).split(",");
          const [bx, by] = screen(hexX(b[0], b[1]), hexY(b[1])).split(",");
          parts.push(`<line class="place-track" x1="${ax}" y1="${ay}" x2="${bx}" y2="${by}"/>`);
        }
      }
      for (const [q, r] of mp.unfit) {
        const [cx, cy] = screen(hexX(q, r), hexY(r)).split(",");
        parts.push(`<circle class="row-ghost bad" cx="${cx}" cy="${cy}" r="5"/>`);
      }
      for (const [q, r] of mp.mines) {
        if (mp.mode === "grid" && this.planWhat.what !== "mine") {
          const [cx, cy] = screen(hexX(q, r), hexY(r)).split(",");
          parts.push(`<circle class="row-ghost" cx="${cx}" cy="${cy}" r="5"/>`);
          continue;
        }
        const pts = [0, 60, 120, 180, 240, 300].map((a) => {
          const rad = (a * Math.PI) / 180;
          return screen(hexX(q, r) + Math.cos(rad) * MINE_REACH * SQRT3, hexY(r) + Math.sin(rad) * MINE_REACH * SQRT3);
        });
        parts.push(`<polygon class="plan-mine" points="${pts.join(" ")}"/>`);
        const [cx, cy] = screen(hexX(q, r), hexY(r)).split(",");
        parts.push(`<circle class="row-ghost" cx="${cx}" cy="${cy}" r="5"/>`);
      }
    }
    // Selected construction sites: a ring on each.
    for (const k of this.siteSel) {
      const [q, r] = k.split(",").map(Number);
      const [px, py] = screen(hexX(q, r), hexY(r)).split(",");
      parts.push(`<circle class="factory-ring" cx="${px}" cy="${py}" r="12"/>`);
    }
    // Selected structures: a ring on each.
    for (const k of this.structs) {
      const d = s.depots[k];
      if (!d) continue;
      const [px, py] = screen(hexX(s.q(d.tile), s.r(d.tile)), hexY(s.r(d.tile))).split(",");
      parts.push(`<circle class="factory-ring" cx="${px}" cy="${py}" r="12"/>`);
    }
    // Selected producers: a ring on each.
    const list = s.statics.producers;
    for (const k of this.factories) {
      const p = list[k];
      if (!p) continue;
      const [px, py] = screen(hexX(p.tile % s.width, Math.floor(p.tile / s.width)), hexY(Math.floor(p.tile / s.width))).split(",");
      parts.push(`<circle class="factory-ring" cx="${px}" cy="${py}" r="12"/>`);
      const rally = this.rallies.get(k);
      if (rally) {
        const [rx, ry] = screen(hexX(rally[0], rally[1]), hexY(rally[1])).split(",");
        parts.push(`<polyline class="route rally" points="${px},${py} ${rx},${ry}"/><circle class="factory-ring" cx="${rx}" cy="${ry}" r="4"/>`);
      }
    }
    // A reason something of yours isn't working: a word under it on the
    // map, the reason in full on hover.
    // Reasons are gathered, then drawn merged: the same reason close by on
    // screen (a block of sites with no steel) once, with how many.
    const reasons: { x: number; y: number; word: string; detail: string }[] = [];
    const why = (x: number, y: number, word: string, detail: string) => {
      reasons.push({ x, y, word, detail });
      return "";
    };
    // Construction sites (drawn as ghosts by the renderer): under each, a
    // bar of how far along it is and the percentage.
    // Only what's on screen; queued ones are faint ghosts already (the
    // Build tab lists them); zoomed out, bars without their numbers.
    const [vw, vh] = [r.camera.width / dpr, r.camera.height / dpr];
    const readable = r.camera.zoom >= 2.5;
    for (const site of this.sites) {
      if (site.queued) continue;
      const [px, py] = screen(hexX(site.at[0], site.at[1]), hexY(site.at[1])).split(",").map(Number);
      if (px < -60 || py < -60 || px > vw + 60 || py > vh + 60) continue;
      // A track's two ends, each marked (each end is built on its own).
      if (site.what === "track" && site.to) {
        const [tx, ty] = screen(hexX(site.to[0], site.to[1]), hexY(site.to[1])).split(",").map(Number);
        parts.push(`<circle class="track-end" cx="${px}" cy="${py}" r="4"/><circle class="track-end" cx="${tx}" cy="${ty}" r="4"/>`);
      }
      const share = Math.min(1, siteDone(site) / Math.max(1, site.need));
      // A ring round the ghost, filling clockwise from the top as it's
      // built (Factorio's), sized to the glyph.
      const rad = Math.max(Math.min(16, Math.max(5, r.camera.zoom * 5)), 2.25 * r.camera.zoom) / 2 + 3;
      const circ = 2 * Math.PI * rad;
      const top = py + rad - 2;
      // Blocked: amber, and why, instead of the percentage.
      const blocked = site.blocked ? BLOCKED[site.blocked]?.[0] : undefined;
      parts.push(
        `<circle class="site-ring" cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="${rad.toFixed(1)}"/>` +
          `<circle class="site-ring-fill${blocked ? " blocked" : ""}" cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="${rad.toFixed(1)}" stroke-dasharray="${(circ * share).toFixed(1)} ${circ.toFixed(1)}" transform="rotate(-90 ${px.toFixed(1)} ${py.toFixed(1)})"/>` +
          (!readable
            ? ""
            : blocked
              ? why(px, top + 16, blocked, BLOCKED[site.blocked!]?.[1] ?? "")
              : `<text class="site-pct" x="${px.toFixed(1)}" y="${(top + 16).toFixed(1)}">${Math.floor(share * 100)}%</text>`),
      );
    }
    // Your idle mines and plants: why, under each (only what needs you).
    const sites = this.playback.header.sites ?? [];
    const labelled = new Set<string>();
    // Sites by tile (once, not a search per site), and whether a tile is on
    // screen (off it, nothing is worked out).
    const byTile = new Map<number, typeof sites>();
    for (const site of sites) byTile.set(site.tile, [...(byTile.get(site.tile) ?? []), site]);
    const tileOnScreen = (t: number) => {
      const [x, y] = screen(hexX(t % s.width, Math.floor(t / s.width)), hexY(Math.floor(t / s.width))).split(",").map(Number);
      return x > -80 && y > -80 && x < vw + 80 && y < vh + 80;
    };
    sites.forEach((site, i) => {
      if (site.faction !== this.faction || !tileOnScreen(site.tile)) return;
      // Where the renderer draws it: a mine's or plant's own glyph (idle
      // only when all its sites are), else round its station's tile. One
      // reason a glyph.
      const kind = s.depots[site.station]?.kind;
      const own = kind === DepotKind.Mine || kind === DepotKind.Plant;
      const now = own ? industry.stationIdle(site.station, s) : industry.status(i, s);
      // One being taken down has its countdown instead.
      if (!now?.why || s.depots[site.station]?.faction === 255) return;
      // A full station: behind its layer (chronic, not a call to act).
      if (now.why === "Station full" && !this.renderer.options.fullWarnings) return;
      const same = byTile.get(site.tile) ?? [site];
      const angle = (same.indexOf(site) / Math.max(1, same.length)) * Math.PI * 2 - Math.PI / 2;
      const [q, r] = [site.tile % s.width, Math.floor(site.tile / s.width)];
      const off = own ? 0 : 1.25;
      const [px, py] = screen(hexX(q, r) + Math.cos(angle) * off, hexY(r) + Math.sin(angle) * off).split(",").map(Number);
      const key = own ? `s${site.station}` : `i${i}`;
      if (labelled.has(key)) return;
      labelled.add(key);
      parts.push(why(px, py + 22, now.why, now.detail ?? ""));
    });
    // The storm stirring beyond the wall: a faint swirl of storm grey that
    // comes and goes over a few seconds. One means nothing; a place that
    // stirs again and again is worth a look (learned by noticing: no label,
    // no tip, nothing on the minimap).
    if (this.stirrings.length) {
      const now = performance.now();
      this.stirrings = this.stirrings.filter((x) => now < x.start + x.dur);
      const reg = s.statics.region;
      const [ux0] = r.camera.toScreen(0, 0);
      const [ux1] = r.camera.toScreen(1, 0);
      const unit = (ux1 - ux0) / dpr;
      for (const x of this.stirrings) {
        if (now < x.start) continue;
        // Only out past the wall (never over ground that's yours to hold).
        const t = x.r * s.width + x.q;
        if (!reg || reg[t] === REGION.INSIDE || reg[t] === undefined) continue;
        const k = (now - x.start) / x.dur;
        const a = Math.sin(Math.PI * k) * 0.22;
        const [px, py] = screen(hexX(x.q, x.r), hexY(x.r)).split(",").map(Number);
        const rad = Math.max(6, unit * SQRT3 * (1.2 + 1.3 * k));
        if (px < -rad || py < -rad || px > vw + rad || py > vh + rad) continue;
        const turn = x.turn + k * 140;
        parts.push(
          `<g class="storm-stir" opacity="${a.toFixed(3)}" transform="rotate(${turn.toFixed(1)} ${px.toFixed(1)} ${py.toFixed(1)})">` +
            `<path d="M ${(px + rad).toFixed(1)} ${py.toFixed(1)} A ${rad.toFixed(1)} ${rad.toFixed(1)} 0 0 1 ${(px - rad * 0.6).toFixed(1)} ${(py + rad * 0.8).toFixed(1)}"/>` +
            `<path d="M ${(px - rad * 0.7).toFixed(1)} ${(py - rad * 0.4).toFixed(1)} A ${(rad * 0.7).toFixed(1)} ${(rad * 0.7).toFixed(1)} 0 0 1 ${(px + rad * 0.3).toFixed(1)} ${(py - rad * 0.6).toFixed(1)}"/>` +
            `</g>`,
        );
      }
      // Still fading in or out: drawn again soon.
      if (this.stirrings.some((x) => now >= x.start)) this.overlayDirty = true;
    }
    // Placing a training camp (v31): its reach round the cursor, a ring of
    // hexes 12 out, as BAR shows a building's range while placing it.
    if (this.placing?.what === "training_camp" && this.hoverPx) {
      const h = r.pick(...this.hoverPx, this.playback.alpha);
      const ring = [
        [CAMP_REACH, 0],
        [0, CAMP_REACH],
        [-CAMP_REACH, CAMP_REACH],
        [-CAMP_REACH, 0],
        [0, -CAMP_REACH],
        [CAMP_REACH, -CAMP_REACH],
      ].map(([dq, dr]) => screen(hexX(h.q + dq, h.r + dr), hexY(h.r + dr)));
      parts.push(`<polygon class="camp-reach" points="${ring.join(" ")}"/>`);
      // And the reach your camps have already.
      for (const [q, rr] of ownCamps()) {
        const pts = [
          [CAMP_REACH, 0],
          [0, CAMP_REACH],
          [-CAMP_REACH, CAMP_REACH],
          [-CAMP_REACH, 0],
          [0, -CAMP_REACH],
          [CAMP_REACH, -CAMP_REACH],
        ].map(([dq, dr]) => screen(hexX(q + dq, rr + dr), hexY(rr + dr)));
        parts.push(`<polygon class="camp-reach own" points="${pts.join(" ")}"/>`);
      }
    }
    // Your camps and comms posts (no depot of their own, so drawn here): a
    // mark each, in view.
    {
      const mark = (q: number, rr: number, kind: "camp" | "post", ax?: number, ay?: number, owner = this.faction) => {
        const [px, py] = ax !== undefined && ay !== undefined ? [ax, ay] : screen(hexX(q, rr), hexY(rr)).split(",").map(Number);
        const c = this.renderer.factionColour(owner);
        if (px < -40 || py < -40 || px > vw + 40 || py > vh + 40) return;
        const k = Math.max(10, Math.min(28, r.camera.zoom * 1.6));
        const whose = owner === this.faction ? "" : ` (${sideName(owner)})`;
        const tip = kind === "camp" ? `Training camp${whose}: trains designs at barracks within 12 hexes` : `Comms post${whose}: +20 command points`;
        const glyph =
          kind === "camp"
            ? `<path d="M4 20 L12 5 L20 20 Z" fill="none" stroke="#e8dfcc" stroke-width="2"/><path d="M12 5 L12 20" stroke="#e8dfcc" stroke-width="1.5"/>`
            : `<path d="M12 4 L12 20 M7 20 L17 20" stroke="#e8dfcc" stroke-width="2"/><path d="M7 7 A7 7 0 0 1 17 7 M9 10 A4 4 0 0 1 15 10" fill="none" stroke="#e8dfcc" stroke-width="1.6"/>`;
        parts.push(`<g class="own-mark why" data-tip="${tip}"><rect style="stroke:${c}" x="${(px - k / 2 - 2).toFixed(1)}" y="${(py - k / 2 - 2).toFixed(1)}" width="${(k + 4).toFixed(1)}" height="${(k + 4).toFixed(1)}" rx="3"/><svg x="${(px - k / 2).toFixed(1)}" y="${(py - k / 2).toFixed(1)}" width="${k.toFixed(1)}" height="${k.toFixed(1)}" viewBox="0 0 24 24">${glyph}</svg></g>`);
      };
      const em = r.emplaced;
      if (em.length) {
        for (const e of em) {
          const [px, py] = screen(e.x, e.y).split(",").map(Number);
          const [qq, rq] = hexAt(e.x, e.y);
          mark(qq, rq, e.kind, px, py, e.faction);
        }
      }
      // Before v31.1 (no emplacement units): your own, from your state.
      if (!em.some((e) => e.faction === this.faction)) {
        for (const [q, rr] of ownCamps()) mark(q, rr, "camp");
        for (const [q, rr] of ownPosts()) mark(q, rr, "post");
      }
    }
    // Formations over command capacity (v31): a mark over each, as BAR
    // greys out what's beyond its command limit.
    const cmd = command();
    if (cmd?.uncommanded.size && cmd.at.length) {
      for (const [q, rr] of cmd.at) {
        const [px, py] = screen(hexX(q, rr), hexY(rr)).split(",").map(Number);
        if (px < -60 || py < -60 || px > vw + 60 || py > vh + 60) continue;
        parts.push(why(px, py - 18, "No command", "Over command capacity: this formation holds and takes no orders. Build a comms post (20 more points)."));
      }
    } else if (cmd?.uncommanded.size) {
      const sums = new Map<number, [number, number, number]>();
      for (const [u, f] of this.formationOf) {
        if (!cmd.uncommanded.has(f) || !s.alive[u]) continue;
        const [x, y] = r.unitPos(u, this.playback.alpha);
        const a = sums.get(f) ?? [0, 0, 0];
        a[0] += x;
        a[1] += y;
        a[2]++;
        sums.set(f, a);
      }
      for (const [x, y, n] of sums.values()) {
        const [px, py] = screen(x / n, y / n).split(",").map(Number);
        parts.push(why(px, py - 18, "No command", "Over command capacity: this formation holds and takes no orders. Build a comms post (20 more points)."));
      }
    }
    // What each of your plants makes, its output's icon on its glyph (as
    // Factorio's alt mode shows an assembler's recipe): a row of plants
    // reads at a glance.
    // A small dark square on the glyph's upper right, about half the
    // glyph (clear of its stock bar; a dense grid keeps its glyphs
    // readable); none below 11 px.
    const [ux0] = r.camera.toScreen(0, 0);
    const [ux1] = r.camera.toScreen(1, 0);
    const unit = (ux1 - ux0) / dpr;
    const size = Math.min(26, unit * 1.0);
    if (size >= 11) {
      sites.forEach((site, i) => {
        if (site.kind !== "plant" || site.faction !== this.faction || !tileOnScreen(site.tile)) return;
        const r = recipeByName(this.switched.get(i) ?? site.name);
        if (!r) return;
        const kind = s.depots[site.station]?.kind;
        const own = kind === DepotKind.Plant;
        const same = byTile.get(site.tile) ?? [site];
        const angle = (same.indexOf(site) / Math.max(1, same.length)) * Math.PI * 2 - Math.PI / 2;
        const [q, rr] = [site.tile % s.width, Math.floor(site.tile / s.width)];
        const off = own ? 0 : 1.25;
        const [px, py] = screen(hexX(q, rr) + Math.cos(angle) * off, hexY(rr) + Math.sin(angle) * off).split(",").map(Number);
        const x = px + unit * 0.3;
        const y = py - unit * 1.25;
        const pad = Math.max(1.5, size * 0.1);
        parts.push(
          `<g class="plant-badge"><rect x="${(x - pad).toFixed(1)}" y="${(y - pad).toFixed(1)}" width="${(size + 2 * pad).toFixed(1)}" height="${(size + 2 * pad).toFixed(1)}" rx="${(pad * 1.5).toFixed(1)}"/><svg x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${size.toFixed(1)}" height="${size.toFixed(1)}" viewBox="0 0 24 24">${goodShape(recipeIconGood(r))}</svg></g>`,
        );
        // Not linked by rail to what it needs: a red broken rail on the
        // glyph's other corner (Factorio's disconnected-wire mark).
        if (industry.status(i, s)?.link) {
          const ux = px - unit * 0.3 - size;
          parts.push(
            `<g class="plant-badge unlinked"><rect x="${(ux - pad).toFixed(1)}" y="${(y - pad).toFixed(1)}" width="${(size + 2 * pad).toFixed(1)}" height="${(size + 2 * pad).toFixed(1)}" rx="${(pad * 1.5).toFixed(1)}"/>${UNLINKED_SVG(size).replace("<svg ", `<svg x="${ux.toFixed(1)}" y="${y.toFixed(1)}" `)}</g>`,
          );
        }
      });
    }
    // What your producing sites made in the last minute, under each (a
    // readable zoom): every good a site makes, side by side.
    if (this.renderer.camera.zoom >= 4) {
      const made = new Map<string, { x: number; y: number; text: string[] }>();
      sites.forEach((site, i) => {
        if (site.faction !== this.faction || s.depots[site.station]?.faction === 255 || !tileOnScreen(site.tile)) return;
        const now = industry.status(i, s);
        if (!now || now.why || !now.rate || now.rate < 0.5) return;
        const kind = s.depots[site.station]?.kind;
        const own = kind === DepotKind.Mine || kind === DepotKind.Plant;
        const same = byTile.get(site.tile) ?? [site];
        const angle = (same.indexOf(site) / Math.max(1, same.length)) * Math.PI * 2 - Math.PI / 2;
        const [q, r] = [site.tile % s.width, Math.floor(site.tile / s.width)];
        const off = own ? 0 : 1.25;
        const [px, py] = screen(hexX(q, r) + Math.cos(angle) * off, hexY(r) + Math.sin(angle) * off).split(",").map(Number);
        const key = own ? `s${site.station}` : `i${i}`;
        const m = made.get(key) ?? { x: px, y: py, text: [] };
        m.text.push(`+${Math.round(now.rate)} ${now.makes}`);
        made.set(key, m);
      });
      for (const p of s.statics.producers) {
        if (p.faction !== this.faction) continue;
        const [q, r] = [p.tile % s.width, Math.floor(p.tile / s.width)];
        const rate = industry.barracksRate(q, r);
        if (!rate || rate.n <= 0) continue;
        const name = typeof rate.design === "number" ? (this.playback.header.designs[rate.design]?.name ?? "units") : (rate.design ?? "units");
        const [px, py] = screen(hexX(q, r), hexY(r)).split(",").map(Number);
        made.set(`p${p.tile}`, { x: px, y: py, text: [`+${rate.n < 10 ? rate.n.toFixed(1) : Math.round(rate.n)} ${String(name).toLowerCase()}`] });
      }
      // Above the glyph (reasons and build bars sit below).
      // Clear of the idle markers over units standing at it.
      for (const m of made.values()) parts.push(`<text class="site-rate" x="${m.x.toFixed(1)}" y="${(m.y - 30).toFixed(1)}">${m.text.join(" · ")}/min</text>`);
    }
    // Your barracks that wait: for what.
    for (const p of s.statics.producers) {
      if (p.faction !== this.faction) continue;
      const [q, r] = [p.tile % s.width, Math.floor(p.tile / s.width)];
      const waits = industry.barracksWhy(q, r);
      if (!waits) continue;
      const [px, py] = screen(hexX(q, r), hexY(r)).split(",").map(Number);
      parts.push(why(px, py + 22, ...waits));
    }
    // Your titans standing unpaid (no fuel or batteries this minute).
    for (const u of s.live) {
      if (s.faction[u] !== this.faction || !isUnpowered(s.design[u])) continue;
      const [ux, uy] = this.renderer.unitPos(u, this.playback.alpha);
      const [px, py] = screen(ux, uy).split(",").map(Number);
      if (px < -60 || py < -60 || px > vw + 60 || py > vh + 60) continue;
      const power = powerOf(s.design[u]);
      why(px, py + 30, "Unpowered", `Its upkeep wasn't paid this minute (${power === "Electric" ? "batteries" : "fuel"}): it stands and holds fire until the next payment.`);
    }
    // Merge by reason within about a label's size on screen.
    // Greedy: each joins the first label of its reason near enough to
    // overlap it (a label is about 130 px wide), else starts its own.
    const merged: { x: number; y: number; word: string; detail: string; n: number }[] = [];
    for (const m of reasons) {
      const at = merged.find((x) => x.word === m.word && Math.abs(x.x - m.x) < 140 && Math.abs(x.y - m.y) < 60);
      if (at) at.n++;
      else merged.push({ ...m, n: 1 });
    }
    for (const m of merged) {
      const tip = m.n > 1 ? `${m.detail} (${m.n} here)` : m.detail;
      parts.push(
        `<text class="site-pct blocked why" x="${m.x.toFixed(1)}" y="${m.y.toFixed(1)}" data-tip="${tip.replace(/"/g, "&quot;")}">⚠ ${m.word}${m.n > 1 ? ` ×${m.n}` : ""}</text>`,
      );
    }
    this.drawFlows(parts, screen);
    this.drawEngineers(parts, screen);
    // Last seen: enemy forces your side saw where it no longer sees, a
    // faint ring in their colour with how many and how long ago.
    const { cols, cell } = samplingGrid();
    if (cols && this.playback.header.fog !== undefined) {
      const factions = this.playback.header.factions;
      for (const m of seenCells()) {
        const [q, r] = [(m.cell % cols) * cell + cell / 2, Math.floor(m.cell / cols) * cell + cell / 2];
        const centre = Math.min(s.height - 1, Math.max(0, Math.floor(r))) * s.width + Math.min(s.width - 1, Math.max(0, Math.floor(q)));
        if (this.renderer.inSight(centre)) continue;
        const [px, py] = screen(hexX(q, r), hexY(r)).split(",").map(Number);
        const ago = minutes(Math.max(0, (s.tick - m.tick) / 10));
        m.forces
          .filter(([f, n]) => f !== this.faction && n > 0)
          .forEach(([f, n], k) => {
            const c = factions[f]?.color ?? "#ccc";
            const y = py + k * 18;
            // Shared by another side (a feed, an agreement): a dashed ring,
            // and whose eyes it was.
            const via = m.from !== undefined && m.from !== this.faction ? sideName(m.from) : "";
            parts.push(
              `<circle class="seen-ring${via ? " fed" : ""}" cx="${px}" cy="${y}" r="7" stroke="${c}"/>` +
                `<text class="seen-label why" x="${(px + 11).toFixed(1)}" y="${(y + 4).toFixed(1)}" fill="${c}" data-tip="Last seen here ${ago} ago: about ${n} of ${sideName(f)}${via ? `. Seen by ${via}, shared with you` : ""}">~${n} · ${ago} ago${via ? ` · via ${via}` : ""}</text>`,
            );
          });
      }
    }
    // Samples under way: a ring filling over the cell, and the time left
    // (their layer, or prospectors selected).
    if (cols && (this.renderer.options.samples || this.prospecting())) {
      for (const smp of sampling()) {
        const [q, r] = [(smp.cell % cols) * cell + cell / 2, Math.floor(smp.cell / cols) * cell + cell / 2];
        const [px, py] = screen(hexX(q, r), hexY(r)).split(",").map(Number);
        const c = 2 * Math.PI * 12;
        parts.push(
          `<circle class="sample-ring back" cx="${px}" cy="${py}" r="12"/>` +
            `<circle class="sample-ring" cx="${px}" cy="${py}" r="12" stroke-dasharray="${(c * smp.done).toFixed(1)} ${c.toFixed(1)}" transform="rotate(-90 ${px} ${py})"/>` +
            `<text class="site-pct" x="${px.toFixed(1)}" y="${(py + 26).toFixed(1)}">${smp.kind === "Soil" ? "Soil sample" : "Deep core"} ${minutes(smp.left)} left</text>`,
        );
      }
    }
    // Structures being taken down: a ring emptying as the time runs out,
    // the time and the refund; amber when an enemy near slows it.
    for (const d of this.decommissions) {
      const [px, py] = screen(hexX(d.at[0], d.at[1]), hexY(d.at[1])).split(",").map(Number);
      const secs = Math.max(0, d.ticks_left / 10 - (performance.now() - d.seen) / 1000);
      const share = Math.min(1, (secs * 10) / Math.max(1, d.full));
      const c = 2 * Math.PI * 14;
      parts.push(
        `<circle class="decom-ring" cx="${px}" cy="${py}" r="14" stroke-dasharray="${(c * share).toFixed(1)} ${c.toFixed(1)}" transform="rotate(-90 ${px} ${py})"/>` +
          (d.slowed
            ? `<text class="site-pct blocked why" x="${px.toFixed(1)}" y="${(py + 26).toFixed(1)}" data-tip="Enemy armed units are near: while they are, taking it down takes longer.">⚠ Contested: slower</text>`
            : `<text class="site-pct" x="${px.toFixed(1)}" y="${(py + 26).toFixed(1)}">−${Math.ceil(secs)} s · +${d.refund} steel</text>`),
      );
    }
    this.drawReports(parts, screen);
    this.drawFindings(parts, screen);
    // Placing: the hex it would take, green where it can go, red (and why)
    // where it can't; track, a dashed line from its first end.
    const hover = this.renderer.hover;
    if (this.placing && hover && hover.tile !== null) {
      const problem = this.placeProblem(this.placing, hover.q, hover.r);
      const [cx, cy] = [hexX(hover.q, hover.r), hexY(hover.r)];
      const pts = [30, 90, 150, 210, 270, 330].map((a) => screen(cx + Math.cos((a * Math.PI) / 180), cy + Math.sin((a * Math.PI) / 180)));
      parts.push(`<polygon class="place-hex${problem ? " bad" : ""}" points="${pts.join(" ")}"/>`);
      const [px, py] = screen(cx, cy).split(",").map(Number);
      if (problem) parts.push(`<text class="site-pct blocked" x="${px.toFixed(1)}" y="${(py - 16).toFixed(1)}">✕ ${problem}</text>`);
      if (this.placing.what === "track" && this.placing.from && !problem) {
        const [fx, fy] = screen(hexX(this.placing.from[0], this.placing.from[1]), hexY(this.placing.from[1])).split(",");
        parts.push(
          `<line class="place-track" x1="${fx}" y1="${fy}" x2="${px.toFixed(1)}" y2="${py.toFixed(1)}"/>` +
            `<text class="site-pct" x="${px.toFixed(1)}" y="${(py - 16).toFixed(1)}">track follows the ground</text>`,
        );
      }
    }
    if (this.placing?.what === "mine" && hover && hover.tile !== null) {
      // Your mines (built, or being built): the ground they already work
      // is dimmed; a new mine gains only the rest.
      const mines: [number, number][] = [
        ...s.depots.filter((d) => d.faction === this.faction && d.kind === DepotKind.Mine).map((d) => [s.q(d.tile), s.r(d.tile)] as [number, number]),
        ...this.sites.filter((x) => x.what === "mine").map((x) => x.at),
      ];
      const hexes: string[] = [];
      for (let dq = -MINE_REACH; dq <= MINE_REACH; dq++) {
        for (let dr = Math.max(-MINE_REACH, -dq - MINE_REACH); dr <= Math.min(MINE_REACH, -dq + MINE_REACH); dr++) {
          const [cx, cy] = [hexX(hover.q + dq, hover.r + dr), hexY(hover.r + dr)];
          const pts = [30, 90, 150, 210, 270, 330].map((a) => screen(cx + Math.cos((a * Math.PI) / 180), cy + Math.sin((a * Math.PI) / 180)));
          const taken = mines.some((m) => Math.max(Math.abs(m[0] - hover.q - dq), Math.abs(m[1] - hover.r - dr), Math.abs(m[0] - hover.q - dq + m[1] - hover.r - dr)) <= 2);
          hexes.push(`<polygon class="mine-reach${taken ? " taken" : ""}" points="${pts.join(" ")}"/>`);
        }
      }
      parts.push(hexes.join(""));
      const fresh = newGround(hover.q, hover.r, mines);
      // What that reach holds, as far as prospecting knows.
      const [lx, ly] = screen(hexX(hover.q, hover.r) + MINE_REACH * SQRT3 + 1.2, hexY(hover.r)).split(",").map(Number);
      const share = fresh < 19 ? `<tspan class="muted"> · ${Math.round((fresh * 100) / 19)}% new ground</tspan>` : "";
      parts.push(`<text class="mine-ores" x="${lx.toFixed(1)}" y="${(ly + 4).toFixed(1)}">${this.reachLabel(hover.q, hover.r)}${share}</text>`);
    }
    // A storm breaker: the ground it breaks open (everything within 24
    // hexes comes inside), and where the wall will run after (the storm
    // reforms just past it, wherever that isn't inside already).
    if (this.placing?.what === "breaker" && hover && hover.tile !== null) {
      const corners = [0, 60, 120, 180, 240, 300].map((a) => {
        const rad = (a * Math.PI) / 180;
        return screen(hexX(hover.q, hover.r) + Math.cos(rad) * BREACH * SQRT3, hexY(hover.r) + Math.sin(rad) * BREACH * SQRT3);
      });
      parts.push(`<polygon class="breach-disc" points="${corners.join(" ")}"/>`);
      const region = s.statics.region;
      if (region) {
        const wall: string[] = [];
        for (let dq = -BREACH - 3; dq <= BREACH + 3; dq++) {
          for (let dr = Math.max(-BREACH - 3, -dq - BREACH - 3); dr <= Math.min(BREACH + 3, -dq + BREACH + 3); dr++) {
            const dist = Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr));
            if (dist <= BREACH) continue;
            const [hq, hr] = [hover.q + dq, hover.r + dr];
            const inMap = hq >= 0 && hr >= 0 && hq < s.width && hr < s.height;
            if (inMap && region[hr * s.width + hq] === REGION.INSIDE) continue;
            const [cx, cy] = [hexX(hq, hr), hexY(hr)];
            wall.push(`<polygon points="${[30, 90, 150, 210, 270, 330].map((a) => screen(cx + Math.cos((a * Math.PI) / 180), cy + Math.sin((a * Math.PI) / 180))).join(" ")}"/>`);
          }
        }
        parts.push(`<g class="breach-wall">${wall.join("")}</g>`);
      }
      const [lx, ly] = screen(hexX(hover.q, hover.r), hexY(hover.r) - BREACH * 1.5 - 2).split(",").map(Number);
      // Out in the storm (or past it): it waits for a breach to reach it.
      const beyond = !!region && hover.tile !== null && region[hover.tile] !== REGION.INSIDE;
      parts.push(
        `<text class="site-pct" x="${lx.toFixed(1)}" y="${ly.toFixed(1)}">${beyond ? "Waits for the wall: opens when a breach reaches it" : "Breaks the storm 24 hexes round; it reforms beyond"}</text>`,
      );
    }
    // Track's first end, while choosing the second.
    if (this.placing?.from) {
      const [fx, fy] = screen(hexX(this.placing.from[0], this.placing.from[1]), hexY(this.placing.from[1])).split(",");
      parts.push(`<circle class="site-ring" cx="${fx}" cy="${fy}" r="5"/>`);
    }
    const html = parts.join("");
    // Against what was last written (reading innerHTML back serialises the
    // whole overlay).
    if (this.routesHtml !== html) {
      this.routesHtml = html;
      this.routes.innerHTML = html;
    }
  }
  private routesHtml = "";

  /** The selection by type, with health and ammunition (BAR's selection panel). */
  private renderInfo(): void {
    const s = this.playback.state;
    const designs = this.playback.header.designs;
    const ids = [...this.selection].filter((u) => s.alive[u]);
    // Several structures: what they are, make and hold together.
    if (!ids.length && this.siteSel.size > 1) {
      setSummary(this.siteSummary());
      return;
    }
    if (!ids.length && this.structs.size > 1) {
      setSummary(this.structureSummary());
      return;
    }
    if (!ids.length) {
      setSummary(null);
      return;
    }
    const byType = new Map<number, number>();
    let hp = 0;
    let hpMax = 0;
    let ammo = 0;
    let ammoMax = 0;
    let dry = 0;
    let out = 0;
    for (const u of ids) {
      const d = designs[s.design[u]];
      byType.set(s.design[u], (byType.get(s.design[u]) ?? 0) + 1);
      hp += s.hp[u];
      hpMax += d.hp;
      if (d.ammo > 0) {
        ammo += s.ammo[u];
        ammoMax += d.ammo;
        if (s.ammo[u] === 0) dry++;
      }
      if (s.flags[u] & (UnitFlags.ENGINE_OUT | UnitFlags.WEAPON_OUT)) out++;
    }
    const pct = (a: number, b: number) => (b ? `${Math.round((100 * a) / b)}%` : "–");
    // Over command capacity (v31): they hold and take no orders.
    const held = command()?.uncommanded;
    const noCommand = held?.size ? ids.filter((u) => held.has(this.formationOf.get(u) ?? -1)).length : 0;
    const extra = [dry ? `${dry} out of ammo` : "", out ? `${out} knocked out` : "", noCommand ? `${noCommand} over command capacity (holding; build a comms post)` : ""].filter(Boolean).join(" · ");
    // Selected engineers: what each is doing, a row each.
    const designsAll = this.playback.header.designs;
    const jobs = engineersReported()
      ? ids
          .filter((u) => designsAll[s.design[u]]?.modules?.some((m) => m.kind === "construction"))
          .slice(0, 12)
          .map((u) => ({ id: u, ...this.jobText(engineerAt(s.q(s.tile[u]), s.r(s.tile[u])), s.q(s.tile[u]), s.r(s.tile[u])) }))
      : [];
    // On a ride: waiting at the boarding station, or walking there.
    let doing = "";
    const f = this.formationOf.get(ids[0]);
    const leg = f === undefined ? undefined : this.legs.get(f)?.[0];
    if (leg?.ride) {
      const [bq, br] = leg.pts[0];
      const there = ids.some((u) => Math.max(Math.abs(s.q(s.tile[u]) - bq), Math.abs(s.r(s.tile[u]) - br), Math.abs(s.q(s.tile[u]) - bq + s.r(s.tile[u]) - br)) <= 2);
      doing = there ? `Waiting for a train at ${bq}, ${br}` : `Walking to the station at ${bq}, ${br}, to ride to ${leg.pts[1][0]}, ${leg.pts[1][1]}`;
    }
    setSummary({
      doing,
      policy: this.policyLine(ids),
      jobs,
      count: ids.length,
      health: pct(hp, hpMax),
      ammo: pct(ammo, ammoMax),
      trouble: extra,
      types: [...byType].sort((a, b) => b[1] - a[1]).map(([d, n]) => count(n, designs[d].name.toLowerCase())),
    });
  }

  /** The selected construction sites together: how many of each, and
   *  "Cancel all". */
  private siteSummary(): Summary {
    const at = [...this.siteSel].map((k) => k.split(",").map(Number) as [number, number]);
    const chosen = at.map((a) => this.siteAt(a[0], a[1])).filter((x): x is Site => !!x);
    const kinds = new Map<string, number>();
    for (const x of chosen) kinds.set(siteName(x), (kinds.get(siteName(x)) ?? 0) + 1);
    const done = chosen.reduce((t, x) => t + siteDone(x), 0);
    const need = chosen.reduce((t, x) => t + x.need, 0);
    return {
      count: chosen.length,
      health: "",
      ammo: "–",
      trouble: "",
      types: [...kinds].map(([name, n]) => `${n} ${(n === 1 || /s$/.test(name) ? name : `${name}s`).toLowerCase()} (building)`),
      group: { lines: [`${Math.floor((100 * done) / Math.max(1, need))}% built together`], ids: [], sites: chosen.map((x) => x.at) },
    };
  }

  /** The selected structures together: how many of each, what they made
   *  a minute and what they hold, for the Selection tab. */
  private structureSummary(): Summary {
    const s = this.playback.state;
    const h = this.playback.header;
    const ks = [...this.structs].filter((k) => s.depots[k]?.faction === this.faction);
    const kinds = new Map<string, number>();
    for (const k of ks) {
      const name = structureName(h, s.depots[k], k);
      kinds.set(name, (kinds.get(name) ?? 0) + 1);
    }
    const made = new Map<string, number>();
    (h.sites ?? []).forEach((site, i) => {
      if (!ks.includes(site.station)) return;
      const now = industry.status(i, s);
      if (now?.rate && !now.why) made.set(now.makes, (made.get(now.makes) ?? 0) + now.rate);
    });
    const held = new Map<string, number>();
    for (const k of ks) for (const [g, n] of stationInfo(k)?.freight ?? []) if (n > 0) held.set(g, (held.get(g) ?? 0) + n);
    const lines = [
      made.size ? `Making ${[...made].map(([g, n]) => `${Math.round(n).toLocaleString("en-US")} ${g}`).join(", ")} a minute` : "",
      held.size ? `Holding ${[...held].map(([g, n]) => `${n.toLocaleString("en-US")} ${g.toLowerCase()}`).join(", ")}` : "",
    ].filter(Boolean);
    return {
      count: ks.length,
      health: "",
      ammo: "–",
      trouble: "",
      types: [...kinds].map(([name, n]) => `${n} ${(n === 1 || /s$/.test(name) ? name : `${name}s`).toLowerCase()}`),
      group: { lines, ids: ks },
    };
  }

  /** Centres the camera on units (a double-tapped group). */
  private focus(ids: number[]): void {
    let x = 0;
    let y = 0;
    for (const id of ids) {
      const [wx, wy] = this.renderer.unitPos(id, this.playback.alpha);
      x += wx;
      y += wy;
    }
    this.renderer.camera.flyTo(x / ids.length, y / ids.length);
  }
}
